import { useState, useEffect, useRef } from "react";
import { supabase } from './supabaseClient';

// ─── EXACT BRAND COLOURS (Evolution Endurance) ────────────────────────────────
// --red:       #922920  (burgundy rust — primary / accent)
// --cream:     #F2E9DF  (white sand — light background / text on dark)
// --black:     #141414  (near-black — primary dark background)
// --highlight: #D66E21  (orange — sparing use only: alerts / single CTA)

// Web Push VAPID public key -- safe to be in client source, it's not secret
// (the matching private key lives only in the notify-dm-message Edge
// Function's environment). Generated once for this project; regenerating it
// would invalidate every existing push subscription.
const VAPID_PUBLIC_KEY = 'BAKPWWkk5q3mKD8AESJztA4bmNEP8E-o1nFPitpK8rTVin1WbAmSzq952gF4DYTqqozjLT5810HllQtf92QsScU';

// ─── DATA ────────────────────────────────────────────────────────────────────
const TIERS = {
  stride: {
    name: "Stride", price: "Free", priceNote: "forever",
    color: "#F2E9DF",
    features: ["Community run schedule", "Club announcements", "Weekly PAID runs", "Access to Q & A"],
    locked: ["Trail-specific training plans", "Nutrition & fuelling advice", "Progress tracking", "Access to all club runs", "1-on-1 coaching sessions"],
  },
  elevate: {
    name: "Elevate", price: "CA$49.99", priceNote: "/ month",
    color: "var(--red)", popular: true,
    features: ["Everything in Stride", "Trail-specific training plans", "Nutrition & fuelling advice", "Progress tracking", "Access to all club runs"],
    locked: ["1-on-1 coaching sessions", "Monthly performance review", "Priority support"],
  },
  apex: {
    name: "Apex", price: "CA$99.99", priceNote: "/ month",
    color: "var(--red)",
    features: ["Everything in Elevate", "1-on-1 coaching sessions", "Monthly performance review", "Priority support"],
    locked: [],
  },
};

const tierOrder = { stride:0, elevate:1, apex:2 };
const canAccess = (u, c) => tierOrder[u] >= tierOrder[c];

// Configurable workout-type categories, shared by the member Training tab and
// the admin Content tab so both stay in sync when a new type is added.
const TRAINING_CATEGORIES = [
  { key: 'running',  label: 'Runs',     emoji: '🏃' },
  { key: 'strength', label: 'Strength', emoji: '💪' },
];

// Strength-program filters. Shared by the member Training > Strength browser
// and the admin Content form so the two stay in sync.
const BODY_FOCUS_OPTIONS = [
  { key: 'upper',     label: 'Upper Body' },
  { key: 'lower',     label: 'Lower Body' },
  { key: 'full_body', label: 'Full Body' },
];
const EQUIPMENT_OPTIONS = [
  { key: 'no_equipment',      label: 'No Equipment' },
  { key: 'minimal_equipment', label: 'Minimal Equipment' },
  { key: 'full_gym',          label: 'Full Gym' },
];
const TRAINING_GOAL_OPTIONS = [
  { key: 'strength',          label: 'Strength' },
  { key: 'injury_prevention', label: 'Injury Prevention' },
];

// Nutrition sections: the top-level split requested in the rebrand brief.
// Each section carries its own category list for the existing category filter.
const NUTRITION_SECTIONS = [
  {
    key: 'sports_performance',
    label: 'Sports Performance',
    categories: ['Pre-Run', 'Mid-Run', 'Post-Run', 'Race Day', 'Hydration', 'Supplements', 'General'],
  },
  {
    key: 'longevity',
    label: 'Longevity',
    categories: ['Weight Management', 'Metabolic Health', 'Meal Planning', 'General'],
  },
];
const categoriesForSection = (key) => NUTRITION_SECTIONS.find(s => s.key === key)?.categories || [];

function LogoNav({ style }) {
  return (
    <img
      className="nav-logo"
      src="/logo.png"
      alt="Evolution Endurance"
      style={{ height: "auto", width: "auto", maxHeight: 64, maxWidth: "55vw", display: "block", ...style }}
    />
  );
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

  @keyframes fadeIn { from { opacity:0; transform:translateX(-50%) translateY(8px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }

  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  /* Evolution Endurance — light theme, matching evolutionendurance.ca
     (white-sand background, burgundy accent, dark ink text). Variable
     NAMES are historical (--black/--cream carry over from the old dark
     theme) but their VALUES now implement the light palette below --
     renaming every call site wasn't worth the risk, so read --black as
     "base surface" and --cream as "primary ink" rather than literally. */
  :root {
    --red:       #922920;
    --red-hi:    #a83322;
    --red-light: #a8432e;
    --red-dark:  #6b1e16;
    --highlight: #D66E21;
    --cream:     #241a14;
    --cream2:    #6b5c50;
    --black:     #F2E9DF;
    --bg2:       #e8dbc8;
    --bg3:       #ddcbb0;
    --muted:     #6f6155;
    --b1:        rgba(36,26,20,0.14);
    --b2:        rgba(36,26,20,0.09);
    --b3:        rgba(36,26,20,0.05);
    --card:      var(--bg2);
    --on-red:    #f7f1e8;
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'Poppins', sans-serif;
    background: var(--black);
    color: var(--cream);
    min-height: 100vh;
  }

  /* Paper grain overlay */
  body::after {
    content: '';
    position: fixed; inset: 0;
    pointer-events: none; z-index: 9999;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E");
    opacity: 0.038;
    mix-blend-mode: overlay;
  }

  .app { position: relative; z-index: 1; min-height: 100vh; display: flex; flex-direction: column; }

  /* ── NAV ── */
  .nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; height: 160px;
    background: rgba(242,233,223,0.94);
    border-bottom: 1px solid var(--b1);
    position: sticky; top: 0; z-index: 200;
    backdrop-filter: blur(16px);
  }
  .nav-right { display: flex; align-items: center; gap: 6px; }

  .tier-pill {
    font-family: 'Poppins', sans-serif;
    font-size: 9px; letter-spacing: 2.5px;
    padding: 4px 10px; text-transform: uppercase; border-radius: 1px;
  }
  .pill-stride  { background: rgba(36,26,20,0.06); color: var(--cream2); border: 1px solid rgba(36,26,20,0.16); }
  .pill-elevate { background: rgba(146,41,32,0.14); color: var(--red-light); border: 1px solid rgba(146,41,32,0.4); }
  .pill-apex    { background: var(--red); color: var(--on-red); border: 1px solid var(--red); }

  .nav-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
    padding: 6px 10px; background: transparent;
    border: 1px solid var(--b1); color: var(--muted);
    cursor: pointer; transition: all 0.18s; border-radius: 1px;
    white-space: nowrap;
  }
  .nav-btn:hover, .nav-btn.on { border-color: var(--red); color: var(--red-light); }

  /* ── HERO ── */
  .hero {
    padding: 80px 36px 72px;
    background: var(--bg2);
    border-bottom: 1px solid var(--b1);
    position: relative; overflow: hidden;
  }
  /* Watermark mountain */
  .hero-mtn {
    position: absolute; right: -30px; top: -20px;
    opacity: 0.04; pointer-events: none;
  }
  /* Subtle left red bar accent */
  .hero::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px; background: var(--red);
  }

  .hero-eyebrow {
    font-family: 'Poppins', sans-serif;
    font-size: 9px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--red); margin-bottom: 20px;
    display: flex; align-items: center; gap: 12px;
  }
  .hero-eyebrow::before {
    content: ''; display: block;
    width: 32px; height: 1px; background: var(--red);
  }

  .hero-h1 {
    font-family: 'Poppins', sans-serif;
    font-weight: 700; text-transform: uppercase;
    font-size: clamp(54px, 8.5vw, 110px);
    line-height: 0.88; letter-spacing: 2px;
    color: var(--cream); margin-bottom: 6px;
  }
  .hero-h1 em { color: var(--red); font-style: normal; }

  .hero-rule { width: 52px; height: 3px; background: var(--red); margin: 26px 0; }

  .hero-sub {
    font-size: 16px; font-weight: 300; font-style: italic;
    color: var(--cream2); max-width: 460px; line-height: 1.72; margin-bottom: 38px;
  }
  .hero-cta { display: flex; gap: 12px; flex-wrap: wrap; }

  /* ── BUTTONS ── */
  .btn {
    font-family: 'Poppins', sans-serif;
    font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase;
    padding: 13px 26px; cursor: pointer; border-radius: 1px;
    transition: all 0.18s; border: none;
  }
  .btn-red   { background: var(--red); color: var(--on-red); }
  .btn-red:hover { background: var(--red-hi); transform: translateY(-1px); box-shadow: 0 4px 20px rgba(146,41,32,0.3); }
  .btn-ghost { background: transparent; color: var(--cream2); border: 1px solid var(--b1); }
  .btn-ghost:hover { border-color: var(--red); color: var(--red-light); }

  /* ── TABS ── */
  .tabnav {
    display: flex; padding: 0 28px;
    background: var(--bg2); border-bottom: 1px solid var(--b2);
    overflow-x: auto; scrollbar-width: none;
  }
  .tabnav::-webkit-scrollbar { display: none; }
  .tab {
    font-family: 'Poppins', sans-serif;
    font-size: 9.5px; letter-spacing: 2.5px; text-transform: uppercase;
    padding: 14px 16px; background: none; border: none; color: var(--muted);
    cursor: pointer; border-bottom: 2px solid transparent;
    margin-bottom: -1px; white-space: nowrap; transition: all 0.15s; flex-shrink: 0;
  }
  .tab:hover { color: var(--cream2); }
  .tab.on { color: var(--red); border-bottom-color: var(--red); }

  /* ── SECTION ── */
  .sec { padding: 44px 32px; max-width: 1080px; margin: 0 auto; width: 100%; }
  .s-eye {
    font-family: 'Poppins', sans-serif;
    font-size: 9px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--red); margin-bottom: 6px;
  }
  .s-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600; font-size: 30px; letter-spacing: 2px;
    text-transform: uppercase; color: var(--cream); margin-bottom: 4px;
  }
  .s-sub {
    font-size: 14px; font-style: italic; color: var(--muted); margin-bottom: 30px;
  }

  /* ── TIER CARDS ── */
  .tier-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(252px, 1fr));
    gap: 1px; background: var(--b2); border: 1px solid var(--b2);
  }
  .tier-card {
    background: var(--bg2); padding: 32px 26px; cursor: pointer;
    position: relative; transition: background 0.2s; border: 2px solid transparent;
  }
  .tier-card:hover { background: var(--bg3); }
  .tier-card.sel { background: var(--bg3); border-color: var(--red); }

  .pop-tag {
    position: absolute; top: -1px; right: 18px;
    font-family: 'Poppins', sans-serif; font-size: 8px; letter-spacing: 2px;
    text-transform: uppercase; padding: 3px 10px;
    background: var(--red); color: var(--on-red);
  }
  .tc-name {
    font-family: 'Poppins', sans-serif; font-weight: 700;
    font-size: 28px; letter-spacing: 3px; text-transform: uppercase;
    color: var(--cream); margin-bottom: 4px;
  }
  .tc-price {
    font-family: 'Poppins', sans-serif; font-size: 24px;
    color: var(--cream); margin-bottom: 2px;
  }
  .tc-note {
    font-family: 'Poppins', sans-serif; font-size: 10px;
    color: var(--muted); margin-bottom: 22px;
  }
  .feat-list { list-style: none; margin-bottom: 28px; }
  .feat {
    font-size: 13px; padding: 7px 0; border-bottom: 1px solid var(--b2);
    display: flex; gap: 8px; align-items: flex-start;
  }
  .feat.y { color: var(--cream); }
  .feat.n { color: var(--muted); }
  .chk { font-size: 10px; margin-top: 2px; flex-shrink: 0; color: var(--red); }
  .lck { font-size: 10px; margin-top: 2px; flex-shrink: 0; color: #c3b6a2; }

  /* ── CONTENT CARDS ── */
  .cgrid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1px; background: var(--b2);
  }
  .cc {
    background: var(--bg2); padding: 24px; position: relative;
    overflow: hidden; transition: background 0.15s; cursor: pointer;
  }
  .cc:hover { background: var(--bg3); }
  .cc.lk { cursor: default; }
  .cc-tag {
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px;
    color: var(--red); text-transform: uppercase; margin-bottom: 10px;
  }
  .cc-title {
    font-family: 'Poppins', sans-serif; font-size: 17px; letter-spacing: 1px;
    text-transform: uppercase; color: var(--cream); margin-bottom: 6px;
  }
  .cc-desc { font-size: 13px; color: var(--muted); font-style: italic; line-height: 1.5; }

  .veil {
    position: absolute; inset: 0;
    background: rgba(20,16,12,0.82);
    backdrop-filter: blur(3px);
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px;
  }
  .veil-ico  { font-size: 20px; opacity: 0.5; }
  .veil-lbl  { font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px; color: rgba(247,241,232,0.75); text-transform: uppercase; }
  .veil-tier { font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 2px; color: #e8a493; text-transform: uppercase; }

  /* ── STATS + CHART ── */
  .stat-row {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1px; background: var(--b2); margin-bottom: 1px;
  }
  .stat-box { background: var(--bg2); padding: 22px 18px; text-align: center; }
  .stat-val {
    font-family: 'Poppins', sans-serif; font-size: 46px; font-weight: 600;
    color: var(--red); line-height: 1;
  }
  .stat-lbl {
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px;
    color: var(--muted); text-transform: uppercase; margin-top: 4px;
  }
  .chart-box { background: var(--bg2); border: 1px solid var(--b2); padding: 26px; }
  .chart-mono {
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px;
    color: var(--muted); text-transform: uppercase; margin-bottom: 18px;
  }
  .bars { display: flex; align-items: flex-end; gap: 5px; height: 120px; }
  .bar-col {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; gap: 5px; height: 100%; justify-content: flex-end;
  }
  .bar-fill {
    width: 100%; border-radius: 1px 1px 0 0; min-height: 4px;
    background: var(--red);
    position: relative; transition: height 0.5s cubic-bezier(0.34,1.4,0.64,1);
  }
  .bar-fill:hover { background: var(--red-hi); }
  .bar-fill:hover::after {
    content: attr(data-v); position: absolute; top: -20px; left: 50%; transform: translateX(-50%);
    font-family: 'Poppins', sans-serif; font-size: 9px; color: var(--cream2); white-space: nowrap;
  }
  .bar-lbl {
    font-family: 'Poppins', sans-serif; font-size: 8.5px;
    color: var(--muted); letter-spacing: 1px;
  }

  /* ── UPGRADE WALL ── */
  .wall {
    background: var(--bg2); border: 1px solid var(--b2);
    padding: 60px 32px; text-align: center;
  }
  .wall-ico   { font-size: 34px; opacity: 0.35; margin-bottom: 14px; }
  .wall-title {
    font-family: 'Poppins', sans-serif; font-size: 24px; letter-spacing: 3px;
    text-transform: uppercase; color: var(--cream); margin-bottom: 8px;
  }
  .wall-sub {
    font-size: 14px; font-style: italic; color: var(--muted);
    max-width: 360px; margin: 0 auto 26px; line-height: 1.65;
  }

  /* ── COACHING ── */
  .co-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--b2); }
  .co-card { background: var(--bg2); padding: 30px; }
  .co-mono {
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px;
    color: var(--muted); text-transform: uppercase; margin-bottom: 14px;
  }
  .co-initials {
    width: 54px; height: 54px; border: 1.5px solid var(--red);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Poppins', sans-serif; font-size: 16px;
    color: var(--red); margin-bottom: 16px; letter-spacing: 2px;
  }
  .co-name {
    font-family: 'Poppins', sans-serif; font-size: 20px; letter-spacing: 2px;
    text-transform: uppercase; color: var(--cream);
  }
  .co-role {
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 2px;
    color: var(--red); text-transform: uppercase; margin-bottom: 12px;
  }
  .co-bio {
    font-size: 13px; font-style: italic; font-weight: 300;
    color: var(--cream2); line-height: 1.72; margin-bottom: 22px;
  }
  .slot-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 8px; }
  .slot {
    border: 1px solid var(--b1); padding: 10px 12px;
    cursor: pointer; transition: all 0.15s;
  }
  .slot:hover { border-color: var(--red); background: rgba(146,41,32,0.09); }
  .slot-time { font-family: 'Poppins', sans-serif; font-size: 12px; color: var(--cream); }
  .slot-day  { font-size: 11px; font-style: italic; color: var(--muted); margin-top: 2px; }

  /* ── ADMIN ── */
  .admin-wrap { background: var(--black); min-height: 100vh; }
  .admin-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 30px; border-bottom: 1px solid var(--b1);
  }
  .admin-title {
    font-family: 'Poppins', sans-serif; font-size: 18px; letter-spacing: 4px;
    text-transform: uppercase; color: var(--red);
  }
  .admin-layout { display: grid; grid-template-columns: 196px 1fr; min-height: calc(100vh - 132px); }
  .admin-side { border-right: 1px solid var(--b2); background: var(--bg2); padding: 20px 0; }
  .anav {
    display: flex; align-items: center; gap: 9px; padding: 11px 22px;
    font-family: 'Poppins', sans-serif; font-size: 9.5px; letter-spacing: 2px;
    text-transform: uppercase; color: var(--muted); cursor: pointer;
    border-left: 2px solid transparent; transition: all 0.14s;
  }
  .anav:hover { color: var(--cream2); }
  .anav.on { color: var(--red); border-left-color: var(--red); background: rgba(146,41,32,0.07); }
  .admin-body { padding: 30px; }

  .astats { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: var(--b2); margin-bottom: 22px; }
  .astat { background: var(--bg2); padding: 20px 22px; }
  .astat-val { font-family: 'Poppins', sans-serif; font-size: 40px; font-weight: 600; color: var(--red); line-height: 1; }
  .astat-lbl { font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 3px; color: var(--muted); text-transform: uppercase; margin-top: 4px; }

  .tbl { background: var(--bg2); border: 1px solid var(--b2); }
  .thead {
    display: grid; grid-template-columns: var(--tc); padding: 11px 18px;
    background: rgba(146,41,32,0.06);
    font-family: 'Poppins', sans-serif; font-size: 9px; letter-spacing: 2px;
    color: var(--muted); text-transform: uppercase;
  }
  .trow {
    display: grid; grid-template-columns: var(--tc); padding: 14px 18px;
    border-top: 1px solid var(--b2); font-size: 13px; align-items: center;
    transition: background 0.1s;
  }
  .trow:hover { background: rgba(146,41,32,0.04); }
  .cell-name  { font-family: 'Poppins', sans-serif; font-size: 15px; letter-spacing: 0.5px; color: var(--cream); }
  .cell-email { color: var(--muted); font-size: 12px; font-style: italic; }
  .cell-mono  { font-family: 'Poppins', sans-serif; font-size: 11px; color: var(--cream2); }
  .cell-red   { font-family: 'Poppins', sans-serif; font-size: 11px; color: var(--red); }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: var(--bg2); }
  ::-webkit-scrollbar-thumb { background: var(--b1); border-radius: 2px; }

  @media (max-width: 640px) {
    .hero { padding: 56px 20px 52px; }
    .sec { padding: 32px 20px; }
    .co-grid { grid-template-columns: 1fr; }
    .admin-layout { grid-template-columns: 1fr; }
    .admin-side { border-right: none; border-bottom: 1px solid var(--b2); }
    .nav { padding: 0 16px; }
    .nav-logo { max-height: 38px !important; max-width: 46vw !important; }
  }
`;

// ─── APP ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [mode,     setMode]     = useState("member");
  const [userTier, setUserTier] = useState("stride");
  const [tab,      setTab]      = useState("dashboard");
  const [adminTab, setAdminTab] = useState("members");
  const [adminMembers, setAdminMembers] = useState([]);
  const [adminLoadingMembers, setAdminLoadingMembers] = useState(false);
  const [isAdmin,             setIsAdmin]             = useState(false);
  const [stravaConnected,    setStravaConnected]    = useState(false);
  const [stravaActivities,   setStravaActivities]   = useState([]);
  const [stravaLoading,      setStravaLoading]      = useState(false);
  const [stravaAthlete,      setStravaAthlete]      = useState(null);
  const [stravaError,       setStravaError]       = useState('');
  const [pendingStravaCode, setPendingStravaCode] = useState(null);
  const [forumPosts,    setForumPosts]    = useState([]);
  const [forumLoading,  setForumLoading]  = useState(false);
  const [openPost,      setOpenPost]      = useState(null);
  const [postReplies,   setPostReplies]   = useState([]);
  const [newPost,       setNewPost]       = useState('');
  const [newReply,      setNewReply]      = useState('');
  const [postPhoto,     setPostPhoto]     = useState(null);
  const [postingForum,  setPostingForum]  = useState(false);
  const [unreadCount,       setUnreadCount]       = useState(0);
  const [unreadWorkouts,    setUnreadWorkouts]    = useState(0);
  const [unreadQa,          setUnreadQa]          = useState(0);
  const [adminQaNotifs,     setAdminQaNotifs]     = useState(0);
  const [communityNotify,   setCommunityNotify]   = useState(false);
  const [savingNotifyPref,  setSavingNotifyPref]  = useState(false);
  const [dashCalMonth,      setDashCalMonth]      = useState(() => { const d=new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [selectedCalDay,   setSelectedCalDay]    = useState(null);
  const [completedWorkouts, setCompletedWorkouts] = useState([]);
  const [communityTab,      setCommunityTab]      = useState('general');
  const [postLikes,         setPostLikes]         = useState({});
  const [showLikesFor,      setShowLikesFor]      = useState(null);
  const [sharePrefs,        setSharePrefs]        = useState({ distance:true, time:true, pace:true, hr:true, calories:true, notes:false });
  const [savingSharePrefs,  setSavingSharePrefs]  = useState(false);
  const [showSharePrefs,    setShowSharePrefs]    = useState(false);
  const [runClubForm,       setRunClubForm]       = useState({ title:'', location:'', maps_url:'', date:'', distance:'' });
  const [savingRunClub,     setSavingRunClub]     = useState(false);
  const [toast,             setToast]             = useState(null); // { msg, type: 'success'|'error' }

  const QA_TOPICS = ['General', 'Physiology', 'Strength', 'Running', 'Nutrition'];
  const [qaPosts,          setQaPosts]          = useState([]);
  const [qaLoading,        setQaLoading]        = useState(false);
  const [qaTopicFilter,    setQaTopicFilter]    = useState('all');
  const [openQaPost,       setOpenQaPost]       = useState(null);
  const [qaReplies,        setQaReplies]        = useState([]);
  const [newQaQuestion,    setNewQaQuestion]    = useState('');
  const [qaAskAnonymous,   setQaAskAnonymous]   = useState(false);
  const [qaQuestionTopic,  setQaQuestionTopic]  = useState('General');
  const [newQaReply,       setNewQaReply]       = useState('');
  const [postingQa,        setPostingQa]        = useState(false);
  const [showQaVideoForm,  setShowQaVideoForm]  = useState(false);
  const [qaVideoForm,      setQaVideoForm]      = useState({ title:'', topic:'General', video_url:'', content:'' });

  // ── Direct messages ──
  const [dmMessages,        setDmMessages]        = useState([]); // the currently-open thread
  const [dmLoading,         setDmLoading]         = useState(false);
  const [newDmMessage,      setNewDmMessage]      = useState('');
  const [sendingDm,         setSendingDm]         = useState(false);
  const [unreadDmCount,     setUnreadDmCount]     = useState(0); // member: unread from coach
  const [adminDmThreads,    setAdminDmThreads]    = useState([]); // [{member_id, email, lastMessage, lastAt, unreadCount}]
  const [adminDmSelected,   setAdminDmSelected]   = useState(null); // member_id of open thread
  const [adminDmUnreadTotal,setAdminDmUnreadTotal]= useState(0);
  const [pushPermission,    setPushPermission]    = useState(typeof Notification!=='undefined'?Notification.permission:'unsupported');
  const [pushBusy,          setPushBusy]          = useState(false);

  // ── Daily reminders ──
  const [reminderEnabled,   setReminderEnabled]   = useState(true);
  const [reminderHour,      setReminderHour]      = useState(7);
  const [savingReminder,    setSavingReminder]    = useState(false);
  const [motivationMsgs,    setMotivationMsgs]    = useState([]);
  const [newMotivation,     setNewMotivation]     = useState({ category:'move', body:'' });
  const [savingMotivation,  setSavingMotivation]  = useState(false);

  const [runs,          setRuns]          = useState([]);
  const [runsLoading,   setRunsLoading]   = useState(false);
  const [showLogForm,   setShowLogForm]   = useState(false);
  const [logRunForm,    setLogRunForm]    = useState({ date: new Date().toISOString().slice(0,10), distance:'', hours:'0', minutes:'', seconds:'0', type:'Easy', elevation:'' });
  const [submittingRun, setSubmittingRun] = useState(false);
  const [editingNotes,  setEditingNotes]  = useState(null);
  const [notesText,     setNotesText]     = useState('');

  const [trainingSubTab,    setTrainingSubTab]    = useState('running');
  const [workouts,          setWorkouts]          = useState([]);
  const [selectedWorkout,   setSelectedWorkout]   = useState(null);
  const [workoutExercises,  setWorkoutExercises]  = useState([]);
  const [lastLogs,          setLastLogs]          = useState({});
  const [logDraft,          setLogDraft]          = useState({});
  const [savingLog,         setSavingLog]         = useState({});
  const [sessionSets,       setSessionSets]       = useState({});
  const [workoutStarted,    setWorkoutStarted]    = useState(false);
  const [workoutStartTime,  setWorkoutStartTime]  = useState(null);
  const [workoutComplete,   setWorkoutComplete]   = useState(false);
  const [wForm,             setWForm]             = useState({ title:'', description:'', tier:'stride', photo_url:'', week_number:'', day_number:'', day_label:'', visibility:'public', mode:'single', duration_weeks:'', body_focus:'', equipment:'', training_goal:'' });
  const [trainingViewTier,  setTrainingViewTier]  = useState(null); // null = tier picker showing
  const [trainingFilters,   setTrainingFilters]   = useState({ body_focus:'all', equipment:'all', training_goal:'all' });
  const [runViewTier,       setRunViewTier]       = useState(null); // null = tier picker showing
  const [runFilterType,     setRunFilterType]     = useState('all');
  const [wktSessions,          setWktSessions]          = useState({});
  const [expandedStrengthProg, setExpandedStrengthProg] = useState(null);
  const [addingStrSessTo,      setAddingStrSessTo]      = useState(null);
  const [strSessForm,          setStrSessForm]          = useState({ week_number:'', day_number:'', day_label:'', description:'' });
  const [savingStrSess,        setSavingStrSess]        = useState(false);
  const [myAssignments,     setMyAssignments]     = useState([]); // workout_ids assigned to the current user
  const [expandedMember,    setExpandedMember]    = useState(null);
  const [memberAssignments, setMemberAssignments] = useState({}); // { [userId]: [{id, workout_id, workouts:{title,tier}}] }
  const [assigningWorkoutId,setAssigningWorkoutId]= useState('');
  const [assignLoading,     setAssignLoading]     = useState(false);
  const [prescribedRuns,    setPrescribedRuns]    = useState([]);
  const [memberPrescRuns,   setMemberPrescRuns]   = useState({}); // { [userId]: [...] }
  const [prForm,            setPrForm]            = useState({ scheduled_date:'', distance_km:'', run_type:'Easy', notes:'' });
  const [savingPr,          setSavingPr]          = useState(false);
  const [logFromPrescribed, setLogFromPrescribed] = useState(null);
  const [exerciseLibrary,   setExerciseLibrary]   = useState([]);
  const [libMuscle,         setLibMuscle]         = useState('All');
  const [libCat,            setLibCat]            = useState('All');
  const [libSearch,         setLibSearch]         = useState('');
  const [newLibEx,          setNewLibEx]          = useState({ name:'', muscle_group:'Full Body', category:'Strength', video_url:'', notes:'' });
  const [savingLibEx,       setSavingLibEx]       = useState(false);
  const [calWeekStart,      setCalWeekStart]      = useState(() => { const d=new Date(); d.setDate(d.getDate()-((d.getDay()+6)%7)); d.setHours(0,0,0,0); return new Date(d); });
  const [scheduledWorkouts, setScheduledWorkouts] = useState([]);
  const [calMember,         setCalMember]         = useState('');
  const [assigningCalDay,   setAssigningCalDay]   = useState(null);
  const [assigningCalWkt,   setAssigningCalWkt]   = useState('');
  const [savingCal,         setSavingCal]         = useState(false);
  const [adminCompNotifs,   setAdminCompNotifs]   = useState(0);
  const [memberCalStart,    setMemberCalStart]    = useState(() => { const d=new Date(); d.setDate(d.getDate()-((d.getDay()+6)%7)); d.setHours(0,0,0,0); return new Date(d); });
  const [memberScheduled,   setMemberScheduled]   = useState([]);
  const [eForm,             setEForm]             = useState({ name:'', video_url:'', sort_order:'0', sets:'', prescribed_reps:'', prescribed_rpe:'', prescribed_notes:'', superset_group:'', exercise_type:'working' });
  const [uploadingVideo,    setUploadingVideo]    = useState(false);
  const [adminWorkouts,     setAdminWorkouts]     = useState([]);
  const [selectedAdminWkt,  setSelectedAdminWkt]  = useState(null);
  const [adminExercises,    setAdminExercises]    = useState([]);
  const [savingWorkout,     setSavingWorkout]     = useState(false);
  const [contentSubTab,     setContentSubTab]     = useState('strength');
  const [adminRunWorkouts,  setAdminRunWorkouts]  = useState([]);
  const [memberRunWorkouts, setMemberRunWorkouts] = useState([]);
  const [runWForm,          setRunWForm]          = useState({ title:'', run_type:'Easy', duration:'', rpe:'', notes:'', tier:'stride', visibility:'public', mode:'single', duration_weeks:'', video_url:'' });
  const [savingRunWkt,      setSavingRunWkt]      = useState(false);
  const [expandedRunWkt,    setExpandedRunWkt]    = useState(null);
  const [runWktSessions,    setRunWktSessions]    = useState({});
  const [addingSessionTo,   setAddingSessionTo]   = useState(null);
  const [savingSession,     setSavingSession]     = useState(false);
  const [runSessionForm,    setRunSessionForm]    = useState({ week_number:'', day_number:'', day_label:'', run_type:'Easy', duration:'', rpe:'', notes:'' });
  const [editingExercise,   setEditingExercise]   = useState(null);
  const [editExForm,        setEditExForm]        = useState({});
  const [dragOverExId,      setDragOverExId]      = useState(null);
  const dragExSrcRef = useRef(null);
  const [editingRunWkt,     setEditingRunWkt]     = useState(null);
  const [editRunWktForm,    setEditRunWktForm]    = useState({});
  const [editingWorkout,    setEditingWorkout]    = useState(null);
  const [editWktForm,       setEditWktForm]       = useState({});
  const [uploadingWktPhoto, setUploadingWktPhoto] = useState(false);

  const [coachBio,          setCoachBio]          = useState(null);
  const [coachBioLoading,   setCoachBioLoading]   = useState(false);
  const [bioForm,           setBioForm]           = useState({ name:'', tagline:'', bio:'', credentials:'' });
  const [savingBio,         setSavingBio]         = useState(false);
  const [uploadingBioPhoto, setUploadingBioPhoto] = useState(false);

  const [nutritionSubTab,       setNutritionSubTab]       = useState('sports_performance');
  const [nutritionItems,        setNutritionItems]        = useState([]);
  const [nutritionLoading,      setNutritionLoading]      = useState(false);
  const [openNutritionItem,     setOpenNutritionItem]     = useState(null);
  const [nForm,                 setNForm]                 = useState({ title:'', summary:'', content:'', section:'sports_performance', category:'General', tier:'stride', file_url:'', file_type:'', file_name:'', sort_order:'0' });
  const [savingNutrition,       setSavingNutrition]       = useState(false);
  const [uploadingNutritionFile,setUploadingNutritionFile]= useState(false);
  const [editingNutritionItem,  setEditingNutritionItem]  = useState(null);
  const [editNForm,             setEditNForm]             = useState({});

  const REPORT_METRICS = [
    { key: 'rmr',                 label: 'RMR',                 unit: 'kcal/day' },
    { key: 'vo2max',               label: 'VO₂max',         unit: 'ml/kg/min' },
    { key: 'vt1',                  label: 'VT1',                 unit: 'bpm' },
    { key: 'vt2',                  label: 'VT2',                 unit: 'bpm' },
    { key: 'cho_fat_ratio_rest',   label: 'CHO:FAT (rest)',      unit: 'ratio' },
  ];
  const [myReports,          setMyReports]          = useState([]);
  const [myReportsLoading,   setMyReportsLoading]   = useState(false);
  const [openReport,         setOpenReport]         = useState(null);
  const [reportSignedUrl,    setReportSignedUrl]    = useState('');
  const [reportMetric,       setReportMetric]       = useState('vo2max');
  const [adminReports,       setAdminReports]       = useState([]);
  const [adminReportsLoading,setAdminReportsLoading]= useState(false);
  const [reportForm,         setReportForm]         = useState({ user_id:'', title:'', test_date:'', rmr:'', vo2max:'', vt1:'', vt2:'', cho_fat_ratio_rest:'', notes:'', video_url:'', file:null });
  const [savingReport,       setSavingReport]       = useState(false);
  const [uploadingReportFile,setUploadingReportFile]= useState(false);

  const [session,      setSession]      = useState(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [authView,     setAuthView]     = useState("login");
  const [authEmail,    setAuthEmail]    = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError,    setAuthError]    = useState("");
  const [authBusy,     setAuthBusy]     = useState(false);

  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showIosInstall,        setShowIosInstall]        = useState(false);
  const [appInstalled,          setAppInstalled]          = useState(false);

  useEffect(() => {
    // Failsafe: if getSession hangs (offline start, bad SW cache), unblock after 8s
    const loadingTimeout = setTimeout(() => setAuthLoading(false), 8000);
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(loadingTimeout);
      setSession(session);
      if (session?.user) applyProfile(session.user.id);
      setAuthLoading(false);
    }).catch(() => {
      clearTimeout(loadingTimeout);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session);
      if (session?.user) applyProfile(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function applyProfile(userId) {
    let { data, error } = await supabase.from('profiles').select('tier, is_admin, notify_community, share_distance, share_time, share_pace, share_hr, share_calories, share_notes').eq('id', userId).maybeSingle();
    if (error) console.error('applyProfile error:', error);
    if (!data) {
      await supabase.from('profiles').upsert({ id: userId });
      data = { tier: 'stride', is_admin: false };
    }
    console.log('applyProfile data:', data, 'userId:', userId);
    setUserTier(data.tier ?? 'stride');
    if (data.is_admin === true) { setIsAdmin(true); }
    setCommunityNotify(data.notify_community === true);
    setSharePrefs({
      distance: data.share_distance !== false,
      time:     data.share_time     !== false,
      pace:     data.share_pace     !== false,
      hr:       data.share_hr       !== false,
      calories: data.share_calories !== false,
      notes:    data.share_notes    === true,
    });
    checkStravaConnection(userId);
    fetchUnread(userId);
    fetchUnreadWorkouts(userId);
    fetchUnreadQa(userId);
    fetchRuns(userId);
    fetchPrescribedRuns(userId);
    fetchWorkouts(userId);
    fetchCoachBio();
    fetchNutritionItems();
    fetchExerciseLibrary();
    fetchMemberScheduledWorkouts(userId);
    fetchCompletedWorkouts(userId);
    fetchMemberRunWorkouts(userId);
    fetchMyReports(userId);
    fetchUnreadDmCount(userId);
    fetchReminderPrefs(userId);
    if (data.is_admin === true) { fetchAdminCompNotifs(); fetchAdminRunWorkouts(); fetchAdminQaNotifs(); fetchAdminReports(); }
  }

  async function handleAuth(e) {
    e.preventDefault();
    setAuthBusy(true);
    setAuthError("");
    if (authView === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
      if (error) setAuthError(error.message);
    } else {
      const { error } = await supabase.auth.signUp({ email: authEmail, password: authPassword });
      if (error) setAuthError(error.message);
      else setAuthError("Check your email to confirm your account.");
    }
    setAuthBusy(false);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setSession(null);
    setMode("member");
    setUserTier("stride");
  }

  // fetchWorkouts is called inside applyProfile after session is confirmed


  // Step 1: On mount, grab the code from URL immediately and clean the URL
  // Do NOT wait for session here — just stash the code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setPendingStravaCode(code);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Step 2: When we have BOTH a pending code AND a session, do the exchange
  useEffect(() => {
    if (pendingStravaCode && session?.user) {
      handleStravaCallback(pendingStravaCode, session.user.id);
      setPendingStravaCode(null);
    }
  }, [pendingStravaCode, session]);

  // PWA install prompt
  useEffect(() => {
    // Already running as installed PWA — no need to show install button
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setAppInstalled(true);
      return;
    }
    // Android/Chrome: capture the native prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // iOS Safari detection
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIos && isSafari) setShowIosInstall(true);
    // Track if user installs via native dialog
    window.addEventListener('appinstalled', () => {
      setAppInstalled(true);
      setDeferredInstallPrompt(null);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handlePwaInstall() {
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') {
      setAppInstalled(true);
      setDeferredInstallPrompt(null);
    }
  }

  async function checkStravaConnection(userId) {
    const { data } = await supabase.from('strava_tokens').select('athlete_id').eq('user_id', userId).single();
    if (data) { setStravaConnected(true); fetchStravaActivities(userId); }
  }

  async function fetchStravaActivities(userId) {
    setStravaLoading(true);
    try {
      const res = await fetch('/api/strava-activities?userId=' + userId);
      const json = await res.json();
      if (json.activities) setStravaActivities(json.activities);
    } catch(e) {}
    setStravaLoading(false);
  }

  async function handleStravaCallback(code, userId) {
    if (!userId || !code) return;
    setStravaError('');
    try {
      const res = await fetch('/api/strava-exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, userId }),
      });
      const json = await res.json();
      if (json.ok) {
        setStravaConnected(true);
        setStravaAthlete(json.athlete);
        fetchStravaActivities(userId);
      } else {
        setStravaError(json.error || 'Strava connection failed. Please try again.');
      }
    } catch(e) {
      setStravaError('Could not reach server. Please try again.');
    }
  }

  function connectStrava() {
    const clientId = '237388';
    const redirect = window.location.origin;
    const url = 'https://www.strava.com/oauth/authorize?client_id=' + clientId + '&redirect_uri=' + encodeURIComponent(redirect) + '&response_type=code&scope=activity:read_all&approval_prompt=auto';
    window.location.href = url;
  }

  function formatPace(speed) {
    if (!speed) return '—';
    const secPerKm = 1000 / speed;
    const min = Math.floor(secPerKm / 60);
    const sec = Math.round(secPerKm % 60);
    return min + ':' + String(sec).padStart(2, '0') + ' /km';
  }

  function formatDist(meters) {
    return (meters / 1000).toFixed(2) + ' km';
  }

  function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm ' + s + 's';
  }

  function isVideo(url) {
    if (!url) return false;
    const ext = url.split('?')[0].split('.').pop().toLowerCase();
    return ['mp4','mov','avi','webm','mkv','m4v'].includes(ext);
  }

  function paceFromSecs(durationSec, distKm) {
    if (!distKm || !durationSec) return '—';
    const secPerKm = durationSec / distKm;
    const min = Math.floor(secPerKm / 60);
    const sec = Math.round(secPerKm % 60);
    return min + ':' + String(sec).padStart(2,'0') + ' /km';
  }

  function haversineKm(a, b) {
    const R = 6371;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLon = (b.lon - a.lon) * Math.PI / 180;
    const x = Math.sin(dLat/2)**2 + Math.cos(a.lat*Math.PI/180) * Math.cos(b.lat*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1-x));
  }

  function parseActivityFile(text, filename) {
    const ext = filename.split('.').pop().toLowerCase();
    const xml = new DOMParser().parseFromString(text, 'text/xml');

    // Fall back to content detection when the OS strips the extension (common on iOS)
    const looksLikeGpx = ext === 'gpx' || (ext !== 'tcx' && (text.includes('<gpx') || text.includes('<trkpt')));
    const looksLikeTcx = ext === 'tcx' || (!looksLikeGpx && (text.includes('TrainingCenterDatabase') || text.includes('<Lap ')));

    if (looksLikeGpx) {
      const trkpts = Array.from(xml.getElementsByTagName('trkpt'));
      if (!trkpts.length) { alert('No track points found in this GPX file. Make sure you exported a GPX activity file (not a route).'); return null; }
      const pts = trkpts.map(pt => ({
        lat: parseFloat(pt.getAttribute('lat')),
        lon: parseFloat(pt.getAttribute('lon')),
        time: new Date((pt.getElementsByTagName('time')[0] || pt.querySelector('time'))?.textContent)
      })).filter(p => !isNaN(p.lat) && !isNaN(p.lon));
      if (!pts.length) { alert('No valid GPS points found.'); return null; }
      let distKm = 0;
      for (let i = 1; i < pts.length; i++) distKm += haversineKm(pts[i-1], pts[i]);
      const durationSec = Math.round((pts[pts.length-1].time - pts[0].time) / 1000);
      // Elevation gain from <ele> tags
      const elevs = trkpts.map(pt => {
        const el = pt.getElementsByTagName('ele')[0] || pt.getElementsByTagNameNS('*','ele')[0];
        return el ? parseFloat(el.textContent) : NaN;
      }).filter(e => !isNaN(e));
      let elevGainM = 0;
      for (let i = 1; i < elevs.length; i++) { const d = elevs[i]-elevs[i-1]; if (d>0) elevGainM+=d; }
      return { date: pts[0].time.toISOString().slice(0,10), distKm, durationSec, elevGainM: Math.round(elevGainM) };
    }

    if (looksLikeTcx) {
      // Check for XML parse errors
      if (xml.querySelector('parsererror')) { alert('Could not read this TCX file — it may be corrupted or not a valid TCX export.'); return null; }
      // Use getElementsByTagNameNS('*', name) so namespace-prefixed TCX files
      // from Garmin Connect, Coros, etc. work on both desktop and mobile.
      const nsGet = (parent, name) => Array.from(parent.getElementsByTagNameNS('*', name));
      const laps = nsGet(xml, 'Lap');
      if (!laps.length) { alert('No lap data found in this TCX file. Make sure you exported an activity file (not a course or route).'); return null; }
      let totalDistM = 0, totalTimeSec = 0;
      laps.forEach(lap => {
        const d = nsGet(lap, 'DistanceMeters')[0];
        const t = nsGet(lap, 'TotalTimeSeconds')[0];
        if (d) totalDistM += parseFloat(d.textContent) || 0;
        if (t) totalTimeSec += parseFloat(t.textContent) || 0;
      });
      // Fall back to trackpoint times if TotalTimeSeconds missing
      if (!totalTimeSec) {
        const times = nsGet(xml, 'Time').map(t => new Date(t.textContent)).filter(t => !isNaN(t));
        if (times.length >= 2) totalTimeSec = Math.round((times[times.length-1] - times[0]) / 1000);
      }
      const idEl = nsGet(xml, 'Id')[0];
      const date = idEl ? new Date(idEl.textContent).toISOString().slice(0,10) : new Date().toISOString().slice(0,10);
      // Elevation gain from trackpoint AltitudeMeters
      const alts = nsGet(xml, 'AltitudeMeters').map(a => parseFloat(a.textContent)).filter(a => !isNaN(a));
      let elevGainM = 0;
      for (let i = 1; i < alts.length; i++) { const d = alts[i]-alts[i-1]; if (d>0) elevGainM+=d; }
      return { date, distKm: totalDistM / 1000, durationSec: Math.round(totalTimeSec), elevGainM: Math.round(elevGainM) };
    }

    alert('Unsupported file type. Please upload a .gpx or .tcx file.');
    return null;
  }

  function handleActivityUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const result = parseActivityFile(ev.target.result, file.name);
        if (!result) return;
        const { date, distKm, durationSec, elevGainM } = result;
        setLogRunForm({
          date,
          distance: distKm.toFixed(2),
          hours: String(Math.floor(durationSec/3600)),
          minutes: String(Math.floor((durationSec%3600)/60)),
          seconds: String(durationSec%60),
          type: 'Easy',
          elevation: elevGainM ? String(elevGainM) : ''
        });
        setShowLogForm(true);
      } catch(err) { alert('Could not read file. Please try another file.'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  async function fetchRuns(userId) {
    setRunsLoading(true);
    const { data } = await supabase.from('runs')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(30);
    if (data) setRuns(data);
    setRunsLoading(false);
  }

  async function logRun(e) {
    e.preventDefault();
    setSubmittingRun(true);
    const totalSec = (parseInt(logRunForm.hours||0)*3600) + (parseInt(logRunForm.minutes||0)*60) + (parseInt(logRunForm.seconds||0));
    const { data: runData, error } = await supabase.from('runs').insert({
      user_id: session.user.id,
      date: logRunForm.date,
      distance_km: parseFloat(logRunForm.distance),
      duration_seconds: totalSec,
      type: logRunForm.type,
      elevation_gain_m: logRunForm.elevation ? parseFloat(logRunForm.elevation) : null,
    }).select().single();
    if (!error) {
      if (logFromPrescribed && runData) {
        await completePrescribedRun(logFromPrescribed, runData.id);
        setLogFromPrescribed(null);
      }
      setShowLogForm(false);
      autoPostWorkout('run', {
        distance_km:      parseFloat(logRunForm.distance) || null,
        duration_seconds: totalSec || null,
        runType:          logRunForm.type,
      });
      setLogRunForm({ date: new Date().toISOString().slice(0,10), distance:'', hours:'0', minutes:'', seconds:'0', type:'Easy', elevation:'' });
      fetchRuns(session.user.id);
    }
    setSubmittingRun(false);
  }

  async function saveRunNotes(runId) {
    await supabase.from('runs').update({ notes: notesText }).eq('id', runId);
    setRuns(prev => prev.map(r => r.id===runId ? {...r, notes: notesText} : r));
    setEditingNotes(null);
  }


  async function fetchForumPosts() {
    setForumLoading(true);
    const { data } = await supabase.from('forum_posts')
      .select('*, profiles(email), post_likes(user_id, profiles(email))')
      .order('created_at', { ascending: false });
    if (data) {
      setForumPosts(data);
      const likes = {};
      data.forEach(p => { likes[p.id] = p.post_likes || []; });
      setPostLikes(likes);
    }
    setForumLoading(false);
  }

  async function fetchUnread(userId) {
    const { count } = await supabase.from('forum_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId).eq('read', false);
    setUnreadCount(count || 0);
  }

  async function fetchUnreadWorkouts(userId) {
    const { count } = await supabase.from('workout_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId).eq('read', false);
    setUnreadWorkouts(count || 0);
  }

  async function markWorkoutNotifsRead(userId) {
    if (!userId) return;
    await supabase.from('workout_notifications')
      .update({ read: true })
      .eq('user_id', userId).eq('read', false);
    setUnreadWorkouts(0);
  }

  async function fetchUnreadQa(userId) {
    const { count } = await supabase.from('qa_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId).eq('read', false);
    setUnreadQa(count || 0);
  }

  async function fetchAdminQaNotifs() {
    const { count } = await supabase.from('qa_notifications')
      .select('*', { count: 'exact', head: true })
      .is('qa_reply_id', null).eq('read', false);
    setAdminQaNotifs(count || 0);
  }

  async function markQaNotifsRead(userId) {
    if (!userId) return;
    await supabase.from('qa_notifications')
      .update({ read: true })
      .eq('user_id', userId).eq('read', false);
    setUnreadQa(0);
  }

  async function markAdminQaNotifsRead() {
    await supabase.from('qa_notifications')
      .update({ read: true })
      .is('qa_reply_id', null).eq('read', false);
    setAdminQaNotifs(0);
  }

  async function toggleLike(e, postId) {
    e.stopPropagation();
    if (!session?.user) return;
    const existing = (postLikes[postId] || []).find(l => l.user_id === session.user.id);
    if (existing) {
      await supabase.from('post_likes').delete().eq('post_id', postId).eq('user_id', session.user.id);
      setPostLikes(p => ({ ...p, [postId]: p[postId].filter(l => l.user_id !== session.user.id) }));
    } else {
      await supabase.from('post_likes').insert({ post_id: postId, user_id: session.user.id });
      const newLike = { user_id: session.user.id, profiles: { email: session.user.email } };
      setPostLikes(p => ({ ...p, [postId]: [...(p[postId] || []), newLike] }));
    }
  }

  async function autoPostWorkout(type, workoutData) {
    if (!session?.user) return;
    let content = '';
    if (type === 'run') {
      const parts = [];
      if (sharePrefs.distance && workoutData.distance_km) parts.push(`${workoutData.distance_km} km`);
      if (sharePrefs.time && workoutData.duration_seconds) {
        const h = Math.floor(workoutData.duration_seconds / 3600);
        const m = Math.floor((workoutData.duration_seconds % 3600) / 60);
        const s = workoutData.duration_seconds % 60;
        parts.push(`${h > 0 ? h + 'h ' : ''}${m}m ${s > 0 ? s + 's' : ''}`.trim());
      }
      if (sharePrefs.pace && workoutData.distance_km && workoutData.duration_seconds) {
        const paceSecPerKm = workoutData.duration_seconds / workoutData.distance_km;
        const pm = Math.floor(paceSecPerKm / 60);
        const ps = Math.round(paceSecPerKm % 60);
        parts.push(`${pm}:${String(ps).padStart(2, '0')}/km pace`);
      }
      const label = workoutData.runType || 'Run';
      content = `🏃 ${label} complete!` + (parts.length ? '\n' + parts.join('  ·  ') : '');
    } else if (type === 'strength') {
      content = `💪 Completed ${workoutData.title}!`;
    }
    if (!content) return;
    const { data } = await supabase.from('forum_posts').insert({
      user_id: session.user.id,
      content,
      tab: 'general',
      workout_data: { type, ...workoutData },
    }).select('*, profiles(email), post_likes(user_id, profiles(email))').single();
    if (data) {
      setForumPosts(p => [data, ...p]);
      setPostLikes(prev => ({ ...prev, [data.id]: [] }));
    }
  }

  async function updateSharePref(key, val) {
    setSavingSharePrefs(true);
    await supabase.from('profiles').update({ [`share_${key}`]: val }).eq('id', session.user.id);
    setSharePrefs(p => ({ ...p, [key]: val }));
    setSavingSharePrefs(false);
  }

  async function createRunClubPost(e) {
    e.preventDefault();
    if (!runClubForm.title.trim()) return;
    setSavingRunClub(true);
    const { data } = await supabase.from('forum_posts').insert({
      user_id: session.user.id,
      content: runClubForm.title.trim(),
      tab: 'run_club',
      workout_data: {
        location: runClubForm.location || null,
        maps_url:  runClubForm.maps_url  || null,
        date:      runClubForm.date      || null,
        distance:  runClubForm.distance  || null,
      },
    }).select('*, profiles(email), post_likes(user_id, profiles(email))').single();
    if (data) {
      setForumPosts(p => [data, ...p]);
      setPostLikes(prev => ({ ...prev, [data.id]: [] }));
      setRunClubForm({ title:'', location:'', maps_url:'', date:'', distance:'' });
      showToast('✓ Run Club post created');
    }
    setSavingRunClub(false);
  }

  async function openForumPost(post) {
    setOpenPost(post);
    const { data } = await supabase.from('forum_replies')
      .select('*, profiles(email)')
      .eq('post_id', post.id)
      .order('created_at', { ascending: true });
    if (data) setPostReplies(data);
  }

  async function submitPost(e) {
    e.preventDefault();
    if (!newPost.trim()) return;
    setPostingForum(true);
    let photo_url = null;
    if (postPhoto) {
      const ext = postPhoto.name.split('.').pop();
      const path = session.user.id + '/' + Date.now() + '.' + ext;
      const { data: upload } = await supabase.storage.from('forum-photos').upload(path, postPhoto);
      if (upload) {
        const { data: { publicUrl } } = supabase.storage.from('forum-photos').getPublicUrl(path);
        photo_url = publicUrl;
      }
    }
    const { data } = await supabase.from('forum_posts').insert({
      user_id: session.user.id, content: newPost.trim(), photo_url,
      tab: communityTab === 'achievements' ? 'achievements' : 'general',
    }).select('*, profiles(email), post_likes(user_id, profiles(email))').single();
    if (data) {
      setForumPosts(p => [data, ...p]);
      setPostLikes(prev => ({ ...prev, [data.id]: [] }));
      setNewPost(''); setPostPhoto(null);
      // Fan out new-post notifications to all opted-in users (excluding poster)
      const { data: optedIn } = await supabase.from('profiles')
        .select('id').eq('notify_community', true).neq('id', session.user.id);
      if (optedIn && optedIn.length > 0) {
        await supabase.from('forum_notifications').insert(
          optedIn.map(u => ({ user_id: u.id, post_id: data.id }))
        );
      }
    }
    setPostingForum(false);
  }

  async function submitReply(e) {
    e.preventDefault();
    if (!newReply.trim() || !openPost) return;
    setPostingForum(true);
    const { data } = await supabase.from('forum_replies').insert({
      post_id: openPost.id, user_id: session.user.id, content: newReply.trim(),
    }).select('*, profiles(email)').single();
    if (data) {
      setPostReplies(p => [...p, data]);
      setNewReply('');
      if (openPost.user_id !== session.user.id) {
        await supabase.from('forum_notifications').insert({
          user_id: openPost.user_id, post_id: openPost.id, reply_id: data.id,
        });
      }
    }
    setPostingForum(false);
  }

  async function markNotificationsRead() {
    if (!session?.user) return;
    await supabase.from('forum_notifications')
      .update({ read: true })
      .eq('user_id', session.user.id)
      .eq('read', false);
    setUnreadCount(0);
  }

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function toggleCommunityNotify() {
    if (!session?.user) return;
    setSavingNotifyPref(true);
    const newVal = !communityNotify;
    await supabase.from('profiles').update({ notify_community: newVal }).eq('id', session.user.id);
    setCommunityNotify(newVal);
    setSavingNotifyPref(false);
  }

  function timeAgo(ts) {
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff/60) + 'm ago';
    if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
    return Math.floor(diff/86400) + 'd ago';
  }

  // ── Q&A ──────────────────────────────────────────────────────────────────

  function getYoutubeEmbed(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&\s?]+)/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  }

  async function fetchQaPosts() {
    setQaLoading(true);
    const { data } = await supabase.from('qa_posts')
      .select('*, profiles(email)')
      .order('created_at', { ascending: false });
    if (data) setQaPosts(isAdmin ? data : data.filter(p => !p.is_hidden));
    setQaLoading(false);
  }

  async function openQaThread(post) {
    setOpenQaPost(post);
    const { data } = await supabase.from('qa_replies')
      .select('*, profiles(email)')
      .eq('post_id', post.id)
      .order('created_at', { ascending: true });
    if (data) setQaReplies(data);
  }

  async function submitQaQuestion(e) {
    e.preventDefault();
    if (!newQaQuestion.trim()) return;
    setPostingQa(true);
    const { data, error } = await supabase.from('qa_posts').insert({
      user_id: session.user.id,
      topic: qaQuestionTopic,
      content: newQaQuestion.trim(),
      is_admin_post: false,
      is_anonymous: qaAskAnonymous,
    }).select('*, profiles(email)').single();
    if (error) { showToast(`Error: ${error.message || error.code || 'unknown'}`, 'error'); }
    else if (data) {
      setQaPosts(p => [data, ...p]); setNewQaQuestion(''); setQaAskAnonymous(false);
      const { data: admins } = await supabase.from('profiles').select('id').eq('is_admin', true);
      if (admins && admins.length > 0) {
        await supabase.from('qa_notifications').insert(admins.map(a => ({ user_id: a.id, qa_post_id: data.id })));
        if (isAdmin) setAdminQaNotifs(p => p + admins.length);
      }
    }
    setPostingQa(false);
  }

  async function submitQaVideo(e) {
    e.preventDefault();
    if (!qaVideoForm.title.trim() || !qaVideoForm.video_url.trim()) return;
    setPostingQa(true);
    const { data, error } = await supabase.from('qa_posts').insert({
      user_id: session.user.id,
      topic: qaVideoForm.topic,
      title: qaVideoForm.title.trim(),
      content: qaVideoForm.content.trim() || null,
      video_url: qaVideoForm.video_url.trim(),
      is_admin_post: true,
    }).select('*, profiles(email)').single();
    if (error) { showToast(`Error: ${error.message || error.code || 'unknown'}`, 'error'); }
    else if (data) {
      setQaPosts(p => [data, ...p]);
      setQaVideoForm({ title:'', topic:'General', video_url:'', content:'' });
      setShowQaVideoForm(false);
      showToast('✓ Video posted');
    }
    setPostingQa(false);
  }

  async function submitQaReply(e) {
    e.preventDefault();
    if (!newQaReply.trim() || !openQaPost) return;
    setPostingQa(true);
    const { data, error } = await supabase.from('qa_replies').insert({
      post_id: openQaPost.id, user_id: session.user.id, content: newQaReply.trim(),
    }).select('*, profiles(email)').single();
    if (error) { showToast(`Error: ${error.message || error.code || 'unknown'}`, 'error'); }
    else if (data) {
      setQaReplies(p => [...p, data]); setNewQaReply('');
      if (isAdmin && openQaPost.user_id !== session.user.id) {
        await supabase.from('qa_notifications').insert({
          user_id: openQaPost.user_id, qa_post_id: openQaPost.id, qa_reply_id: data.id,
        });
      }
    }
    setPostingQa(false);
  }

  async function deleteQaPost(id) {
    if (!window.confirm('Delete this post?')) return;
    await supabase.from('qa_posts').delete().eq('id', id);
    setQaPosts(p => p.filter(post => post.id !== id));
    if (openQaPost?.id === id) { setOpenQaPost(null); setQaReplies([]); }
  }

  async function toggleQaHidden(post) {
    const nextHidden = !post.is_hidden;
    const { error } = await supabase.from('qa_posts').update({ is_hidden: nextHidden }).eq('id', post.id);
    if (error) { showToast(`Error: ${error.message}`, 'error'); return; }
    setQaPosts(p => p.map(x => x.id === post.id ? { ...x, is_hidden: nextHidden } : x));
    if (openQaPost?.id === post.id) setOpenQaPost(p => ({ ...p, is_hidden: nextHidden }));
    showToast(nextHidden ? 'Post hidden from members' : 'Post unhidden');
  }

  // ── END Q&A ───────────────────────────────────────────────────────────────

  // ── DIRECT MESSAGES ──────────────────────────────────────────────────────
  // One thread per member (member_id); sender_id tells you who wrote each
  // message. RLS scopes reads/writes to that member + any admin server-side.

  async function fetchDmMessages(memberId) {
    setDmLoading(true);
    try {
      const { data, error } = await supabase.from('dm_messages')
        .select('*').eq('member_id', memberId).order('created_at', { ascending: true });
      if (error) throw error;
      setDmMessages(data || []);
    } catch (err) {
      console.error('fetchDmMessages error:', err);
      showToast('Could not load messages: ' + (err.message || 'unknown error'), 'error');
    } finally {
      setDmLoading(false);
    }
  }

  async function sendDmMessage(memberId, e) {
    e.preventDefault();
    if (!newDmMessage.trim()) return;
    setSendingDm(true);
    try {
      const { data, error } = await supabase.from('dm_messages').insert({
        member_id: memberId, sender_id: session.user.id, body: newDmMessage.trim(),
      }).select().single();
      if (error) throw error;
      setDmMessages(p => [...p, data]);
      setNewDmMessage('');
      setAdminDmThreads(p => {
        const exists = p.some(t => t.member_id === memberId);
        const email = adminMembers.find(a => a.id === memberId)?.email || 'Unknown';
        const updated = exists
          ? p.map(t => t.member_id === memberId ? { ...t, lastMessage: data.body, lastAt: data.created_at } : t)
          : [...p, { member_id: memberId, email, lastMessage: data.body, lastAt: data.created_at, unreadCount: 0 }];
        return updated.slice().sort((a,b) => new Date(b.lastAt) - new Date(a.lastAt));
      });
    } catch (err) {
      console.error('sendDmMessage error:', err);
      showToast('Could not send: ' + (err.message || 'unknown error'), 'error');
    } finally {
      setSendingDm(false);
    }
  }

  async function markDmRead(memberId, viewerId) {
    try {
      await supabase.from('dm_messages').update({ read: true })
        .eq('member_id', memberId).eq('read', false).neq('sender_id', viewerId);
    } catch (err) {
      console.error('markDmRead error:', err);
    }
  }

  async function fetchUnreadDmCount(userId) {
    const { count } = await supabase.from('dm_messages')
      .select('*', { count: 'exact', head: true }).eq('member_id', userId).eq('read', false).neq('sender_id', userId);
    setUnreadDmCount(count || 0);
  }

  async function fetchAdminDmThreads() {
    try {
      const { data, error } = await supabase.from('dm_messages').select('*').order('created_at', { ascending: true });
      if (error) throw error;
      const byMember = {};
      for (const m of (data || [])) {
        if (!byMember[m.member_id]) byMember[m.member_id] = { member_id: m.member_id, unreadCount: 0 };
        byMember[m.member_id].lastMessage = m.body;
        byMember[m.member_id].lastAt = m.created_at;
        if (!m.read && m.sender_id === m.member_id) byMember[m.member_id].unreadCount++;
      }
      const threads = Object.values(byMember)
        .map(t => ({ ...t, email: adminMembers.find(a => a.id === t.member_id)?.email || 'Unknown member' }))
        .sort((a,b) => new Date(b.lastAt) - new Date(a.lastAt));
      setAdminDmThreads(threads);
      setAdminDmUnreadTotal(threads.reduce((s,t) => s + t.unreadCount, 0));
    } catch (err) {
      console.error('fetchAdminDmThreads error:', err);
    }
  }

  async function openAdminDmThread(memberId) {
    setAdminDmSelected(memberId);
    await fetchDmMessages(memberId);
    await markDmRead(memberId, session.user.id);
    setAdminDmThreads(p => p.map(t => t.member_id === memberId ? { ...t, unreadCount: 0 } : t));
    setAdminDmUnreadTotal(p => Math.max(0, p - (adminDmThreads.find(t=>t.member_id===memberId)?.unreadCount || 0)));
  }

  // ── END DIRECT MESSAGES ──────────────────────────────────────────────────

  // ── PUSH NOTIFICATIONS ───────────────────────────────────────────────────
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  }

  async function enablePushNotifications() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      showToast('Push notifications are not supported on this browser.', 'error');
      return;
    }
    setPushBusy(true);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      const json = sub.toJSON();
      const { error } = await supabase.from('push_subscriptions').upsert({
        user_id: session.user.id,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      }, { onConflict: 'endpoint' });
      if (error) throw error;
      showToast('Notifications enabled.');
    } catch (err) {
      console.error('enablePushNotifications error:', err);
      showToast('Could not enable notifications: ' + (err.message || 'unknown error'), 'error');
    } finally {
      setPushBusy(false);
    }
  }
  // ── END PUSH NOTIFICATIONS ───────────────────────────────────────────────

  // ── DAILY REMINDERS ──────────────────────────────────────────────────────
  // Fetched separately from applyProfile on purpose: if the columns don't
  // exist yet, a failure here must not break profile/admin loading.
  async function fetchReminderPrefs(userId) {
    const { data, error } = await supabase.from('profiles').select('reminder_enabled, reminder_hour').eq('id', userId).maybeSingle();
    if (error || !data) return;
    setReminderEnabled(data.reminder_enabled !== false);
    setReminderHour(typeof data.reminder_hour === 'number' ? data.reminder_hour : 7);
  }

  async function saveReminderPrefs(enabled, hour) {
    setReminderEnabled(enabled); setReminderHour(hour);
    setSavingReminder(true);
    const { error } = await supabase.from('profiles').update({ reminder_enabled: enabled, reminder_hour: hour }).eq('id', session.user.id);
    setSavingReminder(false);
    if (error) showToast('Could not save reminder settings: ' + error.message, 'error');
    else showToast('Reminder settings saved.');
  }

  async function fetchMotivationMsgs() {
    const { data, error } = await supabase.from('motivation_messages').select('*').order('created_at', { ascending: true });
    if (error) { showToast('Could not load reminders: ' + error.message, 'error'); return; }
    setMotivationMsgs(data || []);
  }

  async function addMotivationMsg(e) {
    e.preventDefault();
    if (!newMotivation.body.trim()) return;
    setSavingMotivation(true);
    const { data, error } = await supabase.from('motivation_messages').insert({ category: newMotivation.category, body: newMotivation.body.trim() }).select().single();
    setSavingMotivation(false);
    if (error) { showToast('Could not add message: ' + error.message, 'error'); return; }
    setMotivationMsgs(p => [...p, data]);
    setNewMotivation(p => ({ ...p, body: '' }));
  }

  async function toggleMotivationMsg(m) {
    const { error } = await supabase.from('motivation_messages').update({ active: !m.active }).eq('id', m.id);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    setMotivationMsgs(p => p.map(x => x.id === m.id ? { ...x, active: !m.active } : x));
  }

  async function deleteMotivationMsg(id) {
    if (!window.confirm('Delete this message?')) return;
    const { error } = await supabase.from('motivation_messages').delete().eq('id', id);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    setMotivationMsgs(p => p.filter(x => x.id !== id));
  }
  // ── END DAILY REMINDERS ──────────────────────────────────────────────────

  function ytEmbed(url) {
    const m = url && url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&\n?#]+)/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  }

  async function fetchWorkouts(userId) {
    const { data } = await supabase.from('workouts').select('*').order('created_at', { ascending: false });
    if (data) setWorkouts(data);
    if (userId) {
      const { data: asgn } = await supabase.from('workout_assignments').select('workout_id').eq('user_id', userId);
      if (asgn) setMyAssignments(asgn.map(a => a.workout_id));
    }
  }

  async function openWorkout(workout) {
    setSessionSets({});
    setWorkoutStarted(false);
    setWorkoutComplete(false);
    setWorkoutStartTime(null);
    setSelectedWorkout(workout);
    const { data: exs } = await supabase.from('exercises').select('*').eq('workout_id', workout.id).order('sort_order');
    if (!exs) return;
    setWorkoutExercises(exs);
    const ids = exs.map(e => e.id);
    if (ids.length === 0) return;
    const { data: logs } = await supabase.from('exercise_logs').select('*').eq('user_id', session.user.id).in('exercise_id', ids).order('logged_at', { ascending: false });
    if (logs) {
      const map = {};
      logs.forEach(l => { if (!map[l.exercise_id]) map[l.exercise_id] = l; });
      setLastLogs(map);
      const draft = {};
      Object.entries(map).forEach(([eid, l]) => { draft[eid] = { reps: l.reps ?? '', weight: l.weight ?? '', duration: l.duration ?? '', rest: l.rest ?? '', notes: l.notes ?? '' }; });
      setLogDraft(draft);
    }
  }

  async function saveLog(exerciseId) {
    setSavingLog(p => ({ ...p, [exerciseId]: true }));
    const vals = logDraft[exerciseId] || {};
    await supabase.from('exercise_logs').insert({
      user_id: session.user.id, exercise_id: exerciseId,
      reps: vals.reps ? parseInt(vals.reps) : null,
      weight: vals.weight ? parseFloat(vals.weight) : null,
      duration: vals.duration || null, rest: vals.rest || null, notes: vals.notes || null,
    });
    setLastLogs(p => ({ ...p, [exerciseId]: { ...vals, logged_at: new Date().toISOString() } }));
    setSessionSets(p => ({ ...p, [exerciseId]: (p[exerciseId] || 0) + 1 }));
    setSavingLog(p => ({ ...p, [exerciseId]: false }));
  }

  async function fetchAdminWorkouts() {
    const { data } = await supabase.from('workouts').select('*').is('parent_id', null).order('created_at', { ascending: false });
    if (data) setAdminWorkouts(data);
  }

  async function fetchStrengthSessions(programId) {
    const { data } = await supabase.from('workouts').select('*')
      .eq('parent_id', programId).order('week_number').order('day_number');
    if (data) setWktSessions(p => ({ ...p, [programId]: data }));
  }

  async function createStrengthSession(programId, e) {
    e.preventDefault();
    setSavingStrSess(true);
    const prog = adminWorkouts.find(w => w.id === programId);
    const label = strSessForm.day_label || `Week ${strSessForm.week_number} Day ${strSessForm.day_number}`;
    const { data, error } = await supabase.from('workouts').insert({
      parent_id: programId,
      title: label,
      description: strSessForm.description || null,
      week_number: strSessForm.week_number ? parseInt(strSessForm.week_number) : null,
      day_number:  strSessForm.day_number  ? parseInt(strSessForm.day_number)  : null,
      day_label:   strSessForm.day_label   || null,
      tier:        prog?.tier || 'stride',
      visibility:  prog?.visibility || 'public',
    }).select().single();
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else if (data) {
      setWktSessions(p => ({
        ...p,
        [programId]: [...(p[programId]||[]), data].sort((a,b)=>(a.week_number||0)-(b.week_number||0)||(a.day_number||0)-(b.day_number||0)),
      }));
      setStrSessForm({ week_number:'', day_number:'', day_label:'', description:'' });
      showToast('✓ Session added');
    }
    setSavingStrSess(false);
  }

  async function createWorkout(e) {
    e.preventDefault(); setSavingWorkout(true);
    const isProgram = wForm.mode === 'program';
    const payload = {
      title: wForm.title, tier: wForm.tier, visibility: wForm.visibility || 'public',
      description:    isProgram ? null : (wForm.description || null),
      photo_url:      isProgram ? null : (wForm.photo_url   || null),
      duration_weeks: isProgram && wForm.duration_weeks ? parseInt(wForm.duration_weeks) : null,
      week_number:    !isProgram && wForm.week_number ? parseInt(wForm.week_number) : null,
      day_number:     !isProgram && wForm.day_number  ? parseInt(wForm.day_number)  : null,
      day_label:      !isProgram ? (wForm.day_label || null) : null,
      body_focus:     wForm.body_focus || null,
      equipment:      wForm.equipment || null,
      training_goal:  wForm.training_goal || null,
      parent_id:      null,
    };
    const { data } = await supabase.from('workouts').insert(payload).select().single();
    if (data) {
      setAdminWorkouts(p => [data, ...p]);
      if (!isProgram) setWorkouts(p => [data, ...p]);
      setWForm({ title:'', description:'', tier:'stride', photo_url:'', week_number:'', day_number:'', day_label:'', visibility:'public', mode:wForm.mode, duration_weeks:'', body_focus:'', equipment:'', training_goal:'' });
      if (isProgram) { setExpandedStrengthProg(data.id); setAddingStrSessTo(data.id); }
      else { setSelectedAdminWkt(data.id); setAdminExercises([]); }
      showToast(isProgram ? '✓ Program created — add sessions below' : '✓ Workout created');
    }
    setSavingWorkout(false);
  }

  // ── WORKOUT ASSIGNMENTS ───────────────────────────────────────────────────

  async function loadMemberAssignments(userId) {
    setAssignLoading(true);
    const { data } = await supabase.from('workout_assignments')
      .select('id, workout_id, workouts(title, tier)')
      .eq('user_id', userId);
    setMemberAssignments(p => ({ ...p, [userId]: data || [] }));
    setAssignLoading(false);
  }

  async function assignWorkoutToMember(workoutId, userId) {
    if (!workoutId || !userId) return;
    setAssignLoading(true);
    // Insert (ignore conflict if already assigned)
    const { error: insertErr } = await supabase.from('workout_assignments')
      .upsert({ workout_id: workoutId, user_id: userId }, { onConflict: 'workout_id,user_id' });
    if (insertErr) {
      console.error('assignWorkout error:', insertErr);
      showToast(`Error: ${insertErr.message || insertErr.code || 'unknown'}`, 'error');
      setAssignLoading(false);
      return;
    }
    // Fetch the row back (handles both insert and conflict case)
    const { data, error: fetchErr } = await supabase.from('workout_assignments')
      .select('id, workout_id, workouts(title, tier)')
      .eq('workout_id', workoutId).eq('user_id', userId).maybeSingle();
    if (fetchErr) console.error('assignWorkout fetch error:', fetchErr);
    if (data) {
      setMemberAssignments(p => ({ ...p, [userId]: [...(p[userId]||[]).filter(a=>a.workout_id!==workoutId), data] }));
      setAssigningWorkoutId('');
      // Create in-app notification for the member
      await supabase.from('workout_notifications').upsert(
        { user_id: userId, workout_id: workoutId, workout_title: data.workouts?.title || 'New workout' },
        { onConflict: 'user_id,workout_id' }
      );
      showToast(`✓ "${data.workouts?.title || 'Workout'}" assigned`);
    }
    setAssignLoading(false);
  }

  async function removeAssignment(assignmentId, userId) {
    const { error } = await supabase.from('workout_assignments').delete().eq('id', assignmentId);
    if (error) { showToast('Failed to remove assignment', 'error'); return; }
    setMemberAssignments(p => ({ ...p, [userId]: (p[userId]||[]).filter(a=>a.id!==assignmentId) }));
    showToast('Assignment removed');
  }

  // ── END WORKOUT ASSIGNMENTS ───────────────────────────────────────────────

  // ── PRESCRIBED RUNS ───────────────────────────────────────────────────────
  async function fetchPrescribedRuns(userId) {
    const { data } = await supabase.from('prescribed_runs')
      .select('*').eq('user_id', userId).eq('completed', false)
      .order('scheduled_date', { ascending: true });
    if (data) setPrescribedRuns(data);
  }

  async function loadMemberPrescribedRuns(userId) {
    const { data } = await supabase.from('prescribed_runs')
      .select('*').eq('user_id', userId)
      .order('scheduled_date', { ascending: false });
    setMemberPrescRuns(p => ({ ...p, [userId]: data || [] }));
  }

  async function createPrescribedRun(userId) {
    if (!prForm.distance_km || !prForm.scheduled_date) return;
    setSavingPr(true);
    const { data, error } = await supabase.from('prescribed_runs').insert({
      user_id: userId,
      assigned_by: session.user.id,
      scheduled_date: prForm.scheduled_date,
      distance_km: parseFloat(prForm.distance_km),
      run_type: prForm.run_type,
      notes: prForm.notes || null,
    }).select().single();
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else if (data) {
      setMemberPrescRuns(p => ({ ...p, [userId]: [data, ...(p[userId]||[])] }));
      setPrForm({ scheduled_date:'', distance_km:'', run_type:'Easy', notes:'' });
      showToast('✓ Run prescribed');
    }
    setSavingPr(false);
  }

  async function deletePrescribedRun(id, userId) {
    const { error } = await supabase.from('prescribed_runs').delete().eq('id', id);
    if (error) { showToast(`Error: ${error.message}`, 'error'); return; }
    setMemberPrescRuns(p => ({ ...p, [userId]: (p[userId]||[]).filter(r => r.id !== id) }));
    showToast('Prescribed run removed');
  }

  async function completePrescribedRun(prescribedId, runId) {
    await supabase.from('prescribed_runs').update({ completed: true, completed_run_id: runId }).eq('id', prescribedId);
    setPrescribedRuns(p => p.filter(r => r.id !== prescribedId));
  }
  // ── END PRESCRIBED RUNS ───────────────────────────────────────────────────

  // ── EXERCISE LIBRARY ─────────────────────────────────────────────────────
  const LIB_MUSCLES = ['All','Chest','Back','Legs','Shoulders','Arms','Core','Full Body','Glutes','Calves'];
  const LIB_CATS    = ['All','Strength','Rehab'];

  async function fetchExerciseLibrary() {
    const { data } = await supabase.from('exercise_library').select('*').order('muscle_group').order('name');
    if (data) setExerciseLibrary(data);
  }

  async function createLibraryExercise(e) {
    if (e) e.preventDefault();
    if (!newLibEx.name.trim()) return;
    setSavingLibEx(true);
    const { data, error } = await supabase.from('exercise_library').insert({
      name: newLibEx.name.trim(), muscle_group: newLibEx.muscle_group,
      category: newLibEx.category, video_url: newLibEx.video_url || null,
      notes: newLibEx.notes || null, created_by: session.user.id,
    }).select().single();
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else if (data) {
      setExerciseLibrary(p => [...p, data].sort((a,b)=>a.name.localeCompare(b.name)));
      setNewLibEx({ name:'', muscle_group:'Full Body', category:'Strength', video_url:'', notes:'' });
      showToast('✓ Exercise saved to library');
    }
    setSavingLibEx(false);
  }

  async function deleteLibraryExercise(id) {
    await supabase.from('exercise_library').delete().eq('id', id);
    setExerciseLibrary(p => p.filter(ex => ex.id !== id));
    showToast('Exercise removed from library');
  }
  // ── END EXERCISE LIBRARY ─────────────────────────────────────────────────

  // ── CALENDAR ─────────────────────────────────────────────────────────────
  function getWeekDays(start) {
    return Array.from({length:7}, (_,i) => { const d=new Date(start); d.setDate(d.getDate()+i); return d; });
  }
  function toDateStr(d) { return d instanceof Date ? d.toISOString().slice(0,10) : d; }

  async function fetchAdminCalScheduled(userId, weekStart) {
    if (!userId) return;
    const days = getWeekDays(weekStart);
    const { data } = await supabase.from('scheduled_workouts')
      .select('*, workouts(id,title,tier)')
      .eq('user_id', userId)
      .gte('scheduled_date', toDateStr(days[0]))
      .lte('scheduled_date', toDateStr(days[6]));
    if (data) setScheduledWorkouts(data);
  }

  async function fetchMemberScheduledWorkouts(userId) {
    const d = new Date(); d.setDate(d.getDate()-((d.getDay()+6)%7)); d.setHours(0,0,0,0);
    const end = new Date(d); end.setDate(end.getDate()+13);
    const { data } = await supabase.from('scheduled_workouts')
      .select('*, workouts(id,title,tier,description)')
      .eq('user_id', userId)
      .gte('scheduled_date', toDateStr(d))
      .lte('scheduled_date', toDateStr(end));
    if (data) setMemberScheduled(data);
  }

  async function fetchCompletedWorkouts(userId) {
    const { data } = await supabase.from('scheduled_workouts')
      .select('*, workouts(id,title,tier)')
      .eq('user_id', userId).eq('completed', true)
      .order('scheduled_date', { ascending: false });
    if (data) setCompletedWorkouts(data);
  }

  async function scheduleWorkoutForMember() {
    if (!calMember || !assigningCalWkt || !assigningCalDay) return;
    setSavingCal(true);
    const { error } = await supabase.from('scheduled_workouts').insert({
      workout_id: assigningCalWkt, user_id: calMember,
      assigned_by: session.user.id, scheduled_date: assigningCalDay,
    });
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else {
      const wkt = adminWorkouts.find(w => w.id === assigningCalWkt);
      await supabase.from('workout_notifications').upsert(
        { user_id: calMember, workout_id: assigningCalWkt, workout_title: (wkt?.title||'Workout')+' (scheduled)' },
        { onConflict: 'user_id,workout_id' }
      );
      await fetchAdminCalScheduled(calMember, calWeekStart);
      setAssigningCalDay(null); setAssigningCalWkt('');
      showToast('✓ Workout scheduled');
    }
    setSavingCal(false);
  }

  async function removeScheduledWorkout(id) {
    const { error } = await supabase.from('scheduled_workouts').delete().eq('id', id);
    if (error) { showToast(`Error: ${error.message}`, 'error'); return; }
    setScheduledWorkouts(p => p.filter(s => s.id !== id));
    setMemberScheduled(p => p.filter(s => s.id !== id));
    showToast('Removed from calendar');
  }

  async function autoCompleteScheduledWorkout(workoutId) {
    const today = new Date().toISOString().slice(0,10);
    const match = memberScheduled.find(s => s.workout_id === workoutId && s.scheduled_date === today && !s.completed);
    if (!match) return;
    await supabase.from('scheduled_workouts')
      .update({ completed: true, completed_at: new Date().toISOString() })
      .eq('id', match.id);
    setMemberScheduled(p => p.map(s => s.id === match.id ? {...s, completed:true, completed_at:new Date().toISOString()} : s));
    await supabase.from('admin_completion_notifications').insert({
      scheduled_workout_id: match.id, user_id: session.user.id,
    });
  }

  async function fetchAdminCompNotifs() {
    const { count } = await supabase.from('admin_completion_notifications')
      .select('*', { count:'exact', head:true }).eq('read', false);
    setAdminCompNotifs(count || 0);
  }

  async function markAdminCompNotifsRead() {
    await supabase.from('admin_completion_notifications').update({ read:true }).eq('read', false);
    setAdminCompNotifs(0);
  }
  // ── END CALENDAR ─────────────────────────────────────────────────────────

  // ── RUN WORKOUTS ─────────────────────────────────────────────────────────
  const RUN_TYPES = ['Easy','Threshold','Intervals','Skill Building','Trail'];

  async function fetchAdminRunWorkouts() {
    const { data } = await supabase.from('run_workouts').select('*').is('parent_id', null).order('created_at', { ascending: false });
    if (data) setAdminRunWorkouts(data);
  }

  async function fetchMemberRunWorkouts(userId) {
    // Fetches every public plan's run workouts, not just the member's own
    // tier -- accessibility is checked client-side (see trainingViewTierRun)
    // so higher tiers can still be shown as a locked preview.
    const { data } = await supabase.from('run_workouts').select('*')
      .eq('visibility','public').is('parent_id', null).order('created_at', { ascending: false });
    if (data) setMemberRunWorkouts(data);
  }

  async function fetchProgramSessions(programId) {
    const { data } = await supabase.from('run_workouts').select('*')
      .eq('parent_id', programId).order('week_number').order('day_number');
    if (data) setRunWktSessions(p => ({ ...p, [programId]: data }));
  }

  async function createRunWorkout(e) {
    e.preventDefault();
    if (!runWForm.title.trim()) return;
    setSavingRunWkt(true);
    const isProgram = runWForm.mode === 'program';
    const { data, error } = await supabase.from('run_workouts').insert({
      title: runWForm.title.trim(),
      run_type: isProgram ? null : runWForm.run_type,
      duration: isProgram ? null : (runWForm.duration || null),
      rpe: isProgram ? null : (runWForm.rpe || null),
      notes: runWForm.notes || null,
      tier: runWForm.tier,
      visibility: runWForm.visibility,
      created_by: session.user.id,
      duration_weeks: isProgram && runWForm.duration_weeks ? parseInt(runWForm.duration_weeks) : null,
      video_url: runWForm.video_url || null,
      parent_id: null,
    }).select().single();
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else if (data) {
      setAdminRunWorkouts(p => [data, ...p]);
      setRunWForm({ title:'', run_type:'Easy', duration:'', rpe:'', notes:'', tier:'stride', visibility:'public', mode:runWForm.mode, duration_weeks:'', video_url:'' });
      showToast('✓ Run workout created');
      if (isProgram) { setExpandedRunWkt(data.id); setAddingSessionTo(data.id); }
    }
    setSavingRunWkt(false);
  }

  async function createRunSession(programId, e) {
    e.preventDefault();
    setSavingSession(true);
    const sessionTitle = runSessionForm.day_label || runSessionForm.run_type;
    const { data, error } = await supabase.from('run_workouts').insert({
      parent_id: programId,
      title:       sessionTitle,
      week_number: runSessionForm.week_number ? parseInt(runSessionForm.week_number) : null,
      day_number:  runSessionForm.day_number  ? parseInt(runSessionForm.day_number)  : null,
      day_label:   runSessionForm.day_label   || null,
      run_type:    runSessionForm.run_type,
      duration:    runSessionForm.duration    || null,
      rpe:         runSessionForm.rpe         || null,
      notes:       runSessionForm.notes       || null,
      created_by:  session.user.id,
      visibility:  'public', tier: 'stride',
    }).select().single();
    if (error) { showToast(`Error: ${error.message}`, 'error'); }
    else if (data) {
      setRunWktSessions(p => ({ ...p, [programId]: [...(p[programId]||[]), data].sort((a,b)=>(a.week_number||0)*10+(a.day_number||0)-((b.week_number||0)*10+(b.day_number||0))) }));
      setRunSessionForm({ week_number:'', day_number:'', day_label:'', run_type:'Easy', duration:'', rpe:'', notes:'' });
      showToast('✓ Session added');
    }
    setSavingSession(false);
  }

  async function deleteRunSession(sessionId, programId) {
    await supabase.from('run_workouts').delete().eq('id', sessionId);
    setRunWktSessions(p => ({ ...p, [programId]: (p[programId]||[]).filter(s => s.id !== sessionId) }));
    showToast('Session deleted');
  }

  function downloadSessionTemplate() {
    const header = 'week_number,day_number,day_label,run_type,duration,rpe,notes';
    const example = '1,1,Easy Aerobic,Easy/Z2,45 min,5,"10 min warm-up, 25 min easy, 10 min cool-down"';
    const blob = new Blob([header + '\n' + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'session_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  async function importRunSessions(programId, file) {
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) { showToast('CSV appears empty', 'error'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = [];
      let cur = '', inQ = false;
      for (const ch of lines[i]) {
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
        else { cur += ch; }
      }
      vals.push(cur.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
      rows.push(row);
    }
    const inserts = rows.map(r => ({
      parent_id:   programId,
      title:       r.day_label || r.run_type || 'Session',
      week_number: r.week_number ? parseInt(r.week_number) : null,
      day_number:  r.day_number  ? parseInt(r.day_number)  : null,
      day_label:   r.day_label   || null,
      run_type:    r.run_type    || 'Easy',
      duration:    r.duration    || null,
      rpe:         r.rpe         || null,
      notes:       r.notes       || null,
      created_by:  session.user.id,
      visibility:  'public', tier: 'stride',
    }));
    const { data, error } = await supabase.from('run_workouts').insert(inserts).select();
    if (error) { showToast(`Import error: ${error.message}`, 'error'); return; }
    if (data) {
      setRunWktSessions(p => {
        const existing = p[programId] || [];
        const merged = [...existing, ...data].sort((a,b) => (a.week_number||0)*10+(a.day_number||0) - ((b.week_number||0)*10+(b.day_number||0)));
        return { ...p, [programId]: merged };
      });
      showToast(`✓ Imported ${data.length} sessions`);
    }
  }

  async function updateRunWorkout(id) {
    const payload = {
      title:          editRunWktForm.title.trim(),
      duration_weeks: editRunWktForm.duration_weeks ? parseInt(editRunWktForm.duration_weeks) : null,
      tier:           editRunWktForm.tier,
      notes:          editRunWktForm.notes || null,
      run_type:       editRunWktForm.run_type || null,
      duration:       editRunWktForm.duration || null,
      rpe:            editRunWktForm.rpe || null,
      video_url:      editRunWktForm.video_url || null,
    };
    const { data } = await supabase.from('run_workouts').update(payload).eq('id', id).select().single();
    if (data) {
      setAdminRunWorkouts(p => p.map(w => w.id === id ? { ...w, ...data } : w));
      setEditingRunWkt(null);
      showToast('✓ Saved');
    }
  }

  async function deleteRunWorkout(id) {
    if (!window.confirm('Delete this run workout?')) return;
    await supabase.from('run_workouts').delete().eq('id', id);
    setAdminRunWorkouts(p => p.filter(w => w.id !== id));
    setMemberRunWorkouts(p => p.filter(w => w.id !== id));
    setRunWktSessions(p => { const n={...p}; delete n[id]; return n; });
    if (expandedRunWkt === id) setExpandedRunWkt(null);
    showToast('Run workout deleted');
  }
  // ── END RUN WORKOUTS ─────────────────────────────────────────────────────

  async function deleteWorkout(id) {
    if (!window.confirm('Delete this workout and all its exercises?')) return;
    await supabase.from('workouts').delete().eq('id', id);
    setAdminWorkouts(p => p.filter(w => w.id !== id)); setWorkouts(p => p.filter(w => w.id !== id));
    if (selectedAdminWkt === id) { setSelectedAdminWkt(null); setAdminExercises([]); }
  }

  async function updateWorkout(id) {
    const payload = {
      title: editWktForm.title,
      description: editWktForm.description || null,
      tier: editWktForm.tier,
      photo_url: editWktForm.photo_url || null,
      week_number: editWktForm.week_number ? parseInt(editWktForm.week_number) : null,
      day_number: editWktForm.day_number ? parseInt(editWktForm.day_number) : null,
      day_label: editWktForm.day_label || null,
      visibility: editWktForm.visibility || 'public',
      body_focus: editWktForm.body_focus || null,
      equipment: editWktForm.equipment || null,
      training_goal: editWktForm.training_goal || null,
    };
    const { data } = await supabase.from('workouts').update(payload).eq('id', id).select().single();
    if (data) {
      setAdminWorkouts(p => p.map(w => w.id === id ? data : w));
      setWorkouts(p => p.map(w => w.id === id ? data : w));
      setEditingWorkout(null);
    }
  }

  async function uploadWorkoutPhoto(file, workoutId) {
    setUploadingWktPhoto(true);
    const ext = file.name.split('.').pop();
    const path = 'workouts/' + workoutId + '-' + Date.now() + '.' + ext;
    const { data: upload } = await supabase.storage.from('forum-photos').upload(path, file, { upsert: true });
    if (upload) {
      const { data: { publicUrl } } = supabase.storage.from('forum-photos').getPublicUrl(path);
      setEditWktForm(p => ({ ...p, photo_url: publicUrl }));
    }
    setUploadingWktPhoto(false);
  }

  async function fetchCoachBio() {
    setCoachBioLoading(true);
    const { data } = await supabase.from('coach_bio').select('*').eq('id', 1).maybeSingle();
    if (data) {
      setCoachBio(data);
      setBioForm({
        name: data.name || '',
        tagline: data.tagline || '',
        bio: data.bio || '',
        credentials: (data.credentials || []).join('\n'),
      });
    }
    setCoachBioLoading(false);
  }

  async function saveCoachBio() {
    setSavingBio(true);
    const payload = {
      id: 1,
      name: bioForm.name,
      tagline: bioForm.tagline,
      bio: bioForm.bio,
      credentials: bioForm.credentials.split('\n').map(s => s.trim()).filter(Boolean),
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('coach_bio').upsert(payload).select().single();
    if (data) { setCoachBio(data); }
    if (error) console.error('saveCoachBio error:', error);
    setSavingBio(false);
  }

  async function uploadBioPhoto(file) {
    setUploadingBioPhoto(true);
    const ext = file.name.split('.').pop();
    const path = 'bio/' + Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext;
    const { data: upload, error } = await supabase.storage.from('forum-photos').upload(path, file, { upsert: true });
    if (upload) {
      const { data: { publicUrl } } = supabase.storage.from('forum-photos').getPublicUrl(path);
      const currentUrls = coachBio?.photo_urls || [];
      const newUrls = [...currentUrls, publicUrl];
      await supabase.from('coach_bio').upsert({ id: 1, photo_urls: newUrls });
      setCoachBio(p => ({ ...(p || { id:1 }), photo_urls: newUrls }));
    }
    if (error) console.error('uploadBioPhoto error:', error);
    setUploadingBioPhoto(false);
  }

  async function deleteBioPhoto(url) {
    const newUrls = (coachBio?.photo_urls || []).filter(u => u !== url);
    await supabase.from('coach_bio').upsert({ id: 1, photo_urls: newUrls });
    setCoachBio(p => ({ ...(p || { id:1 }), photo_urls: newUrls }));
  }

  async function openAdminWorkout(id) {
    setSelectedAdminWkt(id);
    const { data } = await supabase.from('exercises').select('*').eq('workout_id', id).order('sort_order');
    if (data) setAdminExercises(data);
  }

  // ── NUTRITION CRUD ────────────────────────────────────────────────────────

  async function fetchNutritionItems() {
    setNutritionLoading(true);
    const { data } = await supabase.from('nutrition_items').select('*').order('sort_order').order('created_at');
    if (data) setNutritionItems(data);
    setNutritionLoading(false);
  }

  async function createNutritionItem(e) {
    e.preventDefault();
    setSavingNutrition(true);
    const payload = {
      title: nForm.title,
      summary: nForm.summary || null,
      content: nForm.content || null,
      section: nForm.section,
      category: nForm.category,
      tier: nForm.tier,
      file_url: nForm.file_url || null,
      file_type: nForm.file_type || null,
      file_name: nForm.file_name || null,
      sort_order: parseInt(nForm.sort_order) || 0,
    };
    const { data } = await supabase.from('nutrition_items').insert(payload).select().single();
    if (data) {
      setNutritionItems(p => [...p, data]);
      setNForm({ title:'', summary:'', content:'', section:'sports_performance', category:'General', tier:'stride', file_url:'', file_type:'', file_name:'', sort_order:'0' });
    }
    setSavingNutrition(false);
  }

  async function updateNutritionItem(id) {
    setSavingNutrition(true);
    const payload = {
      title: editNForm.title,
      summary: editNForm.summary || null,
      content: editNForm.content || null,
      section: editNForm.section,
      category: editNForm.category,
      tier: editNForm.tier,
      file_url: editNForm.file_url || null,
      file_type: editNForm.file_type || null,
      file_name: editNForm.file_name || null,
      sort_order: parseInt(editNForm.sort_order) || 0,
    };
    const { data } = await supabase.from('nutrition_items').update(payload).eq('id', id).select().single();
    if (data) {
      setNutritionItems(p => p.map(n => n.id === id ? data : n));
      setEditingNutritionItem(null);
    }
    setSavingNutrition(false);
  }

  async function deleteNutritionItem(id) {
    if (!window.confirm('Delete this nutrition item?')) return;
    await supabase.from('nutrition_items').delete().eq('id', id);
    setNutritionItems(p => p.filter(n => n.id !== id));
  }

  async function uploadNutritionFile(file, target) {
    // target = 'new' | item id (for editing)
    setUploadingNutritionFile(true);
    const ext = file.name.split('.').pop().toLowerCase();
    const path = 'nutrition/' + Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext;
    const { data: upload, error } = await supabase.storage.from('forum-photos').upload(path, file, { upsert: true });
    if (upload) {
      const { data: { publicUrl } } = supabase.storage.from('forum-photos').getPublicUrl(path);
      const fileType = ['pdf'].includes(ext) ? 'pdf'
        : ['mp4','mov','avi','webm','mkv','m4v'].includes(ext) ? 'video'
        : 'image';
      if (target === 'new') {
        setNForm(p => ({ ...p, file_url: publicUrl, file_type: fileType, file_name: file.name }));
      } else {
        setEditNForm(p => ({ ...p, file_url: publicUrl, file_type: fileType, file_name: file.name }));
      }
    }
    if (error) console.error('uploadNutritionFile error:', error);
    setUploadingNutritionFile(false);
  }

  // ── END NUTRITION CRUD ────────────────────────────────────────────────────

  // Shared admin-only video upload, used by exercises, the exercise library,
  // Q&A videos, run workouts, and report walkthroughs. Public 'videos'
  // bucket, but storage RLS restricts writes to admins server-side -- the
  // client role check here is just UX, not the actual enforcement.
  async function uploadVideoFile(file, onUploaded) {
    setUploadingVideo(true);
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('videos').upload(path, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('videos').getPublicUrl(path);
      onUploaded(publicUrl);
    } catch (err) {
      console.error('uploadVideoFile error:', err);
      showToast('Video upload failed: ' + (err.message || 'unknown error'), 'error');
    } finally {
      setUploadingVideo(false);
    }
  }

  // ── METABOLIC REPORTS ─────────────────────────────────────────────────────
  // Confidential health data. RLS on both the table and the private 'reports'
  // storage bucket scopes every row/file to its own user_id server-side; this
  // client code never widens that (no cross-member queries, no public URLs).

  async function fetchMyReports(userId) {
    setMyReportsLoading(true);
    try {
      const { data, error } = await supabase.from('metabolic_reports')
        .select('*').eq('user_id', userId).order('test_date', { ascending: false });
      if (error) throw error;
      setMyReports(data || []);
    } catch (err) {
      console.error('fetchMyReports error:', err);
    } finally {
      setMyReportsLoading(false);
    }
  }

  // Note: deliberately NOT joining profiles(email) here via a PostgREST FK
  // embed (e.g. profiles!metabolic_reports_user_id_fkey(email)) -- that
  // requires PostgREST's schema cache to have already picked up the new
  // table/FK, which can lag right after running a migration and fails the
  // whole query if it hasn't. Member emails are looked up client-side from
  // adminMembers (already fetched) instead -- see the admin Reports list.
  async function fetchAdminReports() {
    setAdminReportsLoading(true);
    try {
      const { data, error } = await supabase.from('metabolic_reports')
        .select('*').order('test_date', { ascending: false });
      if (error) throw error;
      setAdminReports(data || []);
    } catch (err) {
      console.error('fetchAdminReports error:', err);
      showToast('Could not load reports: ' + (err.message || 'unknown error'), 'error');
    } finally {
      setAdminReportsLoading(false);
    }
  }

  async function createReport(e) {
    e.preventDefault();
    if (!reportForm.user_id || !reportForm.test_date || !reportForm.file) return;
    setSavingReport(true);
    setUploadingReportFile(true);
    const file = reportForm.file;
    const ext = file.name.split('.').pop().toLowerCase();
    const path = `${reportForm.user_id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    try {
      const { error: uploadErr } = await supabase.storage.from('reports').upload(path, file);
      setUploadingReportFile(false);
      if (uploadErr) throw uploadErr;

      const fileType = ext === 'pdf' ? 'pdf' : 'image';
      const payload = {
        user_id: reportForm.user_id,
        uploaded_by: session.user.id,
        title: reportForm.title || null,
        test_date: reportForm.test_date,
        file_path: path,
        file_name: file.name,
        file_type: fileType,
        rmr: reportForm.rmr === '' ? null : parseFloat(reportForm.rmr),
        vo2max: reportForm.vo2max === '' ? null : parseFloat(reportForm.vo2max),
        vt1: reportForm.vt1 === '' ? null : parseFloat(reportForm.vt1),
        vt2: reportForm.vt2 === '' ? null : parseFloat(reportForm.vt2),
        cho_fat_ratio_rest: reportForm.cho_fat_ratio_rest === '' ? null : reportForm.cho_fat_ratio_rest.trim(),
        notes: reportForm.notes || null,
        video_url: reportForm.video_url || null,
      };
      const { data, error } = await supabase.from('metabolic_reports').insert(payload).select().single();
      if (error) throw error;

      setAdminReports(p => [data, ...p]);
      setReportForm({ user_id:'', title:'', test_date:'', rmr:'', vo2max:'', vt1:'', vt2:'', cho_fat_ratio_rest:'', notes:'', video_url:'', file:null });
      showToast('Report uploaded.');
    } catch (err) {
      console.error('createReport error:', err);
      showToast('Upload failed: ' + (err.message || 'unknown error'), 'error');
      await supabase.storage.from('reports').remove([path]).catch(()=>{});
    } finally {
      setUploadingReportFile(false);
      setSavingReport(false);
    }
  }

  async function deleteReport(report) {
    if (!window.confirm('Delete this report? This cannot be undone.')) return;
    try {
      await supabase.storage.from('reports').remove([report.file_path]);
      const { error } = await supabase.from('metabolic_reports').delete().eq('id', report.id);
      if (error) throw error;
      setAdminReports(p => p.filter(r => r.id !== report.id));
      showToast('Report deleted.');
    } catch (err) {
      console.error('deleteReport error:', err);
      showToast('Delete failed: ' + (err.message || 'unknown error'), 'error');
    }
  }

  async function openReportFile(report) {
    setOpenReport(report);
    setReportSignedUrl('');
    try {
      const { data, error } = await supabase.storage.from('reports').createSignedUrl(report.file_path, 300);
      if (error) throw error;
      if (data?.signedUrl) {
        setReportSignedUrl(data.signedUrl);
        await supabase.from('report_access_log').insert({ report_id: report.id, accessed_by: session.user.id });
      }
    } catch (err) {
      console.error('openReportFile error:', err);
      showToast('Could not open report: ' + (err.message || 'unknown error'), 'error');
      setOpenReport(null);
    }
  }

  // ── END METABOLIC REPORTS ─────────────────────────────────────────────────

  function downloadExerciseTemplate() {
    const header = 'grouping,exercise,sets,reps,notes';
    const example = 'A,Barbell Back Squat,4,6-8,"Keep chest up, brace core"';
    const blob = new Blob([header + '\n' + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'exercise_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  async function importExercises(workoutId, file) {
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) { showToast('CSV appears empty', 'error'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = [];
      let cur = '', inQ = false;
      for (const ch of lines[i]) {
        if (ch === '"') { inQ = !inQ; }
        else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
        else { cur += ch; }
      }
      vals.push(cur.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
      rows.push(row);
    }
    const inserts = rows.filter(r => r.exercise).map((r, idx) => ({
      workout_id:       workoutId,
      name:             r.exercise,
      superset_group:   r.grouping   || null,
      sets:             r.sets       ? parseInt(r.sets) : null,
      prescribed_reps:  r.reps       || null,
      prescribed_notes: r.notes      || null,
      sort_order:       idx,
      exercise_type:    'working',
      video_url:        null,
      prescribed_rpe:   null,
    }));
    const { data, error } = await supabase.from('exercises').insert(inserts).select();
    if (error) { showToast(`Import error: ${error.message}`, 'error'); return; }
    if (data) {
      setAdminExercises(p => [...p, ...data]);
      showToast(`✓ Imported ${data.length} exercises`);
    }
  }

  async function reorderExercises(dragId, dropId) {
    if (!dragId || !dropId || dragId === dropId) return;
    const list = [...adminExercises];
    const fromIdx = list.findIndex(e => e.id === dragId);
    const toIdx   = list.findIndex(e => e.id === dropId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    const updated = list.map((ex, i) => ({ ...ex, sort_order: i }));
    setAdminExercises(updated);
    await Promise.all(updated.map(ex => supabase.from('exercises').update({ sort_order: ex.sort_order }).eq('id', ex.id)));
  }

  async function createExercise(e) {
    e.preventDefault();
    const payload = { workout_id: selectedAdminWkt, name: eForm.name, video_url: eForm.video_url || null, sort_order: adminExercises.length, sets: eForm.sets ? parseInt(eForm.sets) : null, prescribed_reps: eForm.prescribed_reps || null, prescribed_rpe: eForm.prescribed_rpe ? parseFloat(eForm.prescribed_rpe) : null, prescribed_notes: eForm.prescribed_notes || null, superset_group: eForm.superset_group || null, exercise_type: eForm.exercise_type || 'working' };
    const { data } = await supabase.from('exercises').insert(payload).select().single();
    if (data) { setAdminExercises(p => [...p, data]); setEForm({ name:'', video_url:'', sort_order:'0', sets:'', prescribed_reps:'', prescribed_rpe:'', prescribed_notes:'', superset_group:'', exercise_type:'working' }); }
  }

  async function deleteExercise(id) {
    await supabase.from('exercises').delete().eq('id', id);
    setAdminExercises(p => p.filter(ex => ex.id !== id));
    if (editingExercise === id) setEditingExercise(null);
  }

  async function updateExercise(id) {
    const payload = {
      name: editExForm.name,
      video_url: editExForm.video_url || null,
      sort_order: parseInt(editExForm.sort_order) || 0,
      sets: editExForm.sets ? parseInt(editExForm.sets) : null,
      prescribed_reps: editExForm.prescribed_reps || null,
      prescribed_rpe: editExForm.prescribed_rpe ? parseFloat(editExForm.prescribed_rpe) : null,
      prescribed_notes: editExForm.prescribed_notes || null,
      superset_group: editExForm.superset_group || null,
      exercise_type: editExForm.exercise_type || 'working',
    };
    const { data } = await supabase.from('exercises').update(payload).eq('id', id).select().single();
    if (data) {
      setAdminExercises(p => p.map(ex => ex.id === id ? data : ex));
      setEditingExercise(null);
    }
  }

  useEffect(() => { if (mode === "admin") fetchAdminMembers(); }, [mode]);
  useEffect(() => { if (mode === "admin") fetchAdminWorkouts(); }, [mode]);

  async function fetchAdminMembers() {
    setAdminLoadingMembers(true);
    const { data } = await supabase.from("profiles").select("id, email, tier, is_admin, created_at").order("created_at", { ascending: false });
    if (data) setAdminMembers(data);
    setAdminLoadingMembers(false);
  }

  async function changeTier(userId, tier) {
    await supabase.from("profiles").update({ tier }).eq("id", userId);
    setAdminMembers(prev => prev.map(m => m.id === userId ? { ...m, tier } : m));
    if (userId === session?.user?.id) {
      setUserTier(tier);
      fetchWorkouts(userId);
      fetchMemberRunWorkouts(userId);
    }
  }

  async function handleUpgrade(tier) {
    if (tier === 'stride') return;
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, userId: session.user.id, email: session.user.email }),
      });
      const { url, error } = await res.json();
      if (url) window.location.href = url;
      else alert(error || 'Something went wrong. Please try again.');
    } catch {
      alert('Could not connect to payment service. Please try again.');
    }
  }

  if (authLoading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F2E9DF',color:'#241a14',fontFamily:"'Poppins',sans-serif",letterSpacing:3,fontSize:11,textTransform:'uppercase'}}>
      Loading…
    </div>
  );

  if (!session) return (
    <>
      <style>{CSS}</style>
      <div style={{minHeight:'100vh',background:'#F2E9DF',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
        <div style={{width:'100%',maxWidth:360,background:'#fff',border:'1px solid #e4d6c2',borderRadius:12,padding:'36px 32px',boxShadow:'0 4px 24px rgba(36,26,20,0.08)'}}>
          <LogoNav style={{ width: "100%", maxWidth: "100%", maxHeight: "none" }} />
          <img src="/tagline.png" alt="Test. Train. Run. No BS." style={{height:12,width:'auto',marginTop:10,display:'block'}} />
          <h2 style={{color:'#241a14',fontFamily:"'Poppins',sans-serif",letterSpacing:2,fontSize:12,textTransform:'uppercase',marginTop:28,marginBottom:24}}>
            {authView === 'login' ? 'Sign In' : 'Create Account'}
          </h2>
          {authError && (
            <div style={{color: authError.includes('Check') ? '#2f8a4e' : 'var(--red)', fontSize:13, marginBottom:16, lineHeight:1.5}}>
              {authError}
            </div>
          )}
          <form onSubmit={handleAuth}>
            <input
              value={authEmail} onChange={e => setAuthEmail(e.target.value)}
              type="email" placeholder="Email" required
              style={{width:'100%',background:'#F2E9DF',border:'1px solid #e4d6c2',color:'#241a14',borderRadius:6,padding:'10px 12px',fontSize:13,marginBottom:12,boxSizing:'border-box',outline:'none'}}
            />
            <input
              value={authPassword} onChange={e => setAuthPassword(e.target.value)}
              type="password" placeholder="Password" required
              style={{width:'100%',background:'#F2E9DF',border:'1px solid #e4d6c2',color:'#241a14',borderRadius:6,padding:'10px 12px',fontSize:13,marginBottom:20,boxSizing:'border-box',outline:'none'}}
            />
            <button type="submit" disabled={authBusy} className="btn btn-red" style={{width:'100%'}}>
              {authBusy ? '…' : authView === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
          <div style={{textAlign:'center',marginTop:18,fontSize:12,color:'#8a7a6e'}}>
            {authView === 'login'
              ? <><span>No account? </span><span style={{color:'var(--red)',cursor:'pointer'}} onClick={() => { setAuthView('signup'); setAuthError(''); }}>Sign up</span></>
              : <><span>Have an account? </span><span style={{color:'var(--red)',cursor:'pointer'}} onClick={() => { setAuthView('login'); setAuthError(''); }}>Sign in</span></>
            }
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="app">

        {/* ── TOAST ── */}
        {toast && (
          <div style={{
            position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
            background: toast.type==='error' ? 'var(--red-dark)' : '#1e3a28',
            border: `1px solid ${toast.type==='error' ? 'var(--red)' : '#3a7a52'}`,
            color:'var(--on-red)', borderRadius:8, padding:'12px 24px',
            fontFamily:"'Poppins',sans-serif", fontSize:13, letterSpacing:1,
            zIndex:9999, whiteSpace:'nowrap', boxShadow:'0 4px 24px rgba(0,0,0,0.5)',
            animation:'fadeIn 0.2s ease',
          }}>
            {toast.msg}
          </div>
        )}

        {/* ── NAV ── */}
        <nav className="nav">
          <LogoNav />
          <div className="nav-right">
            {mode === "member" && (
              <span className={`tier-pill pill-${userTier}`}>{userTier}</span>
            )}
            {isAdmin && (
              <button
                className={`nav-btn ${mode === "admin" ? "on" : ""}`}
                onClick={() => setMode(mode === "admin" ? "member" : "admin")}
              >
                {mode === "admin" ? "← Member" : "Admin"}
              </button>
            )}
            <button className="nav-btn" onClick={handleLogout}>Sign Out</button>

          </div>
        </nav>

        {mode === "member" ? (<>

          {/* ── HERO ── */}
          <div className="hero">
            {/* Watermark mountain */}
            <svg className="hero-mtn" width="600" height="380" viewBox="0 0 600 380" fill="none">
              <path d="M0 360 L160 68 L208 116 L248 46 L292 0 L336 52 L372 24 L428 96 L466 64 L520 128 L556 96 L600 360Z" fill="#241a14"/>
            </svg>

            <div className="hero-eyebrow">Metabolic Testing & Coaching for Longevity and Performance</div>
            <h1 className="hero-h1">BUILT<br/>TO <em>ENDURE.</em></h1>
            <div className="hero-rule" />
            <p className="hero-sub">
              Training plans, expert nutrition, and direct coaching — built on science, tailored to your physiology. No templates. No guesswork.
            </p>
            <div className="hero-cta">
              <button className="btn btn-red" onClick={() => setTab("membership")}>View Plans</button>
              <button className="btn btn-ghost" onClick={() => setTab("dashboard")}>My Dashboard</button>
            </div>
          </div>

          {/* ── TABNAV ── */}
          <div className="tabnav">
            {[["dashboard","Dashboard"],["training","Training"],["nutrition","Nutrition"],["reports","My Reports"],["messages","Messages"],["community","Community"],["qa","Q & A"],["squad","Owner / Head Coach"],["membership","Membership"]].map(([t,label]) => (
              <button key={t} className={`tab ${tab===t?"on":""}`}
                onClick={() => {
                  setTab(t);
                  if(t==='community'){ fetchForumPosts(); markNotificationsRead(); }
                  if(t==='qa'){ fetchQaPosts(); markQaNotifsRead(session?.user?.id); }
                  if(t==='training' && unreadWorkouts > 0){ markWorkoutNotifsRead(session?.user?.id); }
                  if(t==='reports'){ setOpenReport(null); setReportSignedUrl(''); }
                  if(t==='messages'){ fetchDmMessages(session.user.id).then(()=>markDmRead(session.user.id, session.user.id).then(()=>setUnreadDmCount(0))); }
                }}
                style={{position:'relative'}}>
                {label}
                {t==='community' && unreadCount > 0 && (
                  <span style={{position:'absolute',top:4,right:4,width:7,height:7,borderRadius:'50%',background:'var(--red)',display:'block'}} />
                )}
                {t==='training' && unreadWorkouts > 0 && (
                  <span style={{position:'absolute',top:2,right:2,minWidth:16,height:16,borderRadius:8,background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontFamily:"'Poppins',sans-serif",color:'var(--on-red)',fontWeight:700,padding:'0 4px'}}>
                    {unreadWorkouts}
                  </span>
                )}
                {t==='qa' && unreadQa > 0 && (
                  <span style={{position:'absolute',top:2,right:2,minWidth:16,height:16,borderRadius:8,background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontFamily:"'Poppins',sans-serif",color:'var(--on-red)',fontWeight:700,padding:'0 4px'}}>
                    {unreadQa}
                  </span>
                )}
                {t==='messages' && unreadDmCount > 0 && (
                  <span style={{position:'absolute',top:2,right:2,minWidth:16,height:16,borderRadius:8,background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontFamily:"'Poppins',sans-serif",color:'var(--on-red)',fontWeight:700,padding:'0 4px'}}>
                    {unreadDmCount}
                  </span>
                )}
              </button>
            ))}
          </div>


          {/* ── CONTENT ── */}
          <div className="sec">

            {/* DASHBOARD */}
            {tab === "dashboard" && (<>
              <div className="s-eye">Overview</div>
              <div className="s-title">My Dashboard</div>
              <div className="s-sub">Your training at a glance.</div>

              {/* PWA INSTALL BANNER */}
              {!appInstalled && deferredInstallPrompt && (
                <div style={{background:'linear-gradient(135deg,var(--red),var(--red-dark))',borderRadius:12,padding:'16px 20px',marginBottom:20,display:'flex',alignItems:'center',gap:14}}>
                  <div style={{fontSize:28,lineHeight:1}}>📲</div>
                  <div style={{flex:1}}>
                    <div style={{color:'#fff',fontWeight:700,fontSize:15,marginBottom:2}}>Add Evolution Endurance to your Home Screen</div>
                    <div style={{color:'rgba(255,255,255,0.75)',fontSize:12}}>Install the app for quick access, offline support &amp; a native feel.</div>
                  </div>
                  <button onClick={handlePwaInstall}
                    style={{background:'#fff',color:'var(--red)',border:'none',borderRadius:8,padding:'9px 16px',fontWeight:700,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
                    Install
                  </button>
                </div>
              )}
              {!appInstalled && showIosInstall && (
                <div style={{background:'linear-gradient(135deg,var(--red),var(--red-dark))',borderRadius:12,padding:'16px 20px',marginBottom:20}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
                    <div style={{fontSize:28,lineHeight:1}}>📲</div>
                    <div>
                      <div style={{color:'#fff',fontWeight:700,fontSize:15}}>Add Evolution Endurance to your Home Screen</div>
                      <div style={{color:'rgba(255,255,255,0.75)',fontSize:12}}>Install for quick access &amp; a native feel.</div>
                    </div>
                  </div>
                  <div style={{background:'rgba(0,0,0,0.25)',borderRadius:8,padding:'10px 14px'}}>
                    <div style={{color:'rgba(255,255,255,0.9)',fontSize:12,lineHeight:1.6}}>
                      1. Tap the <strong style={{color:'#fff'}}>Share</strong> button <span style={{fontSize:14}}>⎋</span> at the bottom of Safari<br/>
                      2. Scroll down and tap <strong style={{color:'#fff'}}>"Add to Home Screen"</strong><br/>
                      3. Tap <strong style={{color:'#fff'}}>Add</strong> — done!
                    </div>
                  </div>
                  <button onClick={()=>setShowIosInstall(false)}
                    style={{marginTop:10,background:'transparent',color:'rgba(255,255,255,0.6)',border:'1px solid rgba(255,255,255,0.3)',borderRadius:6,padding:'5px 12px',fontSize:11,cursor:'pointer'}}>
                    Dismiss
                  </button>
                </div>
              )}

              {/* DAILY REMINDER */}
              <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:'14px 18px',marginBottom:24,display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                <div style={{fontSize:22,lineHeight:1}}>🔔</div>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{color:'var(--cream)',fontWeight:700,fontSize:13}}>Daily reminder</div>
                  <div style={{color:'var(--muted)',fontSize:11}}>A quick nudge to move, eat well, or stay motivated.</div>
                </div>
                {pushPermission !== 'granted' ? (
                  pushPermission === 'unsupported'
                    ? <div style={{color:'var(--muted)',fontSize:11}}>Not supported on this browser.</div>
                    : <button onClick={enablePushNotifications} disabled={pushBusy} className="btn btn-red" style={{fontSize:11,padding:'7px 16px'}}>{pushBusy?'…':'Enable Notifications'}</button>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',color:'var(--muted)',fontSize:12}}>
                      <input type="checkbox" checked={reminderEnabled} disabled={savingReminder} onChange={e=>saveReminderPrefs(e.target.checked, reminderHour)} style={{accentColor:'var(--red)',width:15,height:15}} />
                      On
                    </label>
                    <select value={reminderHour} disabled={!reminderEnabled||savingReminder} onChange={e=>saveReminderPrefs(reminderEnabled, parseInt(e.target.value,10))}
                      style={{background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'6px 10px',fontSize:12}}>
                      {Array.from({length:24},(_,h)=>(
                        <option key={h} value={h}>{(h%12||12)+':00 '+(h<12?'AM':'PM')}</option>
                      ))}
                    </select>
                    <span style={{color:'var(--muted)',fontSize:10}}>Eastern</span>
                  </div>
                )}
              </div>

              {/* TESTING PROGRESS */}
              {myReports.length > 0 && (
                <div style={{marginBottom:28}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:10}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>Testing Progress</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {REPORT_METRICS.map(m => (
                        <button key={m.key} onClick={()=>setReportMetric(m.key)}
                          style={{padding:'5px 12px',borderRadius:16,cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1,
                            background: reportMetric===m.key ? 'var(--red)' : 'var(--card)',
                            border:`1px solid ${reportMetric===m.key ? 'var(--red)' : 'var(--b2)'}`,
                            color: reportMetric===m.key ? 'var(--cream)' : 'var(--muted)'}}>
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const metric = REPORT_METRICS.find(m=>m.key===reportMetric);
                    const points = myReports
                      .filter(r => r[reportMetric] != null)
                      .slice().sort((a,b)=> new Date(a.test_date) - new Date(b.test_date));
                    if (points.length === 0) return (
                      <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:'32px 20px',textAlign:'center',color:'var(--muted)',fontSize:13}}>
                        No {metric.label} data recorded yet.
                      </div>
                    );
                    if (points.length === 1) {
                      const p = points[0];
                      return (
                        <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:'24px 20px',display:'flex',alignItems:'baseline',gap:10}}>
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:32,fontWeight:700,color:'var(--red)'}}>{p[reportMetric]}</div>
                          <div style={{color:'var(--muted)',fontSize:12}}>{metric.unit} · {new Date(p.test_date+'T12:00:00').toLocaleDateString('en-CA',{year:'numeric',month:'short',day:'numeric'})}</div>
                          <div style={{color:'var(--muted)',fontSize:11,marginLeft:'auto'}}>One test so far — the trend line appears after your next.</div>
                        </div>
                      );
                    }
                    // cho_fat_ratio_rest is free text (e.g. "60:40"), not a plain
                    // number -- pull the first number out of it to plot on the
                    // y-axis, but keep showing the original text as the label.
                    const numAt = p => {
                      const raw = p[reportMetric];
                      if (typeof raw === 'number') return raw;
                      const m = String(raw).match(/-?\d+(\.\d+)?/);
                      return m ? parseFloat(m[0]) : null;
                    };
                    const plottable = points.filter(p => numAt(p) != null);
                    if (plottable.length < 2) {
                      return (
                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                          {points.map(p => (
                            <div key={p.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'baseline',gap:10}}>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:20,fontWeight:700,color:'var(--red)'}}>{p[reportMetric]}</div>
                              <div style={{color:'var(--muted)',fontSize:12}}>{metric.unit} · {new Date(p.test_date+'T12:00:00').toLocaleDateString('en-CA',{year:'numeric',month:'short',day:'numeric'})}</div>
                            </div>
                          ))}
                          {points.length < 2 && <div style={{color:'var(--muted)',fontSize:11}}>One test so far — the trend line appears after your next.</div>}
                        </div>
                      );
                    }
                    const W = 600, H = 220, padL = 46, padR = 20, padT = 20, padB = 34;
                    const vals = plottable.map(numAt);
                    let vMin = Math.min(...vals), vMax = Math.max(...vals);
                    if (vMin === vMax) { vMin -= 1; vMax += 1; }
                    const vPad = (vMax - vMin) * 0.15;
                    vMin -= vPad; vMax += vPad;
                    const xAt = i => padL + (i/(plottable.length-1)) * (W-padL-padR);
                    const yAt = v => padT + (1 - (v-vMin)/(vMax-vMin)) * (H-padT-padB);
                    const pathD = plottable.map((p,i)=>`${i===0?'M':'L'}${xAt(i)},${yAt(numAt(p))}`).join(' ');
                    return (
                      <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:'16px 12px 8px'}}>
                        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:'auto',display:'block'}}>
                          <line x1={padL} y1={padT} x2={padL} y2={H-padB} stroke="var(--b1)" strokeWidth="1" />
                          <line x1={padL} y1={H-padB} x2={W-padR} y2={H-padB} stroke="var(--b1)" strokeWidth="1" />
                          <text x={padL-8} y={yAt(vMax)+4} textAnchor="end" fontSize="10" fill="var(--muted)">{vMax.toFixed(1)}</text>
                          <text x={padL-8} y={yAt(vMin)+4} textAnchor="end" fontSize="10" fill="var(--muted)">{vMin.toFixed(1)}</text>
                          <path d={pathD} fill="none" stroke="var(--red)" strokeWidth="2.5" />
                          {plottable.map((p,i) => (
                            <g key={p.id}>
                              <circle cx={xAt(i)} cy={yAt(numAt(p))} r="4" fill="var(--red)" />
                              <text x={xAt(i)} y={yAt(numAt(p))-10} textAnchor="middle" fontSize="10" fill="var(--cream)" fontWeight="700">{p[reportMetric]}</text>
                              <text x={xAt(i)} y={H-padB+16} textAnchor="middle" fontSize="9" fill="var(--muted)">
                                {new Date(p.test_date+'T12:00:00').toLocaleDateString('en-CA',{month:'short',day:'numeric'})}
                              </text>
                            </g>
                          ))}
                        </svg>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* RUN TRACKING */}
              {runsLoading ? (
                <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:20}}>Loading...</div>
              ) : (()=>{
                const now = new Date();
                const dow = now.getDay();
                const startOfWeek = new Date(now);
                startOfWeek.setDate(now.getDate() - (dow===0 ? 6 : dow-1));
                startOfWeek.setHours(0,0,0,0);
                const weekRuns = runs.filter(r => new Date(r.date+'T12:00:00') >= startOfWeek);
                const weekKm = weekRuns.reduce((s,r) => s + parseFloat(r.distance_km), 0);
                const startOfLastWeek = new Date(startOfWeek); startOfLastWeek.setDate(startOfWeek.getDate()-7);
                const lastWeekRuns = runs.filter(r => { const d=new Date(r.date+'T12:00:00'); return d>=startOfLastWeek && d<startOfWeek; });
                const lastWeekKm = lastWeekRuns.reduce((s,r) => s + parseFloat(r.distance_km), 0);
                const weekChangePct = lastWeekKm > 0 ? ((weekKm - lastWeekKm) / lastWeekKm) * 100 : null;
                const over10 = weekChangePct !== null && weekChangePct > 10;
                const weekElevM = weekRuns.reduce((s,r) => s + (parseFloat(r.elevation_gain_m)||0), 0);
                const recentTen = runs.slice(0,10);
                const avgPaceSec = recentTen.length ? recentTen.reduce((s,r)=>s+r.duration_seconds,0) / recentTen.reduce((s,r)=>s+parseFloat(r.distance_km),0) : 0;
                const avgPaceStr = avgPaceSec ? Math.floor(avgPaceSec/60)+':'+String(Math.round(avgPaceSec%60)).padStart(2,'0')+' /km' : '—';
                return (<>
                  <div className="stat-row" style={{marginBottom:12}}>
                    <div className="stat-box"><div className="stat-val">{weekKm.toFixed(1)}</div><div className="stat-lbl">km this week</div></div>
                    <div className="stat-box"><div className="stat-val">{runs.length}</div><div className="stat-lbl">total runs</div></div>
                    <div className="stat-box"><div className="stat-val">{avgPaceStr}</div><div className="stat-lbl">avg pace</div></div>
                  </div>
                  <div className="stat-row" style={{marginBottom:20}}>
                    <div className="stat-box"><div className="stat-val">{weekElevM > 0 ? weekElevM.toFixed(0)+'m' : '—'}</div><div className="stat-lbl">↑ elev this week</div></div>
                    <div className="stat-box">
                      <div className="stat-val" style={{color: over10 ? '#b85f26' : weekChangePct !== null && weekChangePct < 0 ? '#2f7a4a' : 'var(--cream)'}}>
                        {weekChangePct !== null ? (weekChangePct > 0 ? '+' : '') + weekChangePct.toFixed(0) + '%' : '—'}
                      </div>
                      <div className="stat-lbl">vs last week</div>
                    </div>
                    <div className="stat-box"><div className="stat-val">{lastWeekKm > 0 ? lastWeekKm.toFixed(1) : '—'}</div><div className="stat-lbl">km last week</div></div>
                  </div>
                  {over10 && (
                    <div style={{background:'linear-gradient(135deg,#7a3a10,#5c2a0a)',border:'1px solid #a0521e',borderRadius:12,padding:'14px 18px',marginBottom:20,display:'flex',gap:12,alignItems:'flex-start'}}>
                      <div style={{fontSize:22,lineHeight:1,flexShrink:0}}>⚠️</div>
                      <div>
                        <div style={{color:'#f5c08a',fontWeight:700,fontSize:13,marginBottom:3}}>10% Rule Warning</div>
                        <div style={{color:'rgba(245,192,138,0.8)',fontSize:12,lineHeight:1.5}}>Your mileage is up <strong style={{color:'#f5c08a'}}>{weekChangePct.toFixed(0)}%</strong> from last week. The 10% rule recommends increasing no more than 10% per week to reduce injury risk. Consider an easier day.</div>
                      </div>
                    </div>
                  )}


                  {/* PRESCRIBED RUNS */}
                  {prescribedRuns.length > 0 && (
                    <div style={{marginBottom:24}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:12}}>Upcoming Prescribed Runs</div>
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {prescribedRuns.map(pr => (
                          <div key={pr.id} style={{background:'var(--card)',border:'1px solid var(--red)',borderRadius:10,padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                            <div>
                              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                                <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1,color:'var(--red)',textTransform:'uppercase'}}>{pr.run_type}</span>
                                <span style={{color:'var(--cream)',fontWeight:700,fontSize:15}}>{pr.distance_km} km</span>
                                <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>
                                  {new Date(pr.scheduled_date+'T12:00:00').toLocaleDateString('en-CA',{weekday:'short',month:'short',day:'numeric'})}
                                </span>
                              </div>
                              {pr.notes && <div style={{color:'var(--muted)',fontSize:12,fontStyle:'italic'}}>{pr.notes}</div>}
                            </div>
                            <button
                              className="btn btn-red"
                              style={{fontSize:12,padding:'8px 16px',whiteSpace:'nowrap'}}
                              onClick={() => {
                                setLogFromPrescribed(pr.id);
                                setLogRunForm(p => ({...p, date: pr.scheduled_date, distance: String(pr.distance_km), type: pr.run_type}));
                                setShowLogForm(true);
                                window.scrollTo({top: document.body.scrollHeight, behavior:'smooth'});
                              }}
                            >Log this run →</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!showLogForm ? (
                    <div style={{display:'flex',gap:10,marginBottom:24}}>
                      <button className="btn btn-red" style={{flex:1,padding:'12px 0',fontSize:14}} onClick={()=>setShowLogForm(true)}>+ Log a Run</button>
                      <label style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'12px 0',fontSize:14,background:'var(--card)',border:'1px solid var(--b2)',borderRadius:8,color:'var(--cream)',cursor:'pointer',fontWeight:600}}>
                        <input type="file" accept=".gpx,.tcx" style={{display:'none'}} onChange={handleActivityUpload} />
                        ↑ Upload GPX / TCX
                      </label>
                    </div>
                  ) : (
                    <form onSubmit={logRun} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:16}}>Log a Run</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DATE</div>
                          <input type="date" value={logRunForm.date} onChange={e=>setLogRunForm(p=>({...p,date:e.target.value}))}
                            style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DISTANCE (KM)</div>
                          <input type="number" step="0.01" min="0" placeholder="e.g. 10.5" value={logRunForm.distance} onChange={e=>setLogRunForm(p=>({...p,distance:e.target.value}))} required
                            style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                      </div>
                      <div style={{marginBottom:12}}>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION</div>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                          <div>
                            <input type="number" min="0" max="23" placeholder="0" value={logRunForm.hours} onChange={e=>setLogRunForm(p=>({...p,hours:e.target.value}))}
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box',textAlign:'center'}} />
                            <div style={{fontSize:10,color:'var(--muted)',textAlign:'center',marginTop:4,fontFamily:"'Poppins',sans-serif"}}>HRS</div>
                          </div>
                          <div>
                            <input type="number" min="0" max="59" placeholder="0" value={logRunForm.minutes} onChange={e=>setLogRunForm(p=>({...p,minutes:e.target.value}))} required
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box',textAlign:'center'}} />
                            <div style={{fontSize:10,color:'var(--muted)',textAlign:'center',marginTop:4,fontFamily:"'Poppins',sans-serif"}}>MIN</div>
                          </div>
                          <div>
                            <input type="number" min="0" max="59" placeholder="0" value={logRunForm.seconds} onChange={e=>setLogRunForm(p=>({...p,seconds:e.target.value}))}
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box',textAlign:'center'}} />
                            <div style={{fontSize:10,color:'var(--muted)',textAlign:'center',marginTop:4,fontFamily:"'Poppins',sans-serif"}}>SEC</div>
                          </div>
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                          <select value={logRunForm.type} onChange={e=>setLogRunForm(p=>({...p,type:e.target.value}))}
                            style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                            {['Easy','Tempo','Long Run','Intervals','Race','Recovery','Trail'].map(t=><option key={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>ELEV GAIN (m) <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>optional</span></div>
                          <input type="number" min="0" step="1" placeholder="e.g. 120" value={logRunForm.elevation} onChange={e=>setLogRunForm(p=>({...p,elevation:e.target.value}))}
                            style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                      </div>
                      <div style={{display:'flex',gap:10}}>
                        <button type="submit" className="btn btn-red" disabled={submittingRun} style={{flex:1,padding:'10px 0'}}>
                          {submittingRun ? 'Saving...' : 'Log Run'}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={()=>setShowLogForm(false)} style={{padding:'10px 20px'}}>Cancel</button>
                      </div>
                    </form>
                  )}

                  {/* ── RUN CALENDAR ── */}
                  {(()=>{
                    const year = dashCalMonth.getFullYear();
                    const month = dashCalMonth.getMonth();
                    const runsByDate = {};
                    runs.forEach(r => { if (!runsByDate[r.date]) runsByDate[r.date]=[]; runsByDate[r.date].push(r); });
                    const wktsByDate = {};
                    completedWorkouts.forEach(w => { if (!wktsByDate[w.scheduled_date]) wktsByDate[w.scheduled_date]=[]; wktsByDate[w.scheduled_date].push(w); });
                    const firstDay = new Date(year, month, 1);
                    const lastDay  = new Date(year, month+1, 0);
                    const startDow = firstDay.getDay();
                    const daysFromMon = startDow===0 ? 6 : startDow-1;
                    const calStart = new Date(firstDay); calStart.setDate(1-daysFromMon);
                    const weeks = [];
                    const cur = new Date(calStart);
                    while (cur<=lastDay || weeks.length<4) {
                      const week=[]; for(let i=0;i<7;i++){week.push(new Date(cur));cur.setDate(cur.getDate()+1);} weeks.push(week);
                      if(cur>lastDay && weeks.length>=4) break;
                    }
                    const todayStr = new Date().toISOString().slice(0,10);
                    const monthLabel = firstDay.toLocaleDateString('en-CA',{month:'long',year:'numeric'});
                    return (
                      <div>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>{monthLabel}</div>
                          <div style={{display:'flex',gap:6}}>
                            <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{const d=new Date(dashCalMonth);d.setMonth(d.getMonth()-1);setDashCalMonth(d);setSelectedCalDay(null);}}>←</button>
                            <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{const d=new Date(dashCalMonth);d.setMonth(d.getMonth()+1);setDashCalMonth(d);setSelectedCalDay(null);}}>→</button>
                          </div>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr) 52px',gap:2,marginBottom:4}}>
                          {['MON','TUE','WED','THU','FRI','SAT','SUN'].map(d=>(
                            <div key={d} style={{fontFamily:"'Poppins',sans-serif",fontSize:8,letterSpacing:1,color:'var(--muted)',textAlign:'center',padding:'3px 0'}}>{d}</div>
                          ))}
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:8,letterSpacing:1,color:'var(--muted)',textAlign:'center',padding:'3px 0'}}>WEEK</div>
                        </div>
                        {weeks.map((week,wi)=>{
                          const weekKm = week.reduce((s,d)=>{
                            const ds=d.toISOString().slice(0,10);
                            return s+(runsByDate[ds]||[]).reduce((a,r)=>a+parseFloat(r.distance_km),0);
                          },0);
                          return (
                            <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr) 52px',gap:2,marginBottom:2}}>
                              {week.map((day,di)=>{
                                const ds=day.toISOString().slice(0,10);
                                const dayRuns=runsByDate[ds]||[];
                                const dayWkts=wktsByDate[ds]||[];
                                const dayKm=dayRuns.reduce((s,r)=>s+parseFloat(r.distance_km),0);
                                const hasActivity=dayRuns.length>0||dayWkts.length>0;
                                const inMonth=day.getMonth()===month;
                                const isToday=ds===todayStr;
                                const isSel=selectedCalDay===ds;
                                return (
                                  <div key={di} onClick={()=>hasActivity?setSelectedCalDay(isSel?null:ds):null}
                                    style={{background:isSel?'rgba(146,41,32,0.2)':isToday?'rgba(146,41,32,0.08)':'var(--card)',border:`1px solid ${isSel?'var(--red)':isToday?'rgba(146,41,32,0.5)':'var(--b2)'}`,borderRadius:6,padding:'5px 3px',textAlign:'center',minHeight:48,cursor:hasActivity?'pointer':'default',opacity:inMonth?1:0.3,transition:'border-color 0.15s'}}>
                                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:isToday?'var(--red)':'var(--muted)',fontWeight:isToday?700:400}}>{day.getDate()}</div>
                                    <div style={{display:'flex',justifyContent:'center',gap:3,margin:'3px 0 2px'}}>
                                      {dayRuns.length>0 && <div style={{width:5,height:5,borderRadius:'50%',background:'var(--red)',flexShrink:0}}/>}
                                      {dayWkts.length>0 && <div style={{width:5,height:5,borderRadius:'50%',background:'#2f7a4a',flexShrink:0}}/>}
                                    </div>
                                    {dayRuns.length>0 && <div style={{fontSize:9,color:'var(--cream)',fontWeight:700,fontFamily:"'Poppins',sans-serif"}}>{dayKm.toFixed(1)}</div>}
                                    {dayWkts.length>0 && dayRuns.length===0 && <div style={{fontSize:8,color:'#2f7a4a',fontFamily:"'Poppins',sans-serif"}}>💪</div>}
                                  </div>
                                );
                              })}
                              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'var(--bg2)',borderRadius:6,border:'1px solid var(--b1)'}}>
                                {weekKm>0 ? (<>
                                  <div style={{fontSize:10,color:'var(--cream)',fontWeight:700,fontFamily:"'Poppins',sans-serif"}}>{weekKm.toFixed(1)}</div>
                                  <div style={{fontSize:7,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>KM</div>
                                </>) : <div style={{fontSize:10,color:'var(--b2)',fontFamily:"'Poppins',sans-serif"}}>—</div>}
                              </div>
                            </div>
                          );
                        })}
                        {runs.length===0 && (
                          <div style={{textAlign:'center',padding:'30px 0',color:'var(--muted)',fontSize:13}}>No runs logged yet — hit that button and get started!</div>
                        )}
                        {selectedCalDay && (runsByDate[selectedCalDay]||wktsByDate[selectedCalDay]) && (
                          <div style={{marginTop:14}}>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',marginBottom:10}}>
                              {new Date(selectedCalDay+'T12:00:00').toLocaleDateString('en-CA',{weekday:'long',month:'long',day:'numeric'}).toUpperCase()}
                            </div>
                            <div style={{display:'flex',flexDirection:'column',gap:10}}>
                              {(wktsByDate[selectedCalDay]||[]).map(w=>(
                                <div key={w.id} style={{background:'var(--card)',border:'1px solid rgba(90,158,111,0.4)',borderRadius:12,padding:16,display:'flex',alignItems:'center',gap:12}}>
                                  <div style={{fontSize:22,flexShrink:0}}>💪</div>
                                  <div>
                                    <div style={{color:'var(--cream)',fontSize:14,fontWeight:700,marginBottom:3}}>{w.workouts?.title || 'Strength Workout'}</div>
                                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1,color:'#2f7a4a'}}>COMPLETED</div>
                                  </div>
                                </div>
                              ))}
                              {runsByDate[selectedCalDay].map(run=>(
                                <div key={run.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:16}}>
                                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                                    <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'3px 8px',borderRadius:4,textTransform:'uppercase'}}>{run.type}</span>
                                  </div>
                                  <div style={{display:'flex',gap:20,flexWrap:'wrap',marginBottom:10}}>
                                    <div><div style={{color:'var(--cream)',fontSize:17,fontWeight:700}}>{parseFloat(run.distance_km).toFixed(2)}</div><div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>KM</div></div>
                                    <div><div style={{color:'var(--cream)',fontSize:17,fontWeight:700}}>{paceFromSecs(run.duration_seconds,parseFloat(run.distance_km))}</div><div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>PACE</div></div>
                                    <div><div style={{color:'var(--cream)',fontSize:17,fontWeight:700}}>{formatDuration(run.duration_seconds)}</div><div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>TIME</div></div>
                                    {run.elevation_gain_m>0 && <div><div style={{color:'var(--cream)',fontSize:17,fontWeight:700}}>{Math.round(run.elevation_gain_m)}m</div><div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>↑ ELEV</div></div>}
                                  </div>
                                  {editingNotes===run.id ? (
                                    <div>
                                      <textarea value={notesText} onChange={e=>setNotesText(e.target.value)} placeholder="How did it feel?" rows={2}
                                        style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,resize:'none',fontFamily:'inherit',boxSizing:'border-box',marginBottom:8}} />
                                      <div style={{display:'flex',gap:8}}>
                                        <button className="btn btn-red" style={{fontSize:12,padding:'6px 16px'}} onClick={()=>saveRunNotes(run.id)}>Save</button>
                                        <button className="btn btn-ghost" style={{fontSize:12,padding:'6px 16px'}} onClick={()=>setEditingNotes(null)}>Cancel</button>
                                      </div>
                                    </div>
                                  ) : run.notes ? (
                                    <div style={{borderTop:'1px solid var(--b1)',paddingTop:10}}>
                                      <div style={{color:'var(--muted)',fontSize:12,lineHeight:1.6,marginBottom:6}}>{run.notes}</div>
                                      <button style={{background:'none',border:'none',color:'var(--red)',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',padding:0}} onClick={()=>{setEditingNotes(run.id);setNotesText(run.notes);}}>EDIT NOTES</button>
                                    </div>
                                  ) : (
                                    <button style={{background:'none',border:'none',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',padding:0}} onClick={()=>{setEditingNotes(run.id);setNotesText('');}}>+ ADD NOTES</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>);
              })()}


            </>)}
            {/* MEMBERSHIP */}
            {tab === "membership" && (<>
              <div className="s-eye">Plans</div>
              <div className="s-title">Choose Your Tier</div>
              <div className="s-sub">Science-backed coaching at every level. Change anytime.</div>
              <div className="tier-grid">
                {Object.entries(TIERS).map(([key, tier]) => (
                  <div
                    key={key}
                    className={`tier-card ${userTier===key?"sel":""}`}
                    onClick={() => key === 'stride' ? setUserTier('stride') : handleUpgrade(key)}
                  >
                    {tier.popular && <div className="pop-tag">Most popular</div>}
                    <div className="tc-name">{tier.name}</div>
                    <div className="tc-price">{tier.price}</div>
                    <div className="tc-note">{tier.priceNote}</div>
                    <ul className="feat-list">
                      {tier.features.map(f => <li key={f} className="feat y"><span className="chk">✓</span>{f}</li>)}
                      {tier.locked.map(f  => <li key={f} className="feat n"><span className="lck">✕</span>{f}</li>)}
                    </ul>
                    <button
                      className="btn"
                      style={{
                        width:"100%",
                        background: userTier===key ? "#2a2320" : "var(--red)",
                        color: "var(--cream)",
                        border: userTier===key ? "1px solid var(--b1)" : "none",
                      }}
                      onClick={e => { e.stopPropagation(); key === 'stride' ? setUserTier('stride') : handleUpgrade(key); }}
                    >
                      {userTier===key ? "Current Plan" : `Select ${tier.name}`}
                    </button>
                  </div>
                ))}
              </div>
            </>)}

            {/* TRAINING */}
            {tab === "training" && (<>
              {selectedWorkout ? (<>
                <button className="btn btn-ghost" style={{marginBottom:20,fontSize:13}} onClick={()=>{setSelectedWorkout(null);setWorkoutExercises([]);setLastLogs({});setLogDraft({});setWorkoutStarted(false);setWorkoutComplete(false);}}>← Back to Workouts</button>
                <div className="s-eye">{selectedWorkout.tier}</div>
                <div className="s-title">{selectedWorkout.title}</div>
                {selectedWorkout.description && <div className="s-sub">{selectedWorkout.description}</div>}

                {/* WORKOUT COMPLETE SCREEN */}
                {workoutComplete && (
                  <div style={{textAlign:'center',padding:'40px 20px'}}>
                    <div style={{fontSize:48,marginBottom:16}}>💪</div>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:12,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:8}}>Workout Complete</div>
                    <div style={{color:'var(--cream)',fontSize:22,fontWeight:700,marginBottom:4}}>{selectedWorkout.title}</div>
                    {workoutStartTime && <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,color:'var(--muted)',marginBottom:24}}>Duration: {Math.round((Date.now()-workoutStartTime)/60000)} min</div>}
                    <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:32,textAlign:'left'}}>
                      {workoutExercises.map(ex=>(
                        <div key={ex.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--card)',border:'1px solid var(--b2)',borderRadius:8,padding:'12px 16px'}}>
                          <span style={{color:'var(--cream)',fontSize:13}}>{ex.name}</span>
                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:11,color:(sessionSets[ex.id]||0)>=(ex.sets||0)&&ex.sets?'var(--red)':'var(--muted)'}}>
                            {sessionSets[ex.id]||0} of {ex.sets||'?'} sets {(sessionSets[ex.id]||0)>=(ex.sets||0)&&ex.sets?'✓':''}
                          </span>
                        </div>
                      ))}
                    </div>
                    <button className="btn btn-red" style={{width:'100%',padding:'14px 0',fontSize:15}} onClick={()=>{setSelectedWorkout(null);setWorkoutExercises([]);setLastLogs({});setLogDraft({});setWorkoutStarted(false);setWorkoutComplete(false);}}>Back to Workouts</button>
                  </div>
                )}

                {/* PRE-START PREVIEW */}
                {!workoutStarted && !workoutComplete && (<>
                  <div style={{marginTop:20,display:'flex',flexDirection:'column',gap:10,marginBottom:28}}>
                    {workoutExercises.length===0 && <div style={{color:'var(--muted)',fontSize:13}}>No exercises added yet.</div>}
                    {workoutExercises.map(ex=>{
                      const last = lastLogs[ex.id];
                      return (
                        <div key={ex.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                          <div>
                            <div style={{color:'var(--cream)',fontSize:13,fontWeight:600,marginBottom:2}}>{ex.name}</div>
                            {(ex.sets||ex.prescribed_reps) && <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>{ex.sets&&`${ex.sets} sets`}{ex.prescribed_reps&&` × ${ex.prescribed_reps}`}</div>}
                          </div>
                          {last ? (
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1,textAlign:'right'}}>
                              LAST: {[last.reps&&`${last.reps} reps`,last.weight&&`${last.weight}kg`,last.duration&&last.duration].filter(Boolean).join(' · ')}
                            </div>
                          ) : <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>No previous data</div>}
                        </div>
                      );
                    })}
                  </div>
                  {workoutExercises.length > 0 && (
                    <button className="btn btn-red" style={{width:'100%',padding:'16px 0',fontSize:16,letterSpacing:1}} onClick={()=>{setWorkoutStarted(true);setWorkoutStartTime(Date.now());}}>
                      Start Workout
                    </button>
                  )}
                </>)}
                {/* ACTIVE WORKOUT */}
                {workoutStarted && !workoutComplete && <div style={{marginTop:24,display:'flex',flexDirection:'column',gap:24}}>
                  {workoutExercises.length===0 && <div style={{color:'var(--muted)',fontSize:13}}>No exercises added yet.</div>}
                  {(()=>{
                    const sorted = [...workoutExercises].sort((a,b)=>{
                      if (a.superset_group && b.superset_group && a.superset_group===b.superset_group) return a.sort_order-b.sort_order;
                      if (a.superset_group && b.superset_group) return a.superset_group.localeCompare(b.superset_group);
                      if (a.superset_group) return -1; if (b.superset_group) return 1;
                      return a.sort_order-b.sort_order;
                    });
                    const groupIdx = {}; const renderedGroups = new Set();
                    return sorted.map(ex => {
                      let label = '';
                      if (ex.superset_group) { groupIdx[ex.superset_group]=(groupIdx[ex.superset_group]||0)+1; label=ex.superset_group+groupIdx[ex.superset_group]; }
                      const isFirstInGroup = ex.superset_group && !renderedGroups.has(ex.superset_group);
                      if (ex.superset_group) renderedGroups.add(ex.superset_group);
                      const embed = ytEmbed(ex.video_url);
                      const last  = lastLogs[ex.id];
                      const draft = logDraft[ex.id] || {};
                      return (
                        <div key={ex.id}>
                          {isFirstInGroup && (
                            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>Superset {ex.superset_group}</div>
                              <div style={{flex:1,height:1,background:'var(--red)',opacity:0.3}} />
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>Perform back-to-back</div>
                            </div>
                          )}
                          <div style={{background:'var(--bg2)',border:`1px solid ${ex.superset_group?'var(--red)':'var(--b2)'}`,borderRadius:10,padding:24,opacity:ex.superset_group?1:1}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                              {label && <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:11,fontFamily:"'Poppins',sans-serif",padding:'3px 9px',borderRadius:4,fontWeight:700,flexShrink:0}}>{label}</span>}
                              {ex.exercise_type==='warmup'&&<span style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'3px 7px',borderRadius:3,background:'rgba(90,158,111,0.15)',color:'#2f7a4a',textTransform:'uppercase',flexShrink:0}}>Warm-up</span>}
                              {ex.exercise_type==='cooldown'&&<span style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'3px 7px',borderRadius:3,background:'rgba(90,120,200,0.15)',color:'#3f6bb0',textTransform:'uppercase',flexShrink:0}}>Cool-down</span>}
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:13,letterSpacing:1,color:'var(--cream)',textTransform:'uppercase'}}>{ex.name}</div>
                            </div>
                            {embed
                              ? <div style={{position:'relative',paddingBottom:'56.25%',height:0,marginBottom:16,borderRadius:8,overflow:'hidden'}}><iframe src={embed} title={ex.name} style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none'}} allowFullScreen /></div>
                              : isVideo(ex.video_url) && <video src={ex.video_url} controls style={{width:'100%',borderRadius:8,marginBottom:16,maxHeight:320}} />}
                            {last && <div style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",marginBottom:12,letterSpacing:1}}>LAST: {[last.reps&&`${last.reps} reps`,last.weight&&`${last.weight}kg`,last.duration&&last.duration,last.rest&&`rest ${last.rest}`].filter(Boolean).join(' · ')}</div>}
                            {(ex.sets||ex.prescribed_reps||ex.prescribed_rpe!=null) && <div style={{background:'var(--black)',border:'1px solid var(--red)',borderRadius:8,padding:'10px 14px',marginBottom:12,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}><span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>Prescribed</span>{ex.sets && <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{ex.sets} sets</span>}{ex.prescribed_reps && <span style={{color:'var(--cream)',fontSize:13}}>× {ex.prescribed_reps}</span>}{ex.prescribed_rpe!=null && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:12,color:'var(--cream)',background:'rgba(146,41,32,0.2)',padding:'2px 8px',borderRadius:4}}>RPE {ex.prescribed_rpe}</span>}{ex.prescribed_notes && <span style={{color:'var(--muted)',fontSize:12,fontStyle:'italic',width:'100%'}}>{ex.prescribed_notes}</span>}</div>}
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginBottom:12}}>
                              {[['reps','Reps'],['weight','Weight (kg)'],['duration','Time'],['rest','Rest']].map(([field,lbl])=>(
                                <div key={field}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>{lbl}</div>
                                  <input type="text" value={draft[field]||''} onChange={e=>setLogDraft(p=>({...p,[ex.id]:{...draft,[field]:e.target.value}}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                </div>
                              ))}
                            </div>
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES</div>
                              <input type="text" value={draft.notes||''} onChange={e=>setLogDraft(p=>({...p,[ex.id]:{...draft,notes:e.target.value}}))} placeholder="Optional notes..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                              <button className="btn btn-red" style={{fontSize:12,padding:'8px 20px'}} disabled={savingLog[ex.id]} onClick={()=>saveLog(ex.id)}>{savingLog[ex.id]?'Saving…':'Log Set'}</button>
                              {(sessionSets[ex.id]||0) > 0 && (
                                <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,color: sessionSets[ex.id]>=(ex.sets||0) ? 'var(--red)' : 'var(--muted)'}}>
                                  {sessionSets[ex.id]} of {ex.sets||'?'} sets{sessionSets[ex.id]>=(ex.sets||0) && ex.sets ? ' ✓' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  <button className="btn" style={{width:'100%',padding:'16px 0',fontSize:15,marginTop:12,background:'var(--card)',border:'1px solid var(--b2)',color:'var(--cream)'}} onClick={()=>{ setWorkoutComplete(true); if(selectedWorkout){ autoCompleteScheduledWorkout(selectedWorkout.id); autoPostWorkout('strength',{title:selectedWorkout.title}); if(session?.user) fetchCompletedWorkouts(session.user.id); } }}>
                    Finish Workout
                  </button>
                </div>}
              </>) : (<>
                <div className="s-eye">Training</div>
                <div className="s-title">{userTier==='apex'?'Your Program':'Workouts'}</div>
                <div className="s-sub">Tap a workout to view exercises and log your sets.</div>

                {/* Sub-tabs */}
                <div style={{display:'flex',gap:8,marginBottom:24,flexWrap:'wrap'}}>
                  {TRAINING_CATEGORIES.map(({key:k,label:l,emoji}) => (
                    <button key={k} onClick={()=>setTrainingSubTab(k)}
                      style={{
                        padding:'8px 18px',borderRadius:20,cursor:'pointer',
                        fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,
                        background: trainingSubTab===k ? 'var(--red)' : 'var(--card)',
                        border:`1px solid ${trainingSubTab===k ? 'var(--red)' : 'var(--b2)'}`,
                        color: trainingSubTab===k ? 'var(--cream)' : 'var(--muted)',
                        transition:'all 0.2s',
                      }}
                    >{emoji} {l.toUpperCase()}</button>
                  ))}
                </div>

                {trainingSubTab === 'strength' && (<>
                {/* ── MEMBER CALENDAR ── */}
                {(()=>{
                  const canSeeCalendar = userTier === 'elevate' || userTier === 'apex';
                  const days = getWeekDays(memberCalStart);
                  const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                  const today = new Date().toISOString().slice(0,10);
                  const calendarUI = (
                    <div style={{marginBottom:28}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>Your Week</div>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{ const d=new Date(memberCalStart); d.setDate(d.getDate()-7); setMemberCalStart(d); if(session?.user) fetchMemberScheduledWorkouts(session.user.id); }}>←</button>
                          <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{ const d=new Date(memberCalStart); d.setDate(d.getDate()+7); setMemberCalStart(d); if(session?.user) fetchMemberScheduledWorkouts(session.user.id); }}>→</button>
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
                        {days.map((day,di) => {
                          const ds = toDateStr(day);
                          const isToday = ds === today;
                          const sw = memberScheduled.find(s => s.scheduled_date === ds);
                          return (
                            <div key={ds} style={{background:isToday?'rgba(146,41,32,0.12)':'var(--card)',border:`1px solid ${isToday?'var(--red)':sw?'rgba(146,41,32,0.4)':'var(--b2)'}`,borderRadius:8,padding:'8px 4px',textAlign:'center',minHeight:80,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:8,letterSpacing:1,color:'var(--muted)',textTransform:'uppercase'}}>{DAY_LABELS[di]}</div>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:13,fontWeight:700,color:isToday?'var(--red)':'var(--cream)'}}>{day.getDate()}</div>
                              {sw && (
                                <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3,width:'100%',padding:'0 2px'}}>
                                  <div style={{fontSize:9,color:'var(--cream)',fontWeight:600,textAlign:'center',lineHeight:1.3,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{sw.workouts?.title}</div>
                                  {sw.completed ? (
                                    <span style={{fontSize:8,color:'#2f7a4a',fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>✓ DONE</span>
                                  ) : (
                                    <button onClick={()=>{ const wkt=workouts.find(w=>w.id===sw.workout_id); if(wkt) openWorkout(wkt); }} style={{background:'var(--red)',border:'none',color:'#fff',borderRadius:4,fontSize:8,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'3px 5px',cursor:'pointer',marginTop:'auto'}}>OPEN</button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                  if (canSeeCalendar) return calendarUI;
                  return (
                    <div style={{marginBottom:28,position:'relative'}}>
                      <div style={{filter:'blur(4px)',pointerEvents:'none',userSelect:'none'}}>{calendarUI}</div>
                      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(20,16,12,0.75)',borderRadius:10}}>
                        <div style={{fontSize:20,marginBottom:8}}>🔒</div>
                        <div style={{color:'var(--on-red)',fontWeight:700,fontSize:14,marginBottom:4}}>Training Calendar</div>
                        <div style={{color:'rgba(247,241,232,0.75)',fontSize:12,textAlign:'center',maxWidth:200}}>Upgrade to Elevate or Apex to see your scheduled workouts.</div>
                      </div>
                    </div>
                  );
                })()}
                {/* Privately assigned workouts — always shown, regardless of tier browsing */}
                {myAssignments.length > 0 && (() => {
                  const assigned = workouts.filter(w => myAssignments.includes(w.id));
                  if (!assigned.length) return null;
                  return (<div style={{marginBottom:28}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
                      Assigned to You <span style={{flex:1,height:1,background:'var(--b2)',display:'block'}} />
                    </div>
                    <div className="cgrid">{assigned.map(w=>(<div key={w.id} className="cc" style={{cursor:'pointer',borderColor:'var(--red)'}} onClick={()=>openWorkout(w)}><div className="cc-tag" style={{background:'var(--red)',color:'var(--on-red)'}}>Assigned</div><div className="cc-title">{w.title}</div>{w.description&&<div className="cc-desc">{w.description}</div>}</div>))}</div>
                  </div>);
                })()}

                {trainingViewTier === null ? (
                  /* ── TIER PICKER ── */
                  <div style={{marginBottom:20}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:14}}>Choose a plan to browse its programs</div>
                    <div className="tier-grid">
                      {Object.entries(TIERS).map(([key,t]) => {
                        const isMine = key === userTier;
                        const isHigher = !canAccess(userTier, key);
                        return (
                          <div key={key} className={`tier-card ${isMine?'sel':''}`} onClick={()=>setTrainingViewTier(key)}>
                            <div className="tc-name">{t.name}</div>
                            {isMine && <div className="pop-tag" style={{position:'static',display:'inline-block',marginTop:6,marginBottom:10}}>Your Plan</div>}
                            {isHigher && <div style={{color:'var(--muted)',fontSize:11,margin:'6px 0 10px'}}>🔒 Preview what's included</div>}
                            <div style={{color:'var(--red)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,marginTop:10}}>View Programs →</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (() => {
                  const locked = !canAccess(userTier, trainingViewTier);
                  const inTier = workouts.filter(w => w.tier === trainingViewTier && w.visibility !== 'private');
                  const filtered = inTier.filter(w =>
                    (trainingFilters.body_focus==='all' || w.body_focus===trainingFilters.body_focus) &&
                    (trainingFilters.equipment==='all' || w.equipment===trainingFilters.equipment) &&
                    (trainingFilters.training_goal==='all' || w.training_goal===trainingFilters.training_goal)
                  );
                  const weeks = [...new Set(filtered.filter(w=>w.week_number).map(w=>w.week_number))].sort((a,b)=>a-b);
                  const flat = filtered.filter(w=>!w.week_number);

                  const card = (w, tagText) => (
                    <div key={w.id} className={`cc ${locked?'lk':''}`} style={{cursor:locked?'default':'pointer'}} onClick={()=>!locked && openWorkout(w)}>
                      <div className="cc-tag">{tagText}</div>
                      <div className="cc-title">{w.title}</div>
                      {w.description && <div className="cc-desc">{w.description}</div>}
                      {locked && <div className="veil"><div className="veil-ico">🔒</div><div className="veil-lbl">Locked</div><div className="veil-tier">↑ {TIERS[trainingViewTier]?.name}</div></div>}
                    </div>
                  );

                  return (<>
                    <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
                      <button className="btn btn-ghost" style={{fontSize:12,padding:'7px 16px'}} onClick={()=>setTrainingViewTier(null)}>← All Plans</button>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>{TIERS[trainingViewTier]?.name} Programs</div>
                      {locked && <button className="btn btn-red" style={{fontSize:11,padding:'6px 14px',marginLeft:'auto'}} onClick={()=>setTab('membership')}>Upgrade</button>}
                    </div>

                    {/* Filters */}
                    <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:20}}>
                      {[['body_focus','Body',BODY_FOCUS_OPTIONS],['equipment','Equipment',EQUIPMENT_OPTIONS],['training_goal','Goal',TRAINING_GOAL_OPTIONS]].map(([key,label,opts])=>(
                        <div key={key} style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--muted)',width:70,flexShrink:0}}>{label.toUpperCase()}</span>
                          {[{key:'all',label:'All'},...opts].map(o=>(
                            <button key={o.key} onClick={()=>setTrainingFilters(p=>({...p,[key]:o.key}))}
                              style={{padding:'5px 12px',borderRadius:16,cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:0.5,
                                background: trainingFilters[key]===o.key ? 'var(--red)' : 'var(--card)',
                                border:`1px solid ${trainingFilters[key]===o.key ? 'var(--red)' : 'var(--b2)'}`,
                                color: trainingFilters[key]===o.key ? 'var(--on-red)' : 'var(--muted)'}}>
                              {o.label}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>

                    {weeks.length > 0 && (
                      <div style={{marginBottom:24}}>
                        {weeks.map(wk=>(
                          <div key={wk} style={{marginBottom:20}}>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:10}}>Week {wk}</div>
                            <div className="cgrid">
                              {filtered.filter(w=>w.week_number===wk).sort((a,b)=>(a.day_number||0)-(b.day_number||0))
                                .map(w=>card(w, w.day_label ? `Day ${w.day_number} · ${w.day_label}` : `Day ${w.day_number}`))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {flat.length > 0 && <div className="cgrid">{flat.map(w=>card(w, w.tier))}</div>}

                    {filtered.length === 0 && (
                      <div style={{color:'var(--muted)',fontSize:13,marginTop:12}}>No programs match those filters yet.</div>
                    )}
                  </>);
                })()}
                </>)}

                {trainingSubTab === 'running' && (<>
                {runViewTier === null ? (
                  /* ── TIER PICKER ── */
                  <div style={{marginBottom:20}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:14}}>Choose a plan to browse its runs</div>
                    <div className="tier-grid">
                      {Object.entries(TIERS).map(([key,t]) => {
                        const isMine = key === userTier;
                        const isHigher = !canAccess(userTier, key);
                        return (
                          <div key={key} className={`tier-card ${isMine?'sel':''}`} onClick={()=>setRunViewTier(key)}>
                            <div className="tc-name">{t.name}</div>
                            {isMine && <div className="pop-tag" style={{position:'static',display:'inline-block',marginTop:6,marginBottom:10}}>Your Plan</div>}
                            {isHigher && <div style={{color:'var(--muted)',fontSize:11,margin:'6px 0 10px'}}>🔒 Preview what's included</div>}
                            <div style={{color:'var(--red)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,marginTop:10}}>View Runs →</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (() => {
                  const locked = !canAccess(userTier, runViewTier);
                  const inTier = memberRunWorkouts.filter(w => w.tier === runViewTier);
                  const filtered = runFilterType === 'all' ? inTier
                    : runFilterType === 'Programs' ? inTier.filter(w => !!w.duration_weeks)
                    : inTier.filter(w => !w.duration_weeks && w.run_type === runFilterType);

                  return (<>
                    <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
                      <button className="btn btn-ghost" style={{fontSize:12,padding:'7px 16px'}} onClick={()=>setRunViewTier(null)}>← All Plans</button>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>{TIERS[runViewTier]?.name} Runs</div>
                      {locked && <button className="btn btn-red" style={{fontSize:11,padding:'6px 14px',marginLeft:'auto'}} onClick={()=>setTab('membership')}>Upgrade</button>}
                    </div>

                    {/* Filter */}
                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:20}}>
                      {[{key:'all',label:'All'},...RUN_TYPES.map(t=>({key:t,label:t})),{key:'Programs',label:'Programs'}].map(o=>(
                        <button key={o.key} onClick={()=>setRunFilterType(o.key)}
                          style={{padding:'6px 14px',borderRadius:16,cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:0.5,
                            background: runFilterType===o.key ? 'var(--red)' : 'var(--card)',
                            border:`1px solid ${runFilterType===o.key ? 'var(--red)' : 'var(--b2)'}`,
                            color: runFilterType===o.key ? 'var(--on-red)' : 'var(--muted)'}}>
                          {o.label}
                        </button>
                      ))}
                    </div>

                    {filtered.length === 0 && (
                      <div style={{color:'var(--muted)',fontSize:13}}>No runs match those filters yet.</div>
                    )}

                    {filtered.map(w => {
                      const isProgram = !!w.duration_weeks;
                      const sessions = runWktSessions[w.id] || [];
                      const sessionWeeks = [...new Set(sessions.map(s=>s.week_number).filter(Boolean))].sort((a,b)=>a-b);
                      if (isProgram) return (
                        <div key={w.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,overflow:'hidden',marginBottom:12,position:'relative'}}>
                          <div style={{padding:'14px 18px',cursor:locked?'default':'pointer'}} onClick={()=>{ if(locked) return; const next=expandedRunWkt===w.id?null:w.id; setExpandedRunWkt(next); if(next&&!runWktSessions[w.id]) fetchProgramSessions(w.id); }}>
                            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                                  <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 7px',borderRadius:3,textTransform:'uppercase'}}>Program</span>
                                  {w.duration_weeks && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>⏱ {w.duration_weeks} weeks</span>}
                                </div>
                                <div style={{color:'var(--cream)',fontSize:16,fontWeight:700}}>{w.title}</div>
                                {w.notes && !locked && <div style={{color:'var(--muted)',fontSize:12,marginTop:4}}>{w.notes}</div>}
                              </div>
                              {!locked && <span style={{color:'var(--muted)',fontSize:13,marginLeft:12}}>{expandedRunWkt===w.id?'▲':'▼'}</span>}
                            </div>
                          </div>
                          {!locked && expandedRunWkt===w.id && sessions.length > 0 && (
                            <div style={{borderTop:'1px solid var(--b1)',padding:'14px 18px'}}>
                              {sessionWeeks.map(wk=>(
                                <div key={wk} style={{marginBottom:14}}>
                                  <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:8}}>Week {wk}</div>
                                  {sessions.filter(s=>s.week_number===wk).map(s=>(
                                    <div key={s.id} style={{background:'var(--bg2)',borderRadius:8,padding:'10px 14px',marginBottom:6}}>
                                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:s.notes?6:0}}>
                                        {s.day_number && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>Day {s.day_number}{s.day_label?` · ${s.day_label}`:''}</span>}
                                        <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 6px',borderRadius:3,textTransform:'uppercase'}}>{s.run_type}</span>
                                        {s.duration && <span style={{fontSize:11,color:'var(--muted)'}}>⏱ {s.duration}</span>}
                                        {s.rpe && <span style={{fontSize:11,color:'var(--muted)'}}>RPE {s.rpe}</span>}
                                      </div>
                                      {s.notes && <div style={{color:'var(--muted)',fontSize:12,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{s.notes}</div>}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                          {locked && <div className="veil" style={{borderRadius:12}}><div className="veil-ico">🔒</div><div className="veil-lbl">Locked</div><div className="veil-tier">↑ {TIERS[runViewTier]?.name}</div></div>}
                        </div>
                      );
                      return (
                        <div key={w.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,overflow:'hidden',marginBottom:10,position:'relative'}}>
                          <div style={{padding:'14px 18px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'3px 8px',borderRadius:3,textTransform:'uppercase'}}>{w.run_type}</span>
                              {w.duration && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>⏱ {w.duration}</span>}
                              {w.rpe && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>RPE {w.rpe}</span>}
                            </div>
                            <div style={{color:'var(--cream)',fontSize:16,fontWeight:700,marginBottom:w.notes?8:0}}>{w.title}</div>
                            {!locked && w.notes && <div style={{color:'var(--muted)',fontSize:12,lineHeight:1.7,whiteSpace:'pre-wrap',borderTop:'1px solid var(--b1)',paddingTop:10,marginTop:6}}>{w.notes}</div>}
                            {!locked && w.video_url && (() => {
                              const embed = ytEmbed(w.video_url);
                              return embed
                                ? <div style={{position:'relative',paddingBottom:'56.25%',height:0,marginTop:10,borderRadius:8,overflow:'hidden'}}><iframe src={embed} title={w.title} style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none'}} allowFullScreen /></div>
                                : isVideo(w.video_url) ? <video src={w.video_url} controls style={{width:'100%',borderRadius:8,marginTop:10,maxHeight:320}} /> : null;
                            })()}
                          </div>
                          {locked && <div className="veil" style={{borderRadius:12}}><div className="veil-ico">🔒</div><div className="veil-lbl">Locked</div><div className="veil-tier">↑ {TIERS[runViewTier]?.name}</div></div>}
                        </div>
                      );
                    })}
                  </>);
                })()}
                </>)}
              </>)}
            </>)}

            {/* FUEL DEPOT */}
            {tab === "nutrition" && (<>
              {openNutritionItem ? (
                /* ── DETAIL VIEW ── */
                <div>
                  <button className="btn btn-ghost" style={{marginBottom:20,fontSize:13}}
                    onClick={() => setOpenNutritionItem(null)}>← Back to Nutrition</button>
                  <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:6}}>{openNutritionItem.category}</div>
                  <h2 style={{color:'var(--cream)',fontSize:24,fontWeight:800,margin:'0 0 6px'}}>{openNutritionItem.title}</h2>
                  {openNutritionItem.summary && <div style={{color:'var(--muted)',fontSize:13,marginBottom:20,lineHeight:1.6}}>{openNutritionItem.summary}</div>}
                  {/* FILE VIEWER */}
                  {openNutritionItem.file_url && (
                    <div style={{marginBottom:24}}>
                      {openNutritionItem.file_type === 'pdf' && (
                        <div>
                          <iframe src={openNutritionItem.file_url} title={openNutritionItem.file_name} style={{width:'100%',height:500,border:'1px solid var(--b2)',borderRadius:10}} />
                          <a href={openNutritionItem.file_url} target="_blank" rel="noreferrer"
                            style={{display:'inline-block',marginTop:10,color:'var(--red)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>
                            ↓ DOWNLOAD PDF
                          </a>
                        </div>
                      )}
                      {openNutritionItem.file_type === 'video' && (
                        <video src={openNutritionItem.file_url} controls style={{width:'100%',borderRadius:10,maxHeight:400}} />
                      )}
                      {openNutritionItem.file_type === 'image' && (
                        <img src={openNutritionItem.file_url} alt={openNutritionItem.file_name} style={{width:'100%',borderRadius:10,maxHeight:500,objectFit:'contain',background:'var(--card)'}} />
                      )}
                      {!openNutritionItem.file_type && (
                        <a href={openNutritionItem.file_url} target="_blank" rel="noreferrer"
                          style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 20px',background:'var(--card)',border:'1px solid var(--b2)',borderRadius:8,color:'var(--cream)',fontSize:13,textDecoration:'none',fontWeight:600}}>
                          📎 {openNutritionItem.file_name || 'Download File'}
                        </a>
                      )}
                    </div>
                  )}
                  {/* BODY CONTENT */}
                  {openNutritionItem.content && (
                    <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20}}>
                      <div style={{color:'var(--cream)',fontSize:14,lineHeight:1.9,whiteSpace:'pre-wrap'}}>{openNutritionItem.content}</div>
                    </div>
                  )}
                </div>
              ) : (
                /* ── LIST VIEW ── */
                <>
                  <div className="s-eye">Fuel</div>
                  <div className="s-title">Nutrition</div>
                  <div className="s-sub">Evidence-based guidance from your coach.</div>

                  {/* Section sub-tabs */}
                  <div style={{display:'flex',gap:8,marginBottom:24,flexWrap:'wrap'}}>
                    {NUTRITION_SECTIONS.map(s => (
                      <button key={s.key} onClick={()=>setNutritionSubTab(s.key)}
                        style={{
                          padding:'8px 18px',borderRadius:20,cursor:'pointer',
                          fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,
                          background: nutritionSubTab===s.key ? 'var(--red)' : 'var(--card)',
                          border:`1px solid ${nutritionSubTab===s.key ? 'var(--red)' : 'var(--b2)'}`,
                          color: nutritionSubTab===s.key ? 'var(--cream)' : 'var(--muted)',
                          transition:'all 0.2s',
                        }}
                      >{s.label.toUpperCase()}</button>
                    ))}
                  </div>

                  {nutritionLoading ? (
                    <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
                  ) : nutritionItems.filter(n => (n.section||'sports_performance') === nutritionSubTab).length === 0 ? (
                    <div style={{textAlign:'center',padding:60}}>
                      <div style={{fontSize:40,marginBottom:12}}>🥗</div>
                      <div style={{color:'var(--cream)',fontSize:16,fontWeight:700,marginBottom:8}}>Coming Soon</div>
                      <div style={{color:'var(--muted)',fontSize:13}}>{NUTRITION_SECTIONS.find(s=>s.key===nutritionSubTab)?.label} advice will appear here.</div>
                    </div>
                  ) : (
                    /* Group by category, within the selected section */
                    (() => {
                      const sectionItems = nutritionItems.filter(n => (n.section||'sports_performance') === nutritionSubTab);
                      const cats = [...new Set(sectionItems.map(n => n.category))];
                      return cats.map(cat => {
                        const items = sectionItems.filter(n => n.category === cat);
                        return (
                          <div key={cat} style={{marginBottom:32}}>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
                              {cat}
                              <span style={{flex:1,height:1,background:'var(--b2)',display:'block'}} />
                            </div>
                            <div className="cgrid">
                              {items.map(n => {
                                const ok = canAccess(userTier, n.tier);
                                return (
                                  <div key={n.id} className={`cc ${!ok?'lk':''}`}
                                    style={{cursor: ok ? 'pointer' : 'default'}}
                                    onClick={() => ok && setOpenNutritionItem(n)}
                                    onMouseEnter={e => ok && (e.currentTarget.style.borderColor='var(--red)')}
                                    onMouseLeave={e => ok && (e.currentTarget.style.borderColor='')}>
                                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                                      <div className="cc-tag">{n.category}</div>
                                      {n.file_type && (
                                        <span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--muted)',letterSpacing:1,background:'var(--bg2)',padding:'2px 6px',borderRadius:4}}>
                                          {n.file_type === 'pdf' ? '📄 PDF' : n.file_type === 'video' ? '▶ VIDEO' : '🖼 IMAGE'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="cc-title">{n.title}</div>
                                    {n.summary && <div className="cc-desc">{n.summary}</div>}
                                    {ok && <div style={{marginTop:10,fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>READ MORE →</div>}
                                    {!ok && <div className="veil">
                                      <div className="veil-ico">🔒</div>
                                      <div className="veil-lbl">Locked</div>
                                      <div className="veil-tier">↑ {n.tier==='apex'?'Apex':'Elevate'}</div>
                                    </div>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                </>
              )}
            </>)}

            {/* COACHING */}
            {tab === "coaching" && (<>
              <div className="s-eye">Apex</div>
              <div className="s-title">1-on-1 Coaching</div>
              <div className="s-sub">Direct access to your run coach & personal trainer.</div>
              {canAccess(userTier,"apex") ? (
                <div className="co-grid">
                  <div className="co-card">
                    <div className="co-initials">EC</div>
                    <div className="co-name">Your Coach</div>
                    <div className="co-role">Run Coach · Personal Trainer</div>
                    <div className="co-bio">
                      With expertise across run coaching and personal training, Apex sessions are built entirely around your goals, training history, and recovery. No templates. No guesswork. Just results.
                    </div>
                    <button className="btn btn-red">Book a Session</button>
                  </div>
                  <div className="co-card">
                    <div className="co-mono">Available Slots</div>
                    <div className="slot-grid">
                      {[["Mon","7:00am"],["Tue","6:00pm"],["Wed","7:00am"],["Thu","6:00pm"],["Fri","7:00am"],["Sat","8:00am"]].map(([d,t]) => (
                        <div key={d+t} className="slot">
                          <div className="slot-time">{t}</div>
                          <div className="slot-day">{d}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="wall">
                  <div className="wall-ico">🔒</div>
                  <div className="wall-title">Apex Members Only</div>
                  <div className="wall-sub">1-on-1 coaching is exclusive to Apex. Upgrade to get direct access to your coach.</div>
                  <button className="btn btn-red" onClick={() => handleUpgrade('apex')}>Upgrade to Apex</button>
                </div>
              )}
            </>)}

            {/* MY REPORTS */}
            {tab === "reports" && (<>
              <div className="s-eye">Confidential</div>
              <div className="s-title">My Reports</div>
              <div className="s-sub">Your metabolic test results, uploaded by your coach. Only you can see these.</div>

              {myReportsLoading ? (
                <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
              ) : myReports.length === 0 ? (
                <div style={{textAlign:'center',padding:60}}>
                  <div style={{fontSize:40,marginBottom:12}}>🧪</div>
                  <div style={{color:'var(--cream)',fontSize:16,fontWeight:700,marginBottom:8}}>No Reports Yet</div>
                  <div style={{color:'var(--muted)',fontSize:13}}>Your coach will upload your results here after a metabolic test.</div>
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {myReports.map(r => (
                    <div key={r.id} onClick={()=>openReportFile(r)}
                      style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:'16px 20px',cursor:'pointer'}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor='var(--red)'}
                      onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b2)'}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                        <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase'}}>
                          {new Date(r.test_date+'T12:00:00').toLocaleDateString('en-CA',{year:'numeric',month:'short',day:'numeric'})}
                        </span>
                        <span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--muted)',background:'var(--bg2)',padding:'2px 6px',borderRadius:4}}>{r.file_type === 'pdf' ? '📄 PDF' : '🖼 IMAGE'}</span>
                        {r.video_url && <span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--muted)',background:'var(--bg2)',padding:'2px 6px',borderRadius:4}}>🎥 VIDEO</span>}
                      </div>
                      <div style={{color:'var(--cream)',fontSize:15,fontWeight:700,marginBottom:r.notes?6:0}}>{r.title || 'Test Report'}</div>
                      {r.notes && <div style={{color:'var(--muted)',fontSize:12,lineHeight:1.5}}>{r.notes}</div>}
                      <div style={{marginTop:8,fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>VIEW REPORT →</div>
                    </div>
                  ))}
                </div>
              )}

              {/* VIEWER */}
              {openReport && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={()=>{setOpenReport(null);setReportSignedUrl('');}}>
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:20,maxWidth:700,width:'100%',maxHeight:'85vh',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                      <div style={{color:'var(--cream)',fontWeight:700,fontSize:14}}>{openReport.title || openReport.file_name}</div>
                      <button onClick={()=>{setOpenReport(null);setReportSignedUrl('');}} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:20}}>✕</button>
                    </div>
                    {!reportSignedUrl ? (
                      <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading…</div>
                    ) : openReport.file_type === 'pdf' ? (
                      <iframe src={reportSignedUrl} title={openReport.file_name} style={{width:'100%',height:500,border:'1px solid var(--b2)',borderRadius:8}} />
                    ) : (
                      <img src={reportSignedUrl} alt={openReport.file_name} style={{width:'100%',borderRadius:8}} />
                    )}
                    {reportSignedUrl && (
                      <a href={reportSignedUrl} target="_blank" rel="noreferrer" download
                        style={{display:'inline-block',marginTop:12,color:'var(--red)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>
                        ↓ DOWNLOAD
                      </a>
                    )}
                    {openReport.video_url && (() => {
                      const embed = ytEmbed(openReport.video_url);
                      return (
                        <div style={{marginTop:16}}>
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:8}}>Walkthrough</div>
                          {embed
                            ? <div style={{position:'relative',paddingBottom:'56.25%',height:0,borderRadius:8,overflow:'hidden'}}><iframe src={embed} title="Walkthrough" style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none'}} allowFullScreen /></div>
                            : <video src={openReport.video_url} controls style={{width:'100%',borderRadius:8,maxHeight:360}} />}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </>)}

            {/* MESSAGES (member <-> coach DM) */}
            {tab === "messages" && (<>
              <div className="s-eye">Private</div>
              <div className="s-title">Messages</div>
              <div className="s-sub">A private thread between you and your coach.</div>

              {pushPermission !== 'granted' && pushPermission !== 'unsupported' && (
                <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:200,color:'var(--muted)',fontSize:12}}>Turn on notifications so you don't miss a reply.</div>
                  <button onClick={enablePushNotifications} disabled={pushBusy} className="btn btn-red" style={{fontSize:11,padding:'7px 16px'}}>{pushBusy?'…':'Enable Notifications'}</button>
                </div>
              )}

              <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16,minHeight:200}}>
                {dmLoading ? (
                  <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
                ) : dmMessages.length === 0 ? (
                  <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>No messages yet. Say hello!</div>
                ) : dmMessages.map(m => {
                  const mine = m.sender_id === session.user.id;
                  return (
                    <div key={m.id} style={{display:'flex',justifyContent:mine?'flex-end':'flex-start'}}>
                      <div style={{maxWidth:'75%',background:mine?'var(--red)':'var(--card)',color:mine?'var(--on-red)':'var(--cream)',border:mine?'none':'1px solid var(--b2)',borderRadius:12,padding:'10px 14px'}}>
                        <div style={{fontSize:13,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{m.body}</div>
                        <div style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,opacity:0.7,marginTop:4}}>{timeAgo(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={e=>sendDmMessage(session.user.id, e)} style={{display:'flex',gap:10,alignItems:'flex-end'}}>
                <textarea value={newDmMessage} onChange={e=>setNewDmMessage(e.target.value)}
                  placeholder="Write a message to your coach..." rows={2}
                  style={{flex:1,background:'var(--card)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit'}} />
                <button type="submit" className="btn btn-red" disabled={sendingDm||!newDmMessage.trim()} style={{whiteSpace:'nowrap',padding:'10px 18px'}}>
                  {sendingDm ? '...' : 'Send'}
                </button>
              </form>
            </>)}

            {/* COMMUNITY FORUM */}
            {tab === "community" && (<>
              <div className="s-eye">Members</div>
              <div className="s-title">Community</div>

              {/* Community sub-tabs */}
              <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
                {[['general','General'],['run_club','Run Club'],['achievements','Achievements']].map(([t,label]) => (
                  <button key={t} onClick={()=>{ setCommunityTab(t); setOpenPost(null); setPostReplies([]); }}
                    style={{
                      padding:'8px 18px',borderRadius:20,cursor:'pointer',
                      fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,
                      background: communityTab===t ? 'var(--red)' : 'var(--card)',
                      border:`1px solid ${communityTab===t ? 'var(--red)' : 'var(--b2)'}`,
                      color: communityTab===t ? 'var(--cream)' : 'var(--muted)',
                      transition:'all 0.2s',
                    }}
                  >{label.toUpperCase()}</button>
                ))}
              </div>

              {openPost ? (
                <div>
                  <button className="btn btn-ghost" style={{marginBottom:20,fontSize:13}}
                    onClick={() => { setOpenPost(null); setPostReplies([]); setNewReply(''); }}>
                    ← Back
                  </button>
                  <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:20}}>
                    {openPost.workout_data && (
                      <div style={{display:'inline-block',background:'rgba(146,41,32,0.15)',border:'1px solid rgba(146,41,32,0.3)',borderRadius:6,padding:'3px 10px',marginBottom:10,fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>
                        {openPost.workout_data.type === 'run' ? '🏃 RUN LOG' : '💪 WORKOUT'}
                      </div>
                    )}
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                      <div style={{width:36,height:36,borderRadius:'50%',background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",fontSize:13,color:'var(--on-red)',fontWeight:700,flexShrink:0}}>
                        {(openPost.profiles?.email||'?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{(openPost.profiles?.email||'').split('@')[0]}</div>
                        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>{timeAgo(openPost.created_at)}</div>
                      </div>
                    </div>
                    <div style={{color:'var(--cream)',fontSize:14,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{openPost.content}</div>
                    {openPost.workout_data?.maps_url && (
                      <a href={openPost.workout_data.maps_url} target="_blank" rel="noopener noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:6,marginTop:12,color:'var(--red)',fontSize:13,textDecoration:'none',fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>
                        📍 VIEW ON GOOGLE MAPS
                      </a>
                    )}
                    {openPost.photo_url && (isVideo(openPost.photo_url)
                      ? <video src={openPost.photo_url} controls style={{width:'100%',borderRadius:8,marginTop:12,maxHeight:400}} />
                      : <img src={openPost.photo_url} alt="post" style={{width:'100%',borderRadius:8,marginTop:12,maxHeight:400,objectFit:'cover'}} />
                    )}
                    <div style={{display:'flex',alignItems:'center',gap:12,marginTop:14}}>
                      <button onClick={e=>toggleLike(e,openPost.id)}
                        style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'1px solid var(--b2)',borderRadius:20,padding:'5px 14px',cursor:'pointer',color:(postLikes[openPost.id]||[]).find(l=>l.user_id===session?.user?.id)?'var(--red)':'var(--muted)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1}}>
                        ❤ {(postLikes[openPost.id]||[]).length}
                      </button>
                      {(postLikes[openPost.id]||[]).length > 0 && (
                        <button onClick={e=>{e.stopPropagation();setShowLikesFor(showLikesFor===openPost.id?null:openPost.id);}}
                          style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1}}>
                          {showLikesFor===openPost.id ? 'HIDE' : 'WHO LIKED'}
                        </button>
                      )}
                    </div>
                    {showLikesFor===openPost.id && (
                      <div style={{marginTop:8,padding:'10px 14px',background:'var(--bg2)',borderRadius:8,fontSize:12,color:'var(--cream)'}}>
                        {(postLikes[openPost.id]||[]).map(l=>(l.profiles?.email||'').split('@')[0]).join(', ')}
                      </div>
                    )}
                  </div>
                  {postReplies.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:20}}>
                      {postReplies.map(r => (
                        <div key={r.id} style={{background:'var(--bg2)',border:'1px solid var(--b1)',borderRadius:10,padding:'14px 16px',display:'flex',gap:10}}>
                          <div style={{width:30,height:30,borderRadius:'50%',background:'var(--b2)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",fontSize:11,color:'var(--cream)',flexShrink:0}}>
                            {(r.profiles?.email||'?')[0].toUpperCase()}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                              <span style={{color:'var(--cream)',fontSize:12,fontWeight:600}}>{(r.profiles?.email||'').split('@')[0]}</span>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>{timeAgo(r.created_at)}</span>
                            </div>
                            <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.5}}>{r.content}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={submitReply} style={{display:'flex',gap:10,alignItems:'flex-end'}}>
                    <textarea value={newReply} onChange={e => setNewReply(e.target.value)}
                      placeholder="Write a reply..." rows={2}
                      style={{flex:1,background:'var(--card)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit'}} />
                    <button type="submit" className="btn btn-red" disabled={postingForum} style={{whiteSpace:'nowrap',padding:'10px 18px'}}>
                      {postingForum ? '...' : 'Reply'}
                    </button>
                  </form>
                </div>
              ) : (
                <div>
                  {/* ── GENERAL TAB ── */}
                  {communityTab === 'general' && (<>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16,flexWrap:'wrap'}}>
                      <button onClick={toggleCommunityNotify} disabled={savingNotifyPref}
                        style={{display:'flex',alignItems:'center',gap:6,background:communityNotify?'rgba(146,41,32,0.15)':'var(--card)',border:`1px solid ${communityNotify?'var(--red)':'var(--b2)'}`,borderRadius:20,padding:'6px 14px',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,color:communityNotify?'var(--red)':'var(--muted)'}}>
                        {communityNotify ? '🔔 NOTIFS ON' : '🔕 NOTIFS OFF'}
                      </button>
                      <button onClick={()=>setShowSharePrefs(p=>!p)}
                        style={{display:'flex',alignItems:'center',gap:6,background:showSharePrefs?'rgba(146,41,32,0.15)':'var(--card)',border:`1px solid ${showSharePrefs?'var(--red)':'var(--b2)'}`,borderRadius:20,padding:'6px 14px',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,color:showSharePrefs?'var(--red)':'var(--muted)'}}>
                        ⚙ WORKOUT SHARING
                      </button>
                    </div>
                    {showSharePrefs && (
                      <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:16,marginBottom:20}}>
                        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',marginBottom:12}}>WORKOUT AUTO-POST SETTINGS</div>
                        <div style={{color:'var(--muted)',fontSize:12,marginBottom:14}}>Choose which metrics appear when a workout is auto-posted to the feed.</div>
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                          {[['distance','Distance'],['time','Time'],['pace','Pace'],['hr','Heart Rate'],['calories','Calories'],['notes','Notes']].map(([key,label]) => (
                            <label key={key} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                              <input type="checkbox" checked={sharePrefs[key]} disabled={savingSharePrefs}
                                onChange={e=>updateSharePref(key,e.target.checked)}
                                style={{accentColor:'var(--red)',width:16,height:16}} />
                              <span style={{color:'var(--cream)',fontSize:13}}>{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <form onSubmit={submitPost} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                      <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                        placeholder="Share something with the community..." rows={3}
                        style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box',marginBottom:10}} />
                      <div style={{display:'flex',alignItems:'center',gap:10,justifyContent:'space-between',flexWrap:'wrap'}}>
                        <label style={{cursor:'pointer',display:'flex',alignItems:'center',gap:6,color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>
                          <input type="file" accept="image/*,video/*" style={{display:'none'}} onChange={e => setPostPhoto(e.target.files[0])} />
                          {postPhoto ? postPhoto.name.slice(0,24) : '📷 ADD PHOTO / VIDEO'}
                        </label>
                        <button type="submit" className="btn btn-red" disabled={postingForum||!newPost.trim()} style={{padding:'8px 20px',fontSize:13}}>
                          {postingForum ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                    </form>
                    {forumLoading && <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>}
                    <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      {forumPosts.filter(p=>!p.tab||p.tab==='general').map(post => (
                        <div key={post.id}
                          style={{background:'var(--card)',border:`1px solid ${post.workout_data?'rgba(146,41,32,0.3)':'var(--b2)'}`,borderRadius:12,padding:20}}>
                          {post.workout_data && (
                            <div style={{display:'inline-block',background:'rgba(146,41,32,0.12)',border:'1px solid rgba(146,41,32,0.25)',borderRadius:6,padding:'3px 10px',marginBottom:10,fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>
                              {post.workout_data.type==='run' ? '🏃 RUN LOG' : '💪 WORKOUT'}
                            </div>
                          )}
                          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,cursor:'pointer'}}
                            onClick={() => openForumPost(post)}
                            onMouseEnter={e=>e.currentTarget.parentElement.style.borderColor='var(--red)'}
                            onMouseLeave={e=>e.currentTarget.parentElement.style.borderColor=post.workout_data?'rgba(146,41,32,0.3)':'var(--b2)'}>
                            <div style={{width:34,height:34,borderRadius:'50%',background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",fontSize:12,color:'var(--on-red)',fontWeight:700,flexShrink:0}}>
                              {(post.profiles?.email||'?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{(post.profiles?.email||'').split('@')[0]}</div>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>{timeAgo(post.created_at)}</div>
                            </div>
                          </div>
                          <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.6,marginBottom:post.photo_url?10:0,whiteSpace:'pre-wrap',cursor:'pointer'}}
                            onClick={()=>openForumPost(post)}>{post.content}</div>
                          {post.photo_url && (isVideo(post.photo_url)
                            ? <video src={post.photo_url} controls style={{width:'100%',borderRadius:8,maxHeight:200}} />
                            : <img src={post.photo_url} alt="post" style={{width:'100%',borderRadius:8,maxHeight:200,objectFit:'cover'}} />
                          )}
                          <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12}}>
                            <button onClick={e=>toggleLike(e,post.id)}
                              style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'1px solid var(--b2)',borderRadius:20,padding:'4px 12px',cursor:'pointer',color:(postLikes[post.id]||[]).find(l=>l.user_id===session?.user?.id)?'var(--red)':'var(--muted)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1}}>
                              ❤ {(postLikes[post.id]||[]).length}
                            </button>
                            {(postLikes[post.id]||[]).length > 0 && (
                              <button onClick={e=>{e.stopPropagation();setShowLikesFor(showLikesFor===post.id?null:post.id);}}
                                style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1}}>
                                {showLikesFor===post.id ? 'HIDE' : 'WHO LIKED'}
                              </button>
                            )}
                            <span onClick={()=>openForumPost(post)} style={{marginLeft:'auto',fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1,cursor:'pointer'}}>COMMENTS</span>
                          </div>
                          {showLikesFor===post.id && (postLikes[post.id]||[]).length > 0 && (
                            <div style={{marginTop:8,padding:'8px 12px',background:'var(--bg2)',borderRadius:8,fontSize:12,color:'var(--cream)'}}>
                              {(postLikes[post.id]||[]).map(l=>(l.profiles?.email||'').split('@')[0]).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                      {!forumLoading && forumPosts.filter(p=>!p.tab||p.tab==='general').length===0 && (
                        <div style={{textAlign:'center',padding:40,color:'var(--muted)',fontSize:13}}>No posts yet. Be the first to share something!</div>
                      )}
                    </div>
                  </>)}

                  {/* ── RUN CLUB TAB ── */}
                  {communityTab === 'run_club' && (<>
                    {isAdmin && (
                      <form onSubmit={createRunClubPost} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',marginBottom:14}}>POST A GROUP RUN</div>
                        <input value={runClubForm.title} onChange={e=>setRunClubForm(p=>({...p,title:e.target.value}))}
                          placeholder="Run title / description *" required
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',boxSizing:'border-box',marginBottom:10}} />
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                          <input value={runClubForm.date} onChange={e=>setRunClubForm(p=>({...p,date:e.target.value}))}
                            type="date" placeholder="Date"
                            style={{background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit'}} />
                          <input value={runClubForm.distance} onChange={e=>setRunClubForm(p=>({...p,distance:e.target.value}))}
                            placeholder="Distance (e.g. 10km)"
                            style={{background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit'}} />
                        </div>
                        <input value={runClubForm.location} onChange={e=>setRunClubForm(p=>({...p,location:e.target.value}))}
                          placeholder="Starting location name"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',boxSizing:'border-box',marginBottom:10}} />
                        <input value={runClubForm.maps_url} onChange={e=>setRunClubForm(p=>({...p,maps_url:e.target.value}))}
                          placeholder="Google Maps link (paste URL)"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',boxSizing:'border-box',marginBottom:14}} />
                        <button type="submit" className="btn btn-red" disabled={savingRunClub||!runClubForm.title.trim()} style={{padding:'8px 20px',fontSize:13}}>
                          {savingRunClub ? 'Posting...' : 'Post Run'}
                        </button>
                      </form>
                    )}
                    {forumLoading && <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>}
                    <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      {forumPosts.filter(p=>p.tab==='run_club').map(post => (
                        <div key={post.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20}}
                          onMouseEnter={e=>e.currentTarget.style.borderColor='var(--red)'}
                          onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b2)'}>
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                            {post.workout_data?.date && (
                              <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1}}>{post.workout_data.date}</span>
                            )}
                            {post.workout_data?.distance && (
                              <span style={{background:'var(--bg2)',color:'var(--muted)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1}}>{post.workout_data.distance}</span>
                            )}
                          </div>
                          <div style={{color:'var(--cream)',fontSize:15,fontWeight:700,marginBottom:8}}>{post.content}</div>
                          {post.workout_data?.location && (
                            <div style={{color:'var(--muted)',fontSize:13,marginBottom:10}}>📍 {post.workout_data.location}</div>
                          )}
                          {post.workout_data?.maps_url && (
                            <a href={post.workout_data.maps_url} target="_blank" rel="noopener noreferrer"
                              onClick={e=>e.stopPropagation()}
                              style={{display:'inline-flex',alignItems:'center',gap:6,color:'var(--red)',fontSize:12,textDecoration:'none',fontFamily:"'Poppins',sans-serif",letterSpacing:1,border:'1px solid rgba(146,41,32,0.4)',borderRadius:8,padding:'6px 14px',marginBottom:12}}>
                              📍 VIEW ON GOOGLE MAPS
                            </a>
                          )}
                          <div style={{display:'flex',alignItems:'center',gap:12,marginTop:8}}>
                            <button onClick={e=>toggleLike(e,post.id)}
                              style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'1px solid var(--b2)',borderRadius:20,padding:'4px 12px',cursor:'pointer',color:(postLikes[post.id]||[]).find(l=>l.user_id===session?.user?.id)?'var(--red)':'var(--muted)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1}}>
                              ❤ {(postLikes[post.id]||[]).length}
                            </button>
                            {(postLikes[post.id]||[]).length > 0 && (
                              <button onClick={e=>{e.stopPropagation();setShowLikesFor(showLikesFor===post.id?null:post.id);}}
                                style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1}}>
                                {showLikesFor===post.id ? 'HIDE' : 'WHO LIKED'}
                              </button>
                            )}
                            <span onClick={()=>openForumPost(post)} style={{marginLeft:'auto',fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1,cursor:'pointer'}}>COMMENTS</span>
                          </div>
                          {showLikesFor===post.id && (postLikes[post.id]||[]).length > 0 && (
                            <div style={{marginTop:8,padding:'8px 12px',background:'var(--bg2)',borderRadius:8,fontSize:12,color:'var(--cream)'}}>
                              {(postLikes[post.id]||[]).map(l=>(l.profiles?.email||'').split('@')[0]).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                      {!forumLoading && forumPosts.filter(p=>p.tab==='run_club').length===0 && (
                        <div style={{textAlign:'center',padding:40,color:'var(--muted)',fontSize:13}}>No group runs posted yet.</div>
                      )}
                    </div>
                  </>)}

                  {/* ── ACHIEVEMENTS TAB ── */}
                  {communityTab === 'achievements' && (<>
                    <form onSubmit={submitPost} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                      <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                        placeholder="Share a race result, milestone, or achievement..." rows={3}
                        style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box',marginBottom:10}} />
                      <div style={{display:'flex',alignItems:'center',gap:10,justifyContent:'space-between',flexWrap:'wrap'}}>
                        <label style={{cursor:'pointer',display:'flex',alignItems:'center',gap:6,color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>
                          <input type="file" accept="image/*,video/*" style={{display:'none'}} onChange={e => setPostPhoto(e.target.files[0])} />
                          {postPhoto ? postPhoto.name.slice(0,24) : '📷 ADD PHOTO / VIDEO'}
                        </label>
                        <button type="submit" className="btn btn-red" disabled={postingForum||!newPost.trim()} style={{padding:'8px 20px',fontSize:13}}>
                          {postingForum ? 'Posting...' : 'Share Achievement'}
                        </button>
                      </div>
                    </form>
                    {forumLoading && <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>}
                    <div style={{display:'flex',flexDirection:'column',gap:12}}>
                      {forumPosts.filter(p=>p.tab==='achievements').map(post => (
                        <div key={post.id}
                          style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20}}>
                          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,cursor:'pointer'}}
                            onClick={() => openForumPost(post)}
                            onMouseEnter={e=>e.currentTarget.parentElement.style.borderColor='var(--red)'}
                            onMouseLeave={e=>e.currentTarget.parentElement.style.borderColor='var(--b2)'}>
                            <div style={{width:34,height:34,borderRadius:'50%',background:'var(--red)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",fontSize:12,color:'var(--on-red)',fontWeight:700,flexShrink:0}}>
                              {(post.profiles?.email||'?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{(post.profiles?.email||'').split('@')[0]}</div>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>{timeAgo(post.created_at)}</div>
                            </div>
                          </div>
                          <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.6,marginBottom:post.photo_url?10:0,whiteSpace:'pre-wrap',cursor:'pointer'}}
                            onClick={()=>openForumPost(post)}>{post.content}</div>
                          {post.photo_url && (isVideo(post.photo_url)
                            ? <video src={post.photo_url} controls style={{width:'100%',borderRadius:8,maxHeight:200}} />
                            : <img src={post.photo_url} alt="post" style={{width:'100%',borderRadius:8,maxHeight:200,objectFit:'cover'}} />
                          )}
                          <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12}}>
                            <button onClick={e=>toggleLike(e,post.id)}
                              style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'1px solid var(--b2)',borderRadius:20,padding:'4px 12px',cursor:'pointer',color:(postLikes[post.id]||[]).find(l=>l.user_id===session?.user?.id)?'var(--red)':'var(--muted)',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1}}>
                              ❤ {(postLikes[post.id]||[]).length}
                            </button>
                            {(postLikes[post.id]||[]).length > 0 && (
                              <button onClick={e=>{e.stopPropagation();setShowLikesFor(showLikesFor===post.id?null:post.id);}}
                                style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:1}}>
                                {showLikesFor===post.id ? 'HIDE' : 'WHO LIKED'}
                              </button>
                            )}
                            <span onClick={()=>openForumPost(post)} style={{marginLeft:'auto',fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1,cursor:'pointer'}}>COMMENTS</span>
                          </div>
                          {showLikesFor===post.id && (postLikes[post.id]||[]).length > 0 && (
                            <div style={{marginTop:8,padding:'8px 12px',background:'var(--bg2)',borderRadius:8,fontSize:12,color:'var(--cream)'}}>
                              {(postLikes[post.id]||[]).map(l=>(l.profiles?.email||'').split('@')[0]).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                      {!forumLoading && forumPosts.filter(p=>p.tab==='achievements').length===0 && (
                        <div style={{textAlign:'center',padding:40,color:'var(--muted)',fontSize:13}}>No achievements posted yet. Share your first one!</div>
                      )}
                    </div>
                  </>)}
                </div>
              )}
            </>)}

            {/* Q & A */}
            {tab === "qa" && (<>
              {openQaPost ? (
                /* ── THREAD DETAIL ── */
                <div>
                  <button className="btn btn-ghost" style={{marginBottom:20,fontSize:13}}
                    onClick={() => { setOpenQaPost(null); setQaReplies([]); setNewQaReply(''); }}>
                    ← Back to Q &amp; A
                  </button>
                  <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:20}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                      <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1,textTransform:'uppercase'}}>{openQaPost.topic}</span>
                      {openQaPost.is_admin_post && <span style={{background:'var(--bg2)',color:'var(--muted)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1}}>▶ VIDEO</span>}
                    </div>
                    {openQaPost.title && <h2 style={{color:'var(--cream)',fontSize:20,fontWeight:800,margin:'0 0 10px'}}>{openQaPost.title}</h2>}
                    {/* Video embed */}
                    {openQaPost.video_url && (() => {
                      const embed = getYoutubeEmbed(openQaPost.video_url);
                      return embed
                        ? <div style={{position:'relative',paddingBottom:'56.25%',height:0,overflow:'hidden',borderRadius:10,marginBottom:14}}>
                            <iframe src={embed} title={openQaPost.title} allowFullScreen style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',border:'none',borderRadius:10}} />
                          </div>
                        : isVideo(openQaPost.video_url)
                          ? <video src={openQaPost.video_url} controls style={{width:'100%',borderRadius:10,marginBottom:14,maxHeight:360}} />
                          : null;
                    })()}
                    {openQaPost.content && <div style={{color:'var(--cream)',fontSize:14,lineHeight:1.8,whiteSpace:'pre-wrap',marginBottom:10}}>{openQaPost.content}</div>}
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:8}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>
                        {openQaPost.is_anonymous && !isAdmin ? 'Anonymous' : (openQaPost.profiles?.email||'').split('@')[0]}
                        {openQaPost.is_anonymous && isAdmin && <span style={{opacity:0.6}}> (anonymous to members)</span>}
                        {' · '}{timeAgo(openQaPost.created_at)}
                      </div>
                      {isAdmin && (
                        <div style={{display:'flex',gap:12}}>
                          <button onClick={()=>toggleQaHidden(openQaPost)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>{openQaPost.is_hidden ? 'UNHIDE' : 'HIDE'}</button>
                          <button onClick={()=>deleteQaPost(openQaPost.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>DELETE</button>
                        </div>
                      )}
                    </div>
                    {openQaPost.is_hidden && (
                      <div style={{marginTop:10,padding:'8px 12px',background:'rgba(146,41,32,0.1)',border:'1px solid rgba(146,41,32,0.3)',borderRadius:6,color:'var(--red)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                        Hidden from members — only you can see this.
                      </div>
                    )}
                  </div>
                  {/* Replies */}
                  {qaReplies.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:20}}>
                      {qaReplies.map(r => (
                        <div key={r.id} style={{background:'var(--bg2)',border:'1px solid var(--b1)',borderRadius:10,padding:'14px 16px',display:'flex',gap:10}}>
                          <div style={{width:30,height:30,borderRadius:'50%',background:'var(--b2)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",fontSize:11,color:'var(--cream)',flexShrink:0}}>
                            {(r.profiles?.email||'?')[0].toUpperCase()}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                              <span style={{color:'var(--cream)',fontSize:12,fontWeight:600}}>{(r.profiles?.email||'').split('@')[0]}</span>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',letterSpacing:1}}>{timeAgo(r.created_at)}</span>
                            </div>
                            <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.5}}>{r.content}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={submitQaReply} style={{display:'flex',gap:10,alignItems:'flex-end'}}>
                    <textarea value={newQaReply} onChange={e=>setNewQaReply(e.target.value)}
                      placeholder="Write a reply..." rows={2}
                      style={{flex:1,background:'var(--card)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit'}} />
                    <button type="submit" className="btn btn-red" disabled={postingQa||!newQaReply.trim()} style={{whiteSpace:'nowrap',padding:'10px 18px'}}>
                      {postingQa ? '...' : 'Reply'}
                    </button>
                  </form>
                </div>
              ) : (
                /* ── FEED ── */
                <>
                  <div className="s-eye">Learn</div>
                  <div className="s-title">Q &amp; A</div>
                  <div className="s-sub">Educational videos and community questions, organised by topic.</div>

                  {/* TOPIC FILTER */}
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:24}}>
                    {['all', ...QA_TOPICS].map(t => (
                      <button key={t} onClick={()=>setQaTopicFilter(t)}
                        style={{padding:'7px 16px',borderRadius:20,border:`1px solid ${qaTopicFilter===t?'var(--red)':'var(--b2)'}`,background:qaTopicFilter===t?'var(--red)':'transparent',color:qaTopicFilter===t?'#fff':'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>
                        {t === 'all' ? 'All' : t}
                      </button>
                    ))}
                  </div>

                  {/* ADMIN: POST A VIDEO */}
                  {isAdmin && (
                    <div style={{marginBottom:20}}>
                      {!showQaVideoForm ? (
                        <button onClick={()=>setShowQaVideoForm(true)}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',background:'var(--card)',border:'1px dashed var(--red)',borderRadius:8,color:'var(--red)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',width:'100%',justifyContent:'center'}}>
                          ▶ POST EDUCATIONAL VIDEO
                        </button>
                      ) : (
                        <form onSubmit={submitQaVideo} style={{background:'var(--card)',border:'1px solid var(--red)',borderRadius:12,padding:20}}>
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:16}}>New Educational Video</div>
                          <div style={{display:'flex',flexDirection:'column',gap:12}}>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE *</div>
                              <input value={qaVideoForm.title} onChange={e=>setQaVideoForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Understanding VO2 Max" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUTUBE / VIDEO URL *</div>
                              <input value={qaVideoForm.video_url} onChange={e=>setQaVideoForm(p=>({...p,video_url:e.target.value}))} required placeholder="https://youtube.com/watch?v=... or upload below" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box',marginBottom:8}} />
                              <label style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                                <input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setQaVideoForm(p=>({...p,video_url:url})))} />
                                {uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}
                              </label>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TOPIC</div>
                              <select value={qaVideoForm.topic} onChange={e=>setQaVideoForm(p=>({...p,topic:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                                {QA_TOPICS.map(t=><option key={t}>{t}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DESCRIPTION (optional)</div>
                              <textarea value={qaVideoForm.content} onChange={e=>setQaVideoForm(p=>({...p,content:e.target.value}))} rows={3} placeholder="Brief description of what this video covers..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box'}} />
                            </div>
                            <div style={{display:'flex',gap:10}}>
                              <button type="submit" className="btn btn-red" disabled={postingQa} style={{flex:1,padding:'10px 0',fontSize:13}}>
                                {postingQa ? 'Posting...' : 'Post Video'}
                              </button>
                              <button type="button" className="btn btn-ghost" onClick={()=>setShowQaVideoForm(false)} style={{padding:'10px 20px'}}>Cancel</button>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  )}

                  {/* ASK A QUESTION */}
                  <form onSubmit={submitQaQuestion} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                    <div style={{display:'flex',gap:10,marginBottom:10,flexWrap:'wrap'}}>
                      {QA_TOPICS.map(t => (
                        <button key={t} type="button" onClick={()=>setQaQuestionTopic(t)}
                          style={{padding:'5px 14px',borderRadius:20,border:`1px solid ${qaQuestionTopic===t?'var(--red)':'var(--b2)'}`,background:qaQuestionTopic===t?'var(--red)':'transparent',color:qaQuestionTopic===t?'#fff':'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <textarea value={newQaQuestion} onChange={e=>setNewQaQuestion(e.target.value)}
                      placeholder="Ask a question about physiology, training, nutrition..." rows={3}
                      style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box',marginBottom:10}} />
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                      <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:'var(--muted)',fontSize:12}}>
                        <input type="checkbox" checked={qaAskAnonymous} onChange={e=>setQaAskAnonymous(e.target.checked)} style={{accentColor:'var(--red)',width:15,height:15}} />
                        Ask anonymously <span style={{opacity:0.7}}>(hidden from other members, coach can still see it)</span>
                      </label>
                      <button type="submit" className="btn btn-red" disabled={postingQa||!newQaQuestion.trim()} style={{padding:'8px 24px',fontSize:13}}>
                        {postingQa ? 'Posting...' : 'Ask Question'}
                      </button>
                    </div>
                  </form>

                  {/* POSTS FEED */}
                  {qaLoading && <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>}
                  {!qaLoading && (() => {
                    const filtered = qaTopicFilter === 'all' ? qaPosts : qaPosts.filter(p => p.topic === qaTopicFilter);
                    if (!filtered.length) return <div style={{textAlign:'center',padding:40,color:'var(--muted)',fontSize:13}}>{qaTopicFilter === 'all' ? 'No posts yet. Be the first to ask a question!' : `No posts in ${qaTopicFilter} yet.`}</div>;
                    return (
                      <div style={{display:'flex',flexDirection:'column',gap:12}}>
                        {filtered.map(post => (
                          <div key={post.id}
                            style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,cursor:'pointer'}}
                            onClick={()=>openQaThread(post)}
                            onMouseEnter={e=>e.currentTarget.style.borderColor='var(--red)'}
                            onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b2)'}>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                              <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1,textTransform:'uppercase'}}>{post.topic}</span>
                              {post.is_admin_post && <span style={{background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--muted)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'3px 8px',borderRadius:4,letterSpacing:1}}>▶ VIDEO</span>}
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1,marginLeft:'auto'}}>{timeAgo(post.created_at)}</span>
                            </div>
                            {post.title
                              ? <div style={{color:'var(--cream)',fontSize:15,fontWeight:700,marginBottom:post.content?6:0}}>{post.title}</div>
                              : null}
                            {post.content && <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.6,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{post.content}</div>}
                            <div style={{marginTop:10,fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>TAP TO VIEW</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </>)}

            {/* SQUAD LEADER */}
            {tab === "squad" && (<>
              {coachBioLoading ? (
                <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
              ) : !coachBio || (!coachBio.name && !coachBio.bio) ? (
                <div style={{textAlign:'center',padding:60}}>
                  <div style={{fontSize:40,marginBottom:12}}>👟</div>
                  <div style={{color:'var(--cream)',fontSize:18,fontWeight:700,marginBottom:8}}>Coming Soon</div>
                  <div style={{color:'var(--muted)',fontSize:13}}>Your owner / head coach bio will appear here.</div>
                </div>
              ) : (
                <div>
                  {/* HERO PHOTO */}
                  {coachBio.photo_urls && coachBio.photo_urls.filter(u=>u).length > 0 && (
                    <div style={{borderRadius:16,overflow:'hidden',marginBottom:28,maxHeight:320,position:'relative'}}>
                      <img src={coachBio.photo_urls.filter(u=>u)[0]} alt="Owner / Head Coach" style={{width:'100%',height:320,objectFit:'cover',objectPosition:'top',display:'block'}} />
                      <div style={{position:'absolute',bottom:0,left:0,right:0,height:'50%',background:'linear-gradient(transparent,rgba(20,20,20,0.9))'}} />
                    </div>
                  )}

                  {/* NAME & TAGLINE */}
                  {coachBio.name && (
                    <div style={{marginBottom:20}}>
                      <h2 style={{color:'var(--cream)',fontSize:28,fontWeight:800,margin:0,lineHeight:1.2}}>{coachBio.name}</h2>
                      {coachBio.tagline && <div style={{color:'var(--red)',fontFamily:"'Poppins',sans-serif",fontSize:12,letterSpacing:2,textTransform:'uppercase',marginTop:6}}>{coachBio.tagline}</div>}
                    </div>
                  )}

                  {/* BIO */}
                  {coachBio.bio && (
                    <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:20}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:12}}>About</div>
                      <div style={{color:'var(--cream)',fontSize:14,lineHeight:1.8,whiteSpace:'pre-wrap'}}>{coachBio.bio}</div>
                    </div>
                  )}

                  {/* CREDENTIALS */}
                  {coachBio.credentials && coachBio.credentials.length > 0 && (
                    <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:20}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:14}}>Credentials &amp; Certifications</div>
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {coachBio.credentials.map((c,i) => (
                          <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10}}>
                            <div style={{width:6,height:6,borderRadius:'50%',background:'var(--red)',marginTop:6,flexShrink:0}} />
                            <div style={{color:'var(--cream)',fontSize:14,lineHeight:1.5}}>{c}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ADDITIONAL PHOTOS */}
                  {coachBio.photo_urls && coachBio.photo_urls.filter(u=>u).length > 1 && (
                    <div style={{marginBottom:20}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:12}}>Photos</div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10}}>
                        {coachBio.photo_urls.filter(u=>u).slice(1).map((url,i) => (
                          <img key={i} src={url} alt="owner / head coach" style={{width:'100%',aspectRatio:'1',objectFit:'cover',borderRadius:10,display:'block'}} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>)}


          </div>

        </>) : (

          /* ── ADMIN ── */
          <div className="admin-wrap">
            <div className="admin-head">
              <div className="admin-title">Admin Dashboard</div>
              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:2,color:"var(--muted)",textTransform:"uppercase"}}>
                Evolution Endurance
              </div>
            </div>
            <div className="admin-layout">
              <div className="admin-side">
                {[["members","👥 Members"],["calendar","📅 Calendar"],["content","📋 Content"],["library","📚 Library"],["fuel","🥗 Nutrition"],["reports","🧪 Reports"],["messages","💬 Messages"],["reminders","🔔 Reminders"],["squad","👤 Owner / Head Coach"],["qa","❓ Q & A"],["analytics","📊 Analytics"]].map(([k,l]) => (
                  <div key={k} className={`anav ${adminTab===k?"on":""}`} onClick={() => { setAdminTab(k); if(k==='calendar'||k==='members') markAdminCompNotifsRead(); if(k==='library') fetchExerciseLibrary(); if(k==='qa'){ fetchQaPosts(); markAdminQaNotifsRead(); } if(k==='reports') fetchAdminReports(); if(k==='messages'){ fetchAdminDmThreads(); setAdminDmSelected(null); } if(k==='reminders') fetchMotivationMsgs(); }} style={{position:'relative'}}>
                    {l}
                    {k==='calendar' && adminCompNotifs > 0 && (
                      <span style={{marginLeft:6,background:'var(--red)',color:'#fff',borderRadius:10,fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'1px 5px',fontWeight:700}}>{adminCompNotifs}</span>
                    )}
                    {k==='qa' && adminQaNotifs > 0 && (
                      <span style={{marginLeft:6,background:'var(--red)',color:'#fff',borderRadius:10,fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'1px 5px',fontWeight:700}}>{adminQaNotifs}</span>
                    )}
                    {k==='messages' && adminDmUnreadTotal > 0 && (
                      <span style={{marginLeft:6,background:'var(--red)',color:'#fff',borderRadius:10,fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'1px 5px',fontWeight:700}}>{adminDmUnreadTotal}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="admin-body">

                {adminTab === "members" && (<>
                  <div className="astats">
                    <div className="astat"><div className="astat-val">{adminMembers.length}</div><div className="astat-lbl">Total Members</div></div>
                    <div className="astat"><div className="astat-val">{adminMembers.filter(m=>m.tier==="apex").length}</div><div className="astat-lbl">Apex</div></div>
                    <div className="astat"><div className="astat-val">{adminMembers.filter(m=>m.tier==="elevate").length}</div><div className="astat-lbl">Elevate</div></div>
                    <div className="astat"><div className="astat-val">{adminMembers.filter(m=>m.tier==="stride").length}</div><div className="astat-lbl">Stride</div></div>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div className="s-eye" style={{margin:0}}>All Members</div>
                    <button className="btn btn-red" style={{fontSize:12,padding:"6px 14px"}} onClick={fetchAdminMembers}>↺ Refresh</button>
                  </div>
                  {adminLoadingMembers ? (
                    <div style={{color:"var(--muted)",fontSize:13,padding:"20px 0"}}>Loading members…</div>
                  ) : (
                  <div className="tbl">
                    <div className="thead" style={{"--tc":"3fr 1fr 1fr"}}>
                      <div>Email</div><div>Tier</div><div>Joined</div>
                    </div>
                    {adminMembers.length === 0 && (
                      <div style={{color:"var(--muted)",fontSize:13,padding:"20px 0"}}>No members yet.</div>
                    )}
                    {adminMembers.map(m => (
                      <div key={m.id} style={{borderBottom:'1px solid var(--b1)'}}>
                        <div className="trow" style={{"--tc":"3fr 1fr 1fr 1fr",cursor:'pointer'}} onClick={()=>{ if(expandedMember===m.id){setExpandedMember(null);}else{setExpandedMember(m.id);loadMemberAssignments(m.id);loadMemberPrescribedRuns(m.id);} }}>
                          <div className="cell-email" style={{fontSize:13,display:'flex',alignItems:'center',gap:6}}>
                            <span style={{color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>{expandedMember===m.id?'▲':'▼'}</span>
                            {m.email}
                          </div>
                          <div onClick={e=>e.stopPropagation()}>
                            <select
                              value={m.tier}
                              onChange={e => changeTier(m.id, e.target.value)}
                              style={{background:"var(--bg2)",color:"var(--cream)",border:"1px solid var(--b2)",borderRadius:4,padding:"4px 8px",fontSize:12,cursor:"pointer"}}
                            >
                              <option value="stride">Stride</option>
                              <option value="elevate">Elevate</option>
                              <option value="apex">Apex</option>
                            </select>
                          </div>
                          <div className="cell-mono" style={{fontSize:12}}>{m.created_at ? new Date(m.created_at).toLocaleDateString("en-CA",{year:"numeric",month:"short",day:"numeric"}) : "—"}</div>
                          <div style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{(memberAssignments[m.id]||[]).length > 0 ? `${(memberAssignments[m.id]||[]).length} assigned` : ''}</div>
                        </div>
                        {expandedMember===m.id && (
                          <>
                          <div style={{background:'var(--black)',borderRadius:8,padding:16,margin:'0 4px 12px 4px'}}>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:12}}>Assigned Workouts</div>
                            {assignLoading && <div style={{color:'var(--muted)',fontSize:12}}>Loading…</div>}
                            {!assignLoading && (memberAssignments[m.id]||[]).length===0 && (
                              <div style={{color:'var(--muted)',fontSize:12,marginBottom:12}}>No workouts assigned yet.</div>
                            )}
                            {!assignLoading && (memberAssignments[m.id]||[]).map(a => (
                              <div key={a.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',background:'var(--bg2)',borderRadius:6,marginBottom:6}}>
                                <div>
                                  <span style={{color:'var(--cream)',fontSize:13}}>{a.workouts?.title || 'Unknown'}</span>
                                  <span className={`tier-pill pill-${a.workouts?.tier||'stride'}`} style={{marginLeft:8,fontSize:10}}>{a.workouts?.tier}</span>
                                </div>
                                <button onClick={()=>removeAssignment(a.id,m.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16,padding:'2px 6px'}} title="Remove assignment">✕</button>
                              </div>
                            ))}
                            <div style={{display:'flex',gap:8,marginTop:10,alignItems:'center'}}>
                              <select
                                value={assigningWorkoutId}
                                onChange={e=>setAssigningWorkoutId(e.target.value)}
                                style={{flex:1,background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12}}
                              >
                                <option value="">— Select a workout to assign —</option>
                                {adminWorkouts.filter(w=>!(memberAssignments[m.id]||[]).some(a=>a.workout_id===w.id)).map(w=>(
                                  <option key={w.id} value={w.id}>{w.title} ({w.tier}){w.visibility==='private'?' 🔒':''}</option>
                                ))}
                              </select>
                              <button
                                className="btn btn-red"
                                style={{fontSize:12,padding:'7px 16px',whiteSpace:'nowrap'}}
                                disabled={!assigningWorkoutId||assignLoading}
                                onClick={()=>assignWorkoutToMember(assigningWorkoutId,m.id)}
                              >Assign</button>
                            </div>
                          </div>

                          {/* PRESCRIBED RUNS SECTION */}
                          <div style={{background:'var(--black)',borderRadius:8,padding:16,margin:'8px 4px 12px 4px'}}>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:12}}>Prescribed Runs</div>
                            {(memberPrescRuns[m.id]||[]).length===0 && <div style={{color:'var(--muted)',fontSize:12,marginBottom:12}}>No prescribed runs yet.</div>}
                            {(memberPrescRuns[m.id]||[]).map(pr => (
                              <div key={pr.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',background:'var(--bg2)',borderRadius:6,marginBottom:6,flexWrap:'wrap',gap:6}}>
                                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                  {pr.completed && <span style={{fontSize:9,color:'#2f7a4a',fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>✓ DONE</span>}
                                  <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{pr.distance_km}km</span>
                                  <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)'}}>{pr.run_type}</span>
                                  <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>
                                    {new Date(pr.scheduled_date+'T12:00:00').toLocaleDateString('en-CA',{month:'short',day:'numeric'})}
                                  </span>
                                  {pr.notes && <span style={{color:'var(--muted)',fontSize:11,fontStyle:'italic'}}>{pr.notes}</span>}
                                </div>
                                <button onClick={()=>deletePrescribedRun(pr.id,m.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16,padding:'2px 6px'}}>✕</button>
                              </div>
                            ))}
                            {/* Add prescribed run form */}
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:12}}>
                              <div>
                                <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>DATE</div>
                                <input type="date" value={prForm.scheduled_date} onChange={e=>setPrForm(p=>({...p,scheduled_date:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}} />
                              </div>
                              <div>
                                <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>DISTANCE (km)</div>
                                <input type="number" min="0" step="0.1" value={prForm.distance_km} onChange={e=>setPrForm(p=>({...p,distance_km:e.target.value}))} placeholder="e.g. 10" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}} />
                              </div>
                              <div>
                                <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>TYPE</div>
                                <select value={prForm.run_type} onChange={e=>setPrForm(p=>({...p,run_type:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}>
                                  {['Easy','Tempo','Long','Intervals','Race','Recovery','Other'].map(t=><option key={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>NOTES</div>
                                <input value={prForm.notes} onChange={e=>setPrForm(p=>({...p,notes:e.target.value}))} placeholder="Optional coaching note" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}} />
                              </div>
                              <div style={{gridColumn:'1/-1'}}>
                                <button className="btn btn-red" style={{fontSize:12,padding:'7px 16px'}} disabled={!prForm.distance_km||!prForm.scheduled_date||savingPr} onClick={()=>createPrescribedRun(m.id)}>
                                  {savingPr ? 'Saving…' : '+ Prescribe Run'}
                                </button>
                              </div>
                            </div>
                          </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                </>)}

                {adminTab === "content" && (<>
                  <div className="s-eye">Library</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:16}}>Workouts</div>
                  {/* Sub-tab toggle */}
                  <div style={{display:'flex',gap:8,marginBottom:24}}>
                    {TRAINING_CATEGORIES.map(({key:k,label:l,emoji}) => (
                      <button key={k} onClick={()=>{ setContentSubTab(k); if(k==='running') fetchAdminRunWorkouts(); }}
                        style={{padding:'8px 20px',borderRadius:6,border:`1px solid ${contentSubTab===k?'var(--red)':'var(--b2)'}`,background:contentSubTab===k?'var(--red)':'transparent',color:contentSubTab===k?'#fff':'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',fontWeight:contentSubTab===k?700:400}}>
                        {emoji} {l}
                      </button>
                    ))}
                  </div>

                  {/* ── RUNNING WORKOUT BUILDER ── */}
                  {contentSubTab === 'running' && (<>
                    <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:10,padding:24,marginBottom:28}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:16}}>New Run Workout</div>
                      {/* Mode toggle */}
                      <div style={{display:'flex',gap:8,marginBottom:16}}>
                        {[['single','Single Workout'],['program','Multi-Week Program']].map(([k,l])=>(
                          <button key={k} type="button" onClick={()=>setRunWForm(p=>({...p,mode:k}))}
                            style={{padding:'6px 16px',borderRadius:6,fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',border:`1px solid ${runWForm.mode===k?'var(--red)':'var(--b2)'}`,background:runWForm.mode===k?'rgba(146,41,32,0.2)':'transparent',color:runWForm.mode===k?'var(--red)':'var(--muted)',textTransform:'uppercase'}}>
                            {l}
                          </button>
                        ))}
                      </div>
                      <form onSubmit={createRunWorkout} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE *</div>
                          <input value={runWForm.title} onChange={e=>setRunWForm(p=>({...p,title:e.target.value}))} required placeholder={runWForm.mode==='program'?'e.g. 8-Week Base Build':'e.g. Tuesday Tempo'} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                        {runWForm.mode === 'program' ? (<>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION (WEEKS)</div>
                            <input type="number" min="1" value={runWForm.duration_weeks} onChange={e=>setRunWForm(p=>({...p,duration_weeks:e.target.value}))} placeholder="e.g. 8" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                            <select value={runWForm.tier} onChange={e=>setRunWForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                              <option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option>
                            </select>
                          </div>
                          <div style={{gridColumn:'1/-1'}}>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES / OVERVIEW</div>
                            <textarea value={runWForm.notes} onChange={e=>setRunWForm(p=>({...p,notes:e.target.value}))} rows={3} placeholder="Program overview, goals, structure…" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                          </div>
                        </>) : (<>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                            <select value={runWForm.run_type} onChange={e=>setRunWForm(p=>({...p,run_type:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                              {RUN_TYPES.map(t=><option key={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION</div>
                            <input value={runWForm.duration} onChange={e=>setRunWForm(p=>({...p,duration:e.target.value}))} placeholder="e.g. 45 min" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE</div>
                            <input value={runWForm.rpe} onChange={e=>setRunWForm(p=>({...p,rpe:e.target.value}))} placeholder="e.g. 6–7" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                            <select value={runWForm.tier} onChange={e=>setRunWForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                              <option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option>
                            </select>
                          </div>
                          <div style={{gridColumn:'1/-1'}}>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES / STRUCTURE</div>
                            <textarea value={runWForm.notes} onChange={e=>setRunWForm(p=>({...p,notes:e.target.value}))} rows={4} placeholder="e.g. 10 min warm-up, 3×10 min at threshold pace with 2 min recovery, 10 min cool-down" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                          </div>
                        </>)}
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VIDEO <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>optional</span></div>
                          <input value={runWForm.video_url} onChange={e=>setRunWForm(p=>({...p,video_url:e.target.value}))} placeholder="YouTube URL, or upload below" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box',marginBottom:6}} />
                          <label style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                            <input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setRunWForm(p=>({...p,video_url:url})))} />
                            {uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}
                          </label>
                        </div>
                        <div style={{gridColumn:'1/-1'}}>
                          <button type="submit" className="btn btn-red" disabled={savingRunWkt}>{savingRunWkt?'Creating…':'+ Create Run Workout'}</button>
                        </div>
                      </form>
                    </div>

                    {adminRunWorkouts.length === 0 && <div style={{color:'var(--muted)',fontSize:13}}>No run workouts yet.</div>}
                    {adminRunWorkouts.map(w=>{
                      const isProgram = !!w.duration_weeks;
                      const sessions = runWktSessions[w.id] || [];
                      const sessionWeeks = [...new Set(sessions.map(s=>s.week_number).filter(Boolean))].sort((a,b)=>a-b);
                      return (
                        <div key={w.id} style={{background:'var(--bg2)',border:`1px solid ${expandedRunWkt===w.id?'var(--red)':'var(--b2)'}`,borderRadius:10,marginBottom:12,overflow:'hidden'}}>
                          {/* Header row */}
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',cursor:'pointer'}}
                            onClick={()=>{ const next = expandedRunWkt===w.id?null:w.id; setExpandedRunWkt(next); if(next && isProgram && !runWktSessions[w.id]) fetchProgramSessions(w.id); }}>
                            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                              {isProgram && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 7px',borderRadius:3,textTransform:'uppercase'}}>Program</span>}
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:13,color:'var(--cream)'}}>{w.title}</span>
                              {!isProgram && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 7px',borderRadius:3,textTransform:'uppercase'}}>{w.run_type}</span>}
                              <span className={`tier-pill pill-${w.tier}`} style={{fontSize:10}}>{w.tier}</span>
                              {isProgram && w.duration_weeks && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>⏱ {w.duration_weeks} weeks</span>}
                              {!isProgram && w.duration && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>⏱ {w.duration}</span>}
                              {!isProgram && w.rpe && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>RPE {w.rpe}</span>}
                            </div>
                            <div style={{display:'flex',gap:8,alignItems:'center'}}>
                              <button className="btn" style={{fontSize:11,padding:'4px 12px',background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)'}} onClick={e=>{e.stopPropagation();deleteRunWorkout(w.id);}}>Delete</button>
                              <span style={{color:'var(--muted)',fontSize:13}}>{expandedRunWkt===w.id?'▲':'▼'}</span>
                            </div>
                          </div>
                          {expandedRunWkt===w.id && (
                            <div style={{borderTop:'1px solid var(--b2)',padding:20}}>
                              {/* Edit button */}
                              <div style={{marginBottom:16}}>
                                <button onClick={()=>{ if(editingRunWkt===w.id){setEditingRunWkt(null);}else{setEditingRunWkt(w.id);setEditRunWktForm({title:w.title,duration_weeks:w.duration_weeks?String(w.duration_weeks):'',tier:w.tier||'stride',notes:w.notes||'',run_type:w.run_type||'Easy',duration:w.duration||'',rpe:w.rpe||'',video_url:w.video_url||''});} }} style={{background:'none',border:'1px solid var(--b2)',color:editingRunWkt===w.id?'var(--red)':'var(--muted)',borderRadius:6,padding:'5px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>
                                  {editingRunWkt===w.id ? '✕ CANCEL EDIT' : '✏ EDIT DETAILS'}
                                </button>
                              </div>
                              {editingRunWkt===w.id && (
                                <div style={{background:'var(--black)',borderRadius:10,padding:16,marginBottom:20,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                                  <div style={{gridColumn:'1/-1'}}>
                                    <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE</div>
                                    <input value={editRunWktForm.title} onChange={e=>setEditRunWktForm(p=>({...p,title:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                  </div>
                                  {isProgram ? (<>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION (WEEKS)</div>
                                      <input type="number" min="1" value={editRunWktForm.duration_weeks} onChange={e=>setEditRunWktForm(p=>({...p,duration_weeks:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                    </div>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                                      <select value={editRunWktForm.tier} onChange={e=>setEditRunWktForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                        <option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option>
                                      </select>
                                    </div>
                                    <div style={{gridColumn:'1/-1'}}>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES / OVERVIEW</div>
                                      <textarea value={editRunWktForm.notes} onChange={e=>setEditRunWktForm(p=>({...p,notes:e.target.value}))} rows={3} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                                    </div>
                                  </>) : (<>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                                      <select value={editRunWktForm.run_type} onChange={e=>setEditRunWktForm(p=>({...p,run_type:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                        {RUN_TYPES.map(t=><option key={t}>{t}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                                      <select value={editRunWktForm.tier} onChange={e=>setEditRunWktForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                        <option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option>
                                      </select>
                                    </div>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION</div>
                                      <input value={editRunWktForm.duration} onChange={e=>setEditRunWktForm(p=>({...p,duration:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                    </div>
                                    <div>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE</div>
                                      <input value={editRunWktForm.rpe} onChange={e=>setEditRunWktForm(p=>({...p,rpe:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                    </div>
                                    <div style={{gridColumn:'1/-1'}}>
                                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES / STRUCTURE</div>
                                      <textarea value={editRunWktForm.notes} onChange={e=>setEditRunWktForm(p=>({...p,notes:e.target.value}))} rows={3} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                                    </div>
                                  </>)}
                                  <div style={{gridColumn:'1/-1'}}>
                                    <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VIDEO <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>optional</span></div>
                                    <input value={editRunWktForm.video_url} onChange={e=>setEditRunWktForm(p=>({...p,video_url:e.target.value}))} placeholder="YouTube URL, or upload below" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box',marginBottom:6}} />
                                    <label style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--bg2)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                                      <input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setEditRunWktForm(p=>({...p,video_url:url})))} />
                                      {uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}
                                    </label>
                                  </div>
                                  <div style={{gridColumn:'1/-1'}}>
                                    <button className="btn btn-red" style={{fontSize:12,padding:'8px 24px'}} onClick={()=>updateRunWorkout(w.id)}>Save Changes</button>
                                  </div>
                                </div>
                              )}
                              {/* Single workout notes */}
                              {!isProgram && !editingRunWkt && w.notes && (
                                <div style={{marginBottom:16}}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Workout Structure</div>
                                  <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.8,whiteSpace:'pre-wrap'}}>{w.notes}</div>
                                </div>
                              )}
                              {/* Program overview */}
                              {isProgram && !editingRunWkt && w.notes && (
                                <div style={{marginBottom:20}}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Overview</div>
                                  <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.7,whiteSpace:'pre-wrap'}}>{w.notes}</div>
                                </div>
                              )}
                              {/* Program sessions */}
                              {isProgram && (<>
                                {sessions.length === 0 && <div style={{color:'var(--muted)',fontSize:12,marginBottom:16}}>No sessions yet — add one below.</div>}
                                {sessionWeeks.length > 0 && sessionWeeks.map(wk=>(
                                  <div key={wk} style={{marginBottom:16}}>
                                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:8}}>Week {wk}</div>
                                    {sessions.filter(s=>s.week_number===wk).map(s=>(
                                      <div key={s.id} style={{background:'var(--black)',borderRadius:8,padding:'10px 14px',marginBottom:6,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                          {s.day_number && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>Day {s.day_number}{s.day_label?` · ${s.day_label}`:''}</span>}
                                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 6px',borderRadius:3,textTransform:'uppercase'}}>{s.run_type}</span>
                                          {s.duration && <span style={{fontSize:11,color:'var(--muted)'}}>⏱ {s.duration}</span>}
                                          {s.rpe && <span style={{fontSize:11,color:'var(--muted)'}}>RPE {s.rpe}</span>}
                                          {s.notes && <span style={{fontSize:11,color:'var(--muted)',fontStyle:'italic'}}>{s.notes}</span>}
                                        </div>
                                        <button onClick={()=>deleteRunSession(s.id,w.id)} style={{background:'none',border:'none',color:'var(--muted)',fontSize:11,cursor:'pointer',fontFamily:"'Poppins',sans-serif"}}>✕</button>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                                {sessions.filter(s=>!s.week_number).map(s=>(
                                  <div key={s.id} style={{background:'var(--black)',borderRadius:8,padding:'10px 14px',marginBottom:6,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                      <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 6px',borderRadius:3,textTransform:'uppercase'}}>{s.run_type}</span>
                                      {s.duration && <span style={{fontSize:11,color:'var(--muted)'}}>⏱ {s.duration}</span>}
                                      {s.rpe && <span style={{fontSize:11,color:'var(--muted)'}}>RPE {s.rpe}</span>}
                                      {s.notes && <span style={{fontSize:11,color:'var(--muted)',fontStyle:'italic'}}>{s.notes}</span>}
                                    </div>
                                    <button onClick={()=>deleteRunSession(s.id,w.id)} style={{background:'none',border:'none',color:'var(--muted)',fontSize:11,cursor:'pointer',fontFamily:"'Poppins',sans-serif"}}>✕</button>
                                  </div>
                                ))}
                                {/* CSV import */}
                                <div style={{display:'flex',gap:8,marginTop:16,marginBottom:8,flexWrap:'wrap'}}>
                                  <button type="button" onClick={downloadSessionTemplate} style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>↓ Download Template</button>
                                  <label style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>
                                    ↑ Import CSV
                                    <input type="file" accept=".csv,text/csv" style={{display:'none'}} onChange={e=>{ if(e.target.files[0]){ importRunSessions(w.id, e.target.files[0]); e.target.value=''; } }} />
                                  </label>
                                </div>
                                {/* Add session form */}
                                <div style={{marginTop:8,background:'var(--black)',borderRadius:10,padding:16}}>
                                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,cursor:'pointer'}} onClick={()=>setAddingSessionTo(addingSessionTo===w.id?null:w.id)}>
                                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase'}}>+ Add Session</div>
                                    <span style={{color:'var(--muted)',fontSize:12}}>{addingSessionTo===w.id?'▲':'▼'}</span>
                                  </div>
                                  {addingSessionTo===w.id && (
                                    <form onSubmit={e=>createRunSession(w.id,e)} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                                      <div>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>WEEK #</div>
                                        <input type="number" min="1" value={runSessionForm.week_number} onChange={e=>setRunSessionForm(p=>({...p,week_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}} />
                                      </div>
                                      <div>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>DAY #</div>
                                        <input type="number" min="1" value={runSessionForm.day_number} onChange={e=>setRunSessionForm(p=>({...p,day_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}} />
                                      </div>
                                      <div style={{gridColumn:'1/-1'}}>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>DAY LABEL</div>
                                        <input value={runSessionForm.day_label} onChange={e=>setRunSessionForm(p=>({...p,day_label:e.target.value}))} placeholder="e.g. Long Run, Rest Day…" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}} />
                                      </div>
                                      <div>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>TYPE</div>
                                        <select value={runSessionForm.run_type} onChange={e=>setRunSessionForm(p=>({...p,run_type:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}}>
                                          {RUN_TYPES.map(t=><option key={t}>{t}</option>)}
                                        </select>
                                      </div>
                                      <div>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>DURATION</div>
                                        <input value={runSessionForm.duration} onChange={e=>setRunSessionForm(p=>({...p,duration:e.target.value}))} placeholder="e.g. 60 min" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}} />
                                      </div>
                                      <div>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>RPE</div>
                                        <input value={runSessionForm.rpe} onChange={e=>setRunSessionForm(p=>({...p,rpe:e.target.value}))} placeholder="e.g. 6–7" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,boxSizing:'border-box'}} />
                                      </div>
                                      <div style={{gridColumn:'1/-1'}}>
                                        <div style={{fontSize:9,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:3}}>NOTES</div>
                                        <textarea value={runSessionForm.notes} onChange={e=>setRunSessionForm(p=>({...p,notes:e.target.value}))} rows={2} placeholder="Session details…" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'7px 10px',fontSize:12,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                                      </div>
                                      <div style={{gridColumn:'1/-1'}}>
                                        <button type="submit" className="btn btn-red" disabled={savingSession} style={{fontSize:12,padding:'7px 18px'}}>{savingSession?'Adding…':'+ Add Session'}</button>
                                      </div>
                                    </form>
                                  )}
                                </div>
                              </>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>)}

                  {/* ── STRENGTH WORKOUT BUILDER (existing) ── */}
                  {contentSubTab === 'strength' && (<>
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:10,padding:24,marginBottom:28}}>
                    {/* Mode toggle */}
                    <div style={{display:'flex',gap:8,marginBottom:18}}>
                      {[['single','Single Workout'],['program','Multi-Week Program']].map(([m,label])=>(
                        <button key={m} type="button" onClick={()=>setWForm(p=>({...p,mode:m}))}
                          style={{padding:'7px 18px',borderRadius:20,cursor:'pointer',fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:1,background:wForm.mode===m?'var(--red)':'var(--card)',border:`1px solid ${wForm.mode===m?'var(--red)':'var(--b2)'}`,color:wForm.mode===m?'var(--cream)':'var(--muted)',transition:'all 0.2s'}}>
                          {label.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <form onSubmit={createWorkout} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE *</div><input value={wForm.title} onChange={e=>setWForm(p=>({...p,title:e.target.value}))} required placeholder={wForm.mode==='program'?'e.g. 8-Week Strength Foundation':'e.g. Upper Body Strength'} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div>
                      {wForm.mode==='program' ? (<>
                        <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DURATION (WEEKS) *</div><input type="number" min="1" value={wForm.duration_weeks} onChange={e=>setWForm(p=>({...p,duration_weeks:e.target.value}))} required placeholder="e.g. 8" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div>
                        <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER *</div><select value={wForm.tier} onChange={e=>setWForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option></select></div>
                        <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VISIBILITY</div><select value={wForm.visibility} onChange={e=>setWForm(p=>({...p,visibility:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="public">Public (tier-based)</option><option value="private">Private (assigned only)</option></select></div>
                      </>) : (<>
                        <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DESCRIPTION</div><input value={wForm.description} onChange={e=>setWForm(p=>({...p,description:e.target.value}))} placeholder="Brief description..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div>
                        <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER *</div><select value={wForm.tier} onChange={e=>setWForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="stride">Stride</option><option value="elevate">Elevate</option><option value="apex">Apex</option></select></div>
                        <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VISIBILITY</div><select value={wForm.visibility} onChange={e=>setWForm(p=>({...p,visibility:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="public">Public (tier-based)</option><option value="private">Private (assigned only)</option></select></div>
                        <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>PHOTO URL</div><input value={wForm.photo_url} onChange={e=>setWForm(p=>({...p,photo_url:e.target.value}))} placeholder="https://..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div>
                        {wForm.tier==='apex'&&<><div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>WEEK #</div><input type="number" value={wForm.week_number} onChange={e=>setWForm(p=>({...p,week_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div><div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY #</div><input type="number" value={wForm.day_number} onChange={e=>setWForm(p=>({...p,day_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div><div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY LABEL</div><input value={wForm.day_label} onChange={e=>setWForm(p=>({...p,day_label:e.target.value}))} placeholder="e.g. Upper Body, Rest Day..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} /></div></>}
                      </>)}
                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BODY FOCUS</div><select value={wForm.body_focus} onChange={e=>setWForm(p=>({...p,body_focus:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="">— Not set</option>{BODY_FOCUS_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}</select></div>
                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EQUIPMENT</div><select value={wForm.equipment} onChange={e=>setWForm(p=>({...p,equipment:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="">— Not set</option>{EQUIPMENT_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}</select></div>
                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>GOAL</div><select value={wForm.training_goal} onChange={e=>setWForm(p=>({...p,training_goal:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}><option value="">— Not set</option>{TRAINING_GOAL_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}</select></div>
                      <div style={{gridColumn:'1/-1'}}><button type="submit" className="btn btn-red" disabled={savingWorkout}>{savingWorkout?'Creating…':wForm.mode==='program'?'+ Create Program':'+ Create Workout'}</button></div>
                    </form>
                  </div>
                  {adminWorkouts.length===0&&<div style={{color:'var(--muted)',fontSize:13}}>No workouts yet. Create your first one above.</div>}
                  {adminWorkouts.map(w=>{
                    const isProgram = !!w.duration_weeks;
                    const progExpanded = expandedStrengthProg===w.id;
                    if (isProgram) return (
                      <div key={w.id} style={{background:'var(--bg2)',border:`1px solid ${progExpanded?'var(--red)':'var(--b2)'}`,borderRadius:10,marginBottom:12,overflow:'hidden'}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',cursor:'pointer'}}
                          onClick={()=>{ const open=!progExpanded; setExpandedStrengthProg(open?w.id:null); if(open){fetchStrengthSessions(w.id);setAddingStrSessTo(w.id);} }}>
                          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                            <span style={{fontFamily:"'Poppins',sans-serif",fontSize:13,color:'var(--cream)',fontWeight:700}}>{w.title}</span>
                            <span style={{background:'rgba(146,41,32,0.2)',color:'var(--red)',fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'2px 8px',borderRadius:4,letterSpacing:1}}>PROGRAM</span>
                            <span className={`tier-pill pill-${w.tier}`} style={{fontSize:10}}>{w.tier}</span>
                            <span style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{w.duration_weeks} weeks</span>
                          </div>
                          <div style={{display:'flex',gap:8,alignItems:'center'}}>
                            <button className="btn" style={{fontSize:11,padding:'4px 12px',background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)'}} onClick={e=>{e.stopPropagation();deleteWorkout(w.id);}}>Delete</button>
                            <span style={{color:'var(--muted)',fontSize:13}}>{progExpanded?'▲':'▼'}</span>
                          </div>
                        </div>
                        {progExpanded && (
                          <div style={{borderTop:'1px solid var(--b2)',padding:20}}>
                            {/* Add session form */}
                            {addingStrSessTo===w.id && (
                              <form onSubmit={e=>createStrengthSession(w.id,e)} style={{background:'var(--black)',borderRadius:10,padding:16,marginBottom:20}}>
                                <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',marginBottom:12}}>ADD SESSION</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>WEEK #</div><input type="number" min="1" value={strSessForm.week_number} onChange={e=>setStrSessForm(p=>({...p,week_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} /></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY #</div><input type="number" min="1" value={strSessForm.day_number} onChange={e=>setStrSessForm(p=>({...p,day_number:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} /></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY LABEL</div><input value={strSessForm.day_label} onChange={e=>setStrSessForm(p=>({...p,day_label:e.target.value}))} placeholder="e.g. Upper Body" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} /></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DESCRIPTION</div><input value={strSessForm.description} onChange={e=>setStrSessForm(p=>({...p,description:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} /></div>
                                </div>
                                <div style={{display:'flex',gap:8}}>
                                  <button type="submit" className="btn btn-red" disabled={savingStrSess} style={{fontSize:12,padding:'7px 20px'}}>{savingStrSess?'Adding…':'+ Add Session'}</button>
                                  <button type="button" className="btn btn-ghost" style={{fontSize:12,padding:'7px 14px'}} onClick={()=>setAddingStrSessTo(null)}>Cancel</button>
                                </div>
                              </form>
                            )}
                            {!addingStrSessTo && <button className="btn btn-ghost" style={{fontSize:12,padding:'6px 16px',marginBottom:16}} onClick={()=>setAddingStrSessTo(w.id)}>+ Add Session</button>}
                            {/* Session list */}
                            {(wktSessions[w.id]||[]).length===0 && <div style={{color:'var(--muted)',fontSize:13,marginBottom:12}}>No sessions yet — add one above.</div>}
                            {(wktSessions[w.id]||[]).map(sess=>(
                              <div key={sess.id} style={{background:'var(--bg2)',border:`1px solid ${selectedAdminWkt===sess.id?'var(--red)':'var(--b1)'}`,borderRadius:8,marginBottom:8,overflow:'hidden'}}>
                                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',cursor:'pointer'}} onClick={()=>selectedAdminWkt===sess.id?setSelectedAdminWkt(null):openAdminWorkout(sess.id)}>
                                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                    {sess.week_number&&<span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--red)',letterSpacing:1}}>WK {sess.week_number} · DAY {sess.day_number}</span>}
                                    <span style={{color:'var(--cream)',fontSize:13}}>{sess.day_label||sess.title}</span>
                                    {sess.description&&<span style={{color:'var(--muted)',fontSize:11}}>{sess.description}</span>}
                                  </div>
                                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                                    <button className="btn" style={{fontSize:10,padding:'3px 10px',background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)'}} onClick={e=>{e.stopPropagation();deleteWorkout(sess.id);setWktSessions(p=>({...p,[w.id]:(p[w.id]||[]).filter(s=>s.id!==sess.id)}));}}>Delete</button>
                                    <span style={{color:'var(--muted)',fontSize:12}}>{selectedAdminWkt===sess.id?'▲':'▼'}</span>
                                  </div>
                                </div>
                                {selectedAdminWkt===sess.id&&<div style={{borderTop:'1px solid var(--b1)',padding:16}}>

                        {/* EDIT SESSION DETAILS */}
                        <div style={{marginBottom:16}}>
                          <button onClick={()=>{ if(editingWorkout===sess.id){setEditingWorkout(null);}else{setEditingWorkout(sess.id);setEditWktForm({title:sess.title,description:sess.description||'',tier:sess.tier,photo_url:sess.photo_url||'',week_number:sess.week_number?String(sess.week_number):'',day_number:sess.day_number?String(sess.day_number):'',day_label:sess.day_label||'',visibility:sess.visibility||'public',body_focus:sess.body_focus||'',equipment:sess.equipment||'',training_goal:sess.training_goal||''});}}} style={{background:'none',border:'1px solid var(--b2)',color:editingWorkout===sess.id?'var(--red)':'var(--muted)',borderRadius:6,padding:'5px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>
                            {editingWorkout===sess.id ? '✕ CANCEL EDIT' : '✏ EDIT SESSION DETAILS'}
                          </button>
                        </div>
                        {editingWorkout===sess.id && (
                          <div style={{background:'var(--black)',borderRadius:10,padding:16,marginBottom:20,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE</div>
                              <input value={editWktForm.title} onChange={e=>setEditWktForm(p=>({...p,title:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DESCRIPTION</div>
                              <textarea value={editWktForm.description} onChange={e=>setEditWktForm(p=>({...p,description:e.target.value}))} rows={2} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box'}} />
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                              <select value={editWktForm.tier} onChange={e=>setEditWktForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="stride">Stride</option>
                                <option value="elevate">Elevate</option>
                                <option value="apex">Apex</option>
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VISIBILITY</div>
                              <select value={editWktForm.visibility} onChange={e=>setEditWktForm(p=>({...p,visibility:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="public">Public (tier-based)</option>
                                <option value="private">Private (assigned only)</option>
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BODY FOCUS</div>
                              <select value={editWktForm.body_focus} onChange={e=>setEditWktForm(p=>({...p,body_focus:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="">— Not set</option>
                                {BODY_FOCUS_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EQUIPMENT</div>
                              <select value={editWktForm.equipment} onChange={e=>setEditWktForm(p=>({...p,equipment:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="">— Not set</option>
                                {EQUIPMENT_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>GOAL</div>
                              <select value={editWktForm.training_goal} onChange={e=>setEditWktForm(p=>({...p,training_goal:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="">— Not set</option>
                                {TRAINING_GOAL_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COVER PHOTO</div>
                              <label style={{display:'flex',alignItems:'center',gap:8,background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:6,padding:'8px 10px',cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                                <input type="file" accept="image/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadWorkoutPhoto(e.target.files[0],sess.id)} />
                                {uploadingWktPhoto ? 'Uploading...' : editWktForm.photo_url ? '✓ Photo set' : '+ Upload photo'}
                              </label>
                              {editWktForm.photo_url && <img src={editWktForm.photo_url} alt="cover" style={{width:'100%',height:60,objectFit:'cover',borderRadius:6,marginTop:6}} />}
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>WEEK #</div>
                              <input type="number" value={editWktForm.week_number} onChange={e=>setEditWktForm(p=>({...p,week_number:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY #</div>
                              <input type="number" value={editWktForm.day_number} onChange={e=>setEditWktForm(p=>({...p,day_number:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY LABEL (e.g. Upper Body)</div>
                              <input value={editWktForm.day_label} onChange={e=>setEditWktForm(p=>({...p,day_label:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <button className="btn btn-red" style={{fontSize:12,padding:'8px 24px'}} onClick={()=>updateWorkout(sess.id)}>Save Session</button>
                            </div>
                          </div>
                        )}

                        {(()=>{
                          const sorted = [...adminExercises].sort((a,b) => a.sort_order - b.sort_order);
                          const groupCounts = {};
                          sorted.forEach(ex => { if (ex.superset_group) { groupCounts[ex.superset_group] = (groupCounts[ex.superset_group]||0)+1; } });
                          const groupIdx = {};
                          return sorted.map(ex => {
                            let label = '';
                            if (ex.superset_group) { groupIdx[ex.superset_group] = (groupIdx[ex.superset_group]||0)+1; label = ex.superset_group+groupIdx[ex.superset_group]; }
                            const isEditing = editingExercise === ex.id;
                            return (<div key={ex.id}
                              draggable
                              onDragStart={()=>{ dragExSrcRef.current = ex.id; }}
                              onDragOver={e=>{ e.preventDefault(); setDragOverExId(ex.id); }}
                              onDragLeave={()=>setDragOverExId(null)}
                              onDrop={()=>{ reorderExercises(dragExSrcRef.current, ex.id); dragExSrcRef.current=null; setDragOverExId(null); }}
                              style={{borderBottom:'1px solid var(--b1)',borderLeft: dragOverExId===ex.id ? '3px solid var(--red)' : '3px solid transparent',transition:'border-color 0.1s'}}>
                              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{cursor:'grab',color:'var(--b2)',fontSize:14,lineHeight:1,userSelect:'none'}} title="Drag to reorder">⠿</span>
                                  {label && <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'2px 7px',borderRadius:4,fontWeight:700}}>{label}</span>}
                                  {ex.exercise_type&&ex.exercise_type!=='working'&&<span style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'2px 6px',borderRadius:3,background:ex.exercise_type==='warmup'?'rgba(90,158,111,0.2)':'rgba(90,120,200,0.2)',color:ex.exercise_type==='warmup'?'#2f7a4a':'#3f6bb0',textTransform:'uppercase'}}>{ex.exercise_type==='warmup'?'Warm-up':'Cool-down'}</span>}
                                  <span style={{color:'var(--cream)',fontSize:13}}>{ex.name}</span>
                                  {ex.sets&&<span style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{ex.sets}×{ex.prescribed_reps||'?'}</span>}
                                  {ex.prescribed_rpe!=null&&<span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--red)',letterSpacing:1}}>RPE {ex.prescribed_rpe}</span>}
                                  {ex.video_url&&<span style={{fontSize:11,color:'var(--muted)'}}>· ▶</span>}
                                </div>
                                <div style={{display:'flex',gap:8}}>
                                  <button onClick={()=>{ if(isEditing){setEditingExercise(null);}else{setEditingExercise(ex.id);setEditExForm({name:ex.name,video_url:ex.video_url||'',sort_order:String(ex.sort_order||0),sets:ex.sets?String(ex.sets):'',prescribed_reps:ex.prescribed_reps||'',prescribed_rpe:ex.prescribed_rpe!=null?String(ex.prescribed_rpe):'',prescribed_notes:ex.prescribed_notes||'',superset_group:ex.superset_group||'',exercise_type:ex.exercise_type||'working'});}}} style={{background:'none',border:'none',color:isEditing?'var(--red)':'var(--muted)',cursor:'pointer',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>{isEditing?'CANCEL':'EDIT'}</button>
                                  <button onClick={()=>deleteExercise(ex.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>✕</button>
                                </div>
                              </div>
                              {isEditing && (
                                <div style={{background:'var(--black)',borderRadius:8,padding:16,marginBottom:12,display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                                  <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EXERCISE NAME</div><input value={editExForm.name} onChange={e=>setEditExForm(p=>({...p,name:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                  <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUTUBE URL <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>or upload below</span></div><input value={editExForm.video_url} onChange={e=>setEditExForm(p=>({...p,video_url:e.target.value}))} placeholder="https://youtube.com/..." style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:6}} /><label style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--bg2)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}><input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setEditExForm(p=>({...p,video_url:url})))} />{uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}</label></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SETS</div><input type="number" value={editExForm.sets} onChange={e=>setEditExForm(p=>({...p,sets:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>REPS / DURATION</div><input value={editExForm.prescribed_reps} onChange={e=>setEditExForm(p=>({...p,prescribed_reps:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                  <div>
                                    <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                                    <select value={editExForm.exercise_type} onChange={e=>setEditExForm(p=>({...p,exercise_type:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                      <option value="warmup">Warm-up</option>
                                      <option value="working">Working Set</option>
                                      <option value="cooldown">Cool-down</option>
                                    </select>
                                  </div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE (1–10)</div><input type="number" min="1" max="10" step="0.5" value={editExForm.prescribed_rpe} onChange={e=>setEditExForm(p=>({...p,prescribed_rpe:e.target.value}))} placeholder="e.g. 7" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                  <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUPERSET</div>
                                    <select value={editExForm.superset_group} onChange={e=>setEditExForm(p=>({...p,superset_group:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                      <option value="">— None</option>
                                      {['A','B','C','D','E','F'].map(g=><option key={g} value={g}>{g}</option>)}
                                    </select>
                                  </div>
                                  <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COACHING NOTES</div><input value={editExForm.prescribed_notes} onChange={e=>setEditExForm(p=>({...p,prescribed_notes:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                  <div style={{gridColumn:'1/-1'}}><button className="btn btn-red" style={{fontSize:12,padding:'8px 20px'}} onClick={()=>updateExercise(ex.id)}>Save Changes</button></div>
                                </div>
                              )}
                            </div>);
                          });
                        })()}
                        {adminExercises.length===0&&<div style={{color:'var(--muted)',fontSize:12,marginBottom:12}}>No exercises yet.</div>}
                        <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                          <button type="button" onClick={downloadExerciseTemplate} style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>↓ Download Template</button>
                          <label style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>
                            ↑ Import CSV
                            <input type="file" accept=".csv,text/csv" style={{display:'none'}} onChange={e=>{ if(e.target.files[0]){ importExercises(selectedAdminWkt, e.target.files[0]); e.target.value=''; } }} />
                          </label>
                        </div>
                        <form onSubmit={createExercise} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:16}}>
                          <div style={{gridColumn:'1/-1'}}>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>PICK FROM LIBRARY <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>(optional)</span></div>
                            <select onChange={e=>{ const ex=exerciseLibrary.find(x=>x.id===e.target.value); if(ex){setEForm(p=>({...p,name:ex.name,video_url:ex.video_url||''}));} e.target.value=''; }}
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--red)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:8}}>
                              <option value="">— Search library to pre-fill —</option>
                              {[...new Set(exerciseLibrary.map(e=>e.muscle_group))].sort().map(mg=>(
                                <optgroup key={mg} label={mg}>
                                  {exerciseLibrary.filter(e=>e.muscle_group===mg).map(ex=>(
                                    <option key={ex.id} value={ex.id}>{ex.name}{ex.category==='Rehab'?' (Rehab)':''}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                          <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EXERCISE NAME *</div><input value={eForm.name} onChange={e=>setEForm(p=>({...p,name:e.target.value}))} required placeholder="e.g. Barbell Back Squat" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUTUBE URL <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>or upload below</span></div><input value={eForm.video_url} onChange={e=>setEForm(p=>({...p,video_url:e.target.value}))} placeholder="https://youtube.com/watch?v=..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:6}} /><label style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}><input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setEForm(p=>({...p,video_url:url})))} />{uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}</label></div>
                          <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SETS</div><input type="number" value={eForm.sets} onChange={e=>setEForm(p=>({...p,sets:e.target.value}))} placeholder="3" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>REPS / DURATION</div><input value={eForm.prescribed_reps} onChange={e=>setEForm(p=>({...p,prescribed_reps:e.target.value}))} placeholder="8–10 or 30s" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                            <select value={eForm.exercise_type} onChange={e=>setEForm(p=>({...p,exercise_type:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                              <option value="warmup">Warm-up</option>
                              <option value="working">Working Set</option>
                              <option value="cooldown">Cool-down</option>
                            </select>
                          </div>
                          <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE (1–10)</div><input type="number" min="1" max="10" step="0.5" value={eForm.prescribed_rpe} onChange={e=>setEForm(p=>({...p,prescribed_rpe:e.target.value}))} placeholder="e.g. 7" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div>
                            <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUPERSET GROUP</div>
                            <select value={eForm.superset_group} onChange={e=>setEForm(p=>({...p,superset_group:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                              <option value="">— None</option>
                              {['A','B','C','D','E','F'].map(g=><option key={g} value={g}>{g}</option>)}
                            </select>
                          </div>
                          <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>ORDER</div><input type="number" value={eForm.sort_order} onChange={e=>setEForm(p=>({...p,sort_order:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COACHING NOTES</div><input value={eForm.prescribed_notes} onChange={e=>setEForm(p=>({...p,prescribed_notes:e.target.value}))} placeholder="e.g. Keep chest up, drive through heels" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                          <div style={{gridColumn:'1/-1'}}><button type="submit" className="btn btn-red" style={{fontSize:12,padding:'8px 20px'}}>+ Add Exercise</button></div>
                        </form>
                                </div>}
                              </div>
                            ))}
                          </div>)}
                        </div>
                      );
                      return (
                        <div key={w.id} style={{background:'var(--bg2)',border:`1px solid ${selectedAdminWkt===w.id?'var(--red)':'var(--b2)'}`,borderRadius:10,marginBottom:10,overflow:'hidden'}}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',cursor:'pointer'}} onClick={()=>selectedAdminWkt===w.id?setSelectedAdminWkt(null):openAdminWorkout(w.id)}>
                            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:13,color:'var(--cream)',fontWeight:600}}>{w.title}</span>
                              <span className={`tier-pill pill-${w.tier}`} style={{fontSize:10}}>{w.tier}</span>
                              {w.week_number&&<span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>WK {w.week_number} · DAY {w.day_number}</span>}
                              {w.day_label&&<span style={{color:'var(--muted)',fontSize:11}}>{w.day_label}</span>}
                            </div>
                            <div style={{display:'flex',gap:8,alignItems:'center'}}>
                              <button className="btn" style={{fontSize:11,padding:'4px 12px',background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)'}} onClick={e=>{e.stopPropagation();deleteWorkout(w.id);}}>Delete</button>
                              <span style={{color:'var(--muted)',fontSize:13}}>{selectedAdminWkt===w.id?'▲':'▼'}</span>
                            </div>
                          </div>
                          {selectedAdminWkt===w.id&&<div style={{borderTop:'1px solid var(--b2)',padding:20}}>
                            {/* EDIT WORKOUT DETAILS */}
                            <div style={{marginBottom:16}}>
                              <button onClick={()=>{ if(editingWorkout===w.id){setEditingWorkout(null);}else{setEditingWorkout(w.id);setEditWktForm({title:w.title,description:w.description||'',tier:w.tier,photo_url:w.photo_url||'',week_number:w.week_number?String(w.week_number):'',day_number:w.day_number?String(w.day_number):'',day_label:w.day_label||'',visibility:w.visibility||'public',body_focus:w.body_focus||'',equipment:w.equipment||'',training_goal:w.training_goal||''});}}} style={{background:'none',border:'1px solid var(--b2)',color:editingWorkout===w.id?'var(--red)':'var(--muted)',borderRadius:6,padding:'5px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>
                                {editingWorkout===w.id ? '✕ CANCEL EDIT' : '✏ EDIT WORKOUT DETAILS'}
                              </button>
                            </div>
                            {editingWorkout===w.id && (
                              <div style={{background:'var(--black)',borderRadius:10,padding:16,marginBottom:20,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                                <div style={{gridColumn:'1/-1'}}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE</div>
                                  <input value={editWktForm.title} onChange={e=>setEditWktForm(p=>({...p,title:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                </div>
                                <div style={{gridColumn:'1/-1'}}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DESCRIPTION</div>
                                  <textarea value={editWktForm.description} onChange={e=>setEditWktForm(p=>({...p,description:e.target.value}))} rows={2} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,resize:'none',fontFamily:'inherit',boxSizing:'border-box'}} />
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                                  <select value={editWktForm.tier} onChange={e=>setEditWktForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                    <option value="stride">Stride</option>
                                    <option value="elevate">Elevate</option>
                                    <option value="apex">Apex</option>
                                  </select>
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VISIBILITY</div>
                                  <select value={editWktForm.visibility} onChange={e=>setEditWktForm(p=>({...p,visibility:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                    <option value="public">Public (tier-based)</option>
                                    <option value="private">Private (assigned only)</option>
                                  </select>
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BODY FOCUS</div>
                                  <select value={editWktForm.body_focus} onChange={e=>setEditWktForm(p=>({...p,body_focus:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                    <option value="">— Not set</option>
                                    {BODY_FOCUS_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EQUIPMENT</div>
                                  <select value={editWktForm.equipment} onChange={e=>setEditWktForm(p=>({...p,equipment:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                    <option value="">— Not set</option>
                                    {EQUIPMENT_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>GOAL</div>
                                  <select value={editWktForm.training_goal} onChange={e=>setEditWktForm(p=>({...p,training_goal:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                    <option value="">— Not set</option>
                                    {TRAINING_GOAL_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COVER PHOTO</div>
                                  <label style={{display:'flex',alignItems:'center',gap:8,background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:6,padding:'8px 10px',cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                                    <input type="file" accept="image/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadWorkoutPhoto(e.target.files[0],w.id)} />
                                    {uploadingWktPhoto ? 'Uploading...' : editWktForm.photo_url ? '✓ Photo set' : '+ Upload photo'}
                                  </label>
                                  {editWktForm.photo_url && <img src={editWktForm.photo_url} alt="cover" style={{width:'100%',height:60,objectFit:'cover',borderRadius:6,marginTop:6}} />}
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>WEEK #</div>
                                  <input type="number" value={editWktForm.week_number} onChange={e=>setEditWktForm(p=>({...p,week_number:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                </div>
                                <div>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY #</div>
                                  <input type="number" value={editWktForm.day_number} onChange={e=>setEditWktForm(p=>({...p,day_number:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                </div>
                                <div style={{gridColumn:'1/-1'}}>
                                  <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>DAY LABEL (e.g. Upper Body)</div>
                                  <input value={editWktForm.day_label} onChange={e=>setEditWktForm(p=>({...p,day_label:e.target.value}))} placeholder="Optional" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                                </div>
                                <div style={{gridColumn:'1/-1'}}>
                                  <button className="btn btn-red" style={{fontSize:12,padding:'8px 24px'}} onClick={()=>updateWorkout(w.id)}>Save Workout</button>
                                </div>
                              </div>
                            )}
                            {(()=>{
                              const sorted = [...adminExercises].sort((a,b) => a.sort_order - b.sort_order);
                              const groupCounts = {};
                              sorted.forEach(ex => { if (ex.superset_group) { groupCounts[ex.superset_group] = (groupCounts[ex.superset_group]||0)+1; } });
                              const groupIdx = {};
                              return sorted.map(ex => {
                                let label = '';
                                if (ex.superset_group) { groupIdx[ex.superset_group] = (groupIdx[ex.superset_group]||0)+1; label = ex.superset_group+groupIdx[ex.superset_group]; }
                                const isEditing = editingExercise === ex.id;
                                return (<div key={ex.id}
                                  draggable
                                  onDragStart={()=>{ dragExSrcRef.current = ex.id; }}
                                  onDragOver={e=>{ e.preventDefault(); setDragOverExId(ex.id); }}
                                  onDragLeave={()=>setDragOverExId(null)}
                                  onDrop={()=>{ reorderExercises(dragExSrcRef.current, ex.id); dragExSrcRef.current=null; setDragOverExId(null); }}
                                  style={{borderBottom:'1px solid var(--b1)',borderLeft: dragOverExId===ex.id ? '3px solid var(--red)' : '3px solid transparent',transition:'border-color 0.1s'}}>
                                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                      <span style={{cursor:'grab',color:'var(--b2)',fontSize:14,lineHeight:1,userSelect:'none'}} title="Drag to reorder">⠿</span>
                                      {label && <span style={{background:'var(--red)',color:'var(--on-red)',fontSize:10,fontFamily:"'Poppins',sans-serif",padding:'2px 7px',borderRadius:4,fontWeight:700}}>{label}</span>}
                                      {ex.exercise_type&&ex.exercise_type!=='working'&&<span style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'2px 6px',borderRadius:3,background:ex.exercise_type==='warmup'?'rgba(90,158,111,0.2)':'rgba(90,120,200,0.2)',color:ex.exercise_type==='warmup'?'#2f7a4a':'#3f6bb0',textTransform:'uppercase'}}>{ex.exercise_type==='warmup'?'Warm-up':'Cool-down'}</span>}
                                      <span style={{color:'var(--cream)',fontSize:13}}>{ex.name}</span>
                                      {ex.sets&&<span style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{ex.sets}×{ex.prescribed_reps||'?'}</span>}
                                      {ex.prescribed_rpe!=null&&<span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--red)',letterSpacing:1}}>RPE {ex.prescribed_rpe}</span>}
                                      {ex.video_url&&<span style={{fontSize:11,color:'var(--muted)'}}>· ▶</span>}
                                    </div>
                                    <div style={{display:'flex',gap:8}}>
                                      <button onClick={()=>{ if(isEditing){setEditingExercise(null);}else{setEditingExercise(ex.id);setEditExForm({name:ex.name,video_url:ex.video_url||'',sort_order:String(ex.sort_order||0),sets:ex.sets?String(ex.sets):'',prescribed_reps:ex.prescribed_reps||'',prescribed_rpe:ex.prescribed_rpe!=null?String(ex.prescribed_rpe):'',prescribed_notes:ex.prescribed_notes||'',superset_group:ex.superset_group||'',exercise_type:ex.exercise_type||'working'});}}} style={{background:'none',border:'none',color:isEditing?'var(--red)':'var(--muted)',cursor:'pointer',fontSize:12,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>{isEditing?'CANCEL':'EDIT'}</button>
                                      <button onClick={()=>deleteExercise(ex.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>✕</button>
                                    </div>
                                  </div>
                                  {isEditing && (
                                    <div style={{background:'var(--black)',borderRadius:8,padding:16,marginBottom:12,display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                                      <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EXERCISE NAME</div><input value={editExForm.name} onChange={e=>setEditExForm(p=>({...p,name:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                      <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUTUBE URL <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>or upload below</span></div><input value={editExForm.video_url} onChange={e=>setEditExForm(p=>({...p,video_url:e.target.value}))} placeholder="https://youtube.com/..." style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:6}} /><label style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--bg2)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}><input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setEditExForm(p=>({...p,video_url:url})))} />{uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}</label></div>
                                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SETS</div><input type="number" value={editExForm.sets} onChange={e=>setEditExForm(p=>({...p,sets:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>REPS / DURATION</div><input value={editExForm.prescribed_reps} onChange={e=>setEditExForm(p=>({...p,prescribed_reps:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                      <div>
                                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                                        <select value={editExForm.exercise_type} onChange={e=>setEditExForm(p=>({...p,exercise_type:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                          <option value="warmup">Warm-up</option>
                                          <option value="working">Working Set</option>
                                          <option value="cooldown">Cool-down</option>
                                        </select>
                                      </div>
                                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE (1–10)</div><input type="number" min="1" max="10" step="0.5" value={editExForm.prescribed_rpe} onChange={e=>setEditExForm(p=>({...p,prescribed_rpe:e.target.value}))} placeholder="e.g. 7" style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                      <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUPERSET</div>
                                        <select value={editExForm.superset_group} onChange={e=>setEditExForm(p=>({...p,superset_group:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                          <option value="">— None</option>
                                          {['A','B','C','D','E','F'].map(g=><option key={g} value={g}>{g}</option>)}
                                        </select>
                                      </div>
                                      <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COACHING NOTES</div><input value={editExForm.prescribed_notes} onChange={e=>setEditExForm(p=>({...p,prescribed_notes:e.target.value}))} style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                                      <div style={{gridColumn:'1/-1'}}><button className="btn btn-red" style={{fontSize:12,padding:'8px 20px'}} onClick={()=>updateExercise(ex.id)}>Save Changes</button></div>
                                    </div>
                                  )}
                                </div>);
                              });
                            })()}
                            {adminExercises.length===0&&<div style={{color:'var(--muted)',fontSize:12,marginBottom:12}}>No exercises yet.</div>}
                            <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                              <button type="button" onClick={downloadExerciseTemplate} style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>↓ Download Template</button>
                              <label style={{background:'transparent',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer',textTransform:'uppercase'}}>
                                ↑ Import CSV
                                <input type="file" accept=".csv,text/csv" style={{display:'none'}} onChange={e=>{ if(e.target.files[0]){ importExercises(selectedAdminWkt, e.target.files[0]); e.target.value=''; } }} />
                              </label>
                            </div>
                            <form onSubmit={createExercise} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:16}}>
                              <div style={{gridColumn:'1/-1'}}>
                                <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>PICK FROM LIBRARY <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>(optional)</span></div>
                                <select onChange={e=>{ const ex=exerciseLibrary.find(x=>x.id===e.target.value); if(ex){setEForm(p=>({...p,name:ex.name,video_url:ex.video_url||''}));} e.target.value=''; }}
                                  style={{width:'100%',background:'var(--black)',border:'1px solid var(--red)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:8}}>
                                  <option value="">— Search library to pre-fill —</option>
                                  {[...new Set(exerciseLibrary.map(e=>e.muscle_group))].sort().map(mg=>(
                                    <optgroup key={mg} label={mg}>
                                      {exerciseLibrary.filter(e=>e.muscle_group===mg).map(ex=>(
                                        <option key={ex.id} value={ex.id}>{ex.name}{ex.category==='Rehab'?' (Rehab)':''}</option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </div>
                              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>EXERCISE NAME *</div><input value={eForm.name} onChange={e=>setEForm(p=>({...p,name:e.target.value}))} required placeholder="e.g. Barbell Back Squat" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUTUBE URL <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>or upload below</span></div><input value={eForm.video_url} onChange={e=>setEForm(p=>({...p,video_url:e.target.value}))} placeholder="https://youtube.com/watch?v=..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box',marginBottom:6}} /><label style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}><input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setEForm(p=>({...p,video_url:url})))} />{uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}</label></div>
                              <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SETS</div><input type="number" value={eForm.sets} onChange={e=>setEForm(p=>({...p,sets:e.target.value}))} placeholder="3" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>REPS / DURATION</div><input value={eForm.prescribed_reps} onChange={e=>setEForm(p=>({...p,prescribed_reps:e.target.value}))} placeholder="8–10 or 30s" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div>
                                <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TYPE</div>
                                <select value={eForm.exercise_type} onChange={e=>setEForm(p=>({...p,exercise_type:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                  <option value="warmup">Warm-up</option>
                                  <option value="working">Working Set</option>
                                  <option value="cooldown">Cool-down</option>
                                </select>
                              </div>
                              <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>RPE (1–10)</div><input type="number" min="1" max="10" step="0.5" value={eForm.prescribed_rpe} onChange={e=>setEForm(p=>({...p,prescribed_rpe:e.target.value}))} placeholder="e.g. 7" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div>
                                <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUPERSET GROUP</div>
                                <select value={eForm.superset_group} onChange={e=>setEForm(p=>({...p,superset_group:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}}>
                                  <option value="">— None</option>
                                  {['A','B','C','D','E','F'].map(g=><option key={g} value={g}>{g}</option>)}
                                </select>
                              </div>
                              <div><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>ORDER</div><input type="number" value={eForm.sort_order} onChange={e=>setEForm(p=>({...p,sort_order:e.target.value}))} placeholder="1" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>COACHING NOTES</div><input value={eForm.prescribed_notes} onChange={e=>setEForm(p=>({...p,prescribed_notes:e.target.value}))} placeholder="e.g. Keep chest up, drive through heels" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:12,boxSizing:'border-box'}} /></div>
                              <div style={{gridColumn:'1/-1'}}><button type="submit" className="btn btn-red" style={{fontSize:12,padding:'8px 20px'}}>+ Add Exercise</button></div>
                            </form>
                          </div>}
                        </div>
                      );
                    })}
                  </>)}
                </>)}

                {adminTab === "fuel" && (<>
                  <div className="s-eye">Content</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Nutrition</div>
                  <div className="s-sub" style={{marginBottom:24}}>Create and manage nutrition advice for your members.</div>

                  {/* CREATE FORM */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:24,marginBottom:28}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:18}}>New Item</div>
                    <form onSubmit={createNutritionItem} style={{display:'flex',flexDirection:'column',gap:14}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE *</div>
                          <input value={nForm.title} onChange={e=>setNForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Pre-Race Carb Loading" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SECTION</div>
                          <select value={nForm.section} onChange={e=>{ const section=e.target.value; setNForm(p=>({...p,section,category:categoriesForSection(section)[0]||'General'})); }} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                            {NUTRITION_SECTIONS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>CATEGORY</div>
                          <select value={nForm.category} onChange={e=>setNForm(p=>({...p,category:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                            {categoriesForSection(nForm.section).map(c=><option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                          <select value={nForm.tier} onChange={e=>setNForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                            <option value="stride">Stride (free)</option>
                            <option value="elevate">Elevate</option>
                            <option value="apex">Apex</option>
                          </select>
                        </div>
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUMMARY <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9,color:'var(--muted)'}}>— shown on the card</span></div>
                          <input value={nForm.summary} onChange={e=>setNForm(p=>({...p,summary:e.target.value}))} placeholder="One-line description members see before opening..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BODY CONTENT <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9,color:'var(--muted)'}}>— shown when opened</span></div>
                          <textarea value={nForm.content} onChange={e=>setNForm(p=>({...p,content:e.target.value}))} rows={5} placeholder="Full advice, tips, protocols..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box',lineHeight:1.7}} />
                        </div>
                        <div style={{gridColumn:'1/-1'}}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:8}}>ATTACHMENT <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9,color:'var(--muted)'}}>— PDF, image, video, or any file</span></div>
                          <label style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:8,cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                            <input type="file" accept="*/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadNutritionFile(e.target.files[0],'new')} />
                            {uploadingNutritionFile ? '⏳ Uploading...' : nForm.file_name ? `✓ ${nForm.file_name}` : '+ Attach File (PDF, image, video, etc.)'}
                          </label>
                          {nForm.file_url && (
                            <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8}}>
                              <span style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{nForm.file_type?.toUpperCase()}</span>
                              <button type="button" onClick={()=>setNForm(p=>({...p,file_url:'',file_type:'',file_name:''}))} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:12}}>✕ Remove</button>
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SORT ORDER</div>
                          <input type="number" value={nForm.sort_order} onChange={e=>setNForm(p=>({...p,sort_order:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                        </div>
                      </div>
                      <button type="submit" className="btn btn-red" disabled={savingNutrition} style={{padding:'10px 0',fontSize:13}}>
                        {savingNutrition ? 'Saving...' : '+ Add Item'}
                      </button>
                    </form>
                  </div>

                  {/* ITEM LIST */}
                  {nutritionItems.length === 0 && <div style={{color:'var(--muted)',fontSize:13}}>No items yet. Create your first one above.</div>}
                  {nutritionItems.map(n => (
                    <div key={n.id} style={{background:'var(--bg2)',border:`1px solid ${editingNutritionItem===n.id?'var(--red)':'var(--b2)'}`,borderRadius:10,marginBottom:10,overflow:'hidden'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{n.title}</span>
                          <span className={`tier-pill pill-${n.tier}`} style={{marginLeft:8,fontSize:10}}>{n.tier}</span>
                          <span style={{marginLeft:8,fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{NUTRITION_SECTIONS.find(s=>s.key===n.section)?.label || 'Sports Performance'} · {n.category}</span>
                          {n.file_type && <span style={{marginLeft:8,fontSize:10,color:'var(--muted)'}}>· {n.file_type.toUpperCase()}</span>}
                        </div>
                        <div style={{display:'flex',gap:8,flexShrink:0}}>
                          <button onClick={()=>{ if(editingNutritionItem===n.id){setEditingNutritionItem(null);}else{setEditingNutritionItem(n.id);setEditNForm({title:n.title,summary:n.summary||'',content:n.content||'',section:n.section||'sports_performance',category:n.category,tier:n.tier,file_url:n.file_url||'',file_type:n.file_type||'',file_name:n.file_name||'',sort_order:String(n.sort_order||0)});}}}
                            style={{background:'none',border:'1px solid var(--b2)',color:editingNutritionItem===n.id?'var(--red)':'var(--muted)',borderRadius:6,padding:'4px 12px',fontSize:11,fontFamily:"'Poppins',sans-serif",cursor:'pointer'}}>
                            {editingNutritionItem===n.id?'CANCEL':'EDIT'}
                          </button>
                          <button onClick={()=>deleteNutritionItem(n.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>✕</button>
                        </div>
                      </div>
                      {editingNutritionItem===n.id && (
                        <div style={{borderTop:'1px solid var(--b2)',padding:20,display:'flex',flexDirection:'column',gap:14}}>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE</div>
                              <input value={editNForm.title} onChange={e=>setEditNForm(p=>({...p,title:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SECTION</div>
                              <select value={editNForm.section} onChange={e=>{ const section=e.target.value; setEditNForm(p=>({...p,section,category:categoriesForSection(section)[0]||'General'})); }} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                {NUTRITION_SECTIONS.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>CATEGORY</div>
                              <select value={editNForm.category} onChange={e=>setEditNForm(p=>({...p,category:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                {categoriesForSection(editNForm.section).map(c=><option key={c}>{c}</option>)}
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TIER</div>
                              <select value={editNForm.tier} onChange={e=>setEditNForm(p=>({...p,tier:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                                <option value="stride">Stride (free)</option>
                                <option value="elevate">Elevate</option>
                                <option value="apex">Apex</option>
                              </select>
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SUMMARY</div>
                              <input value={editNForm.summary} onChange={e=>setEditNForm(p=>({...p,summary:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BODY CONTENT</div>
                              <textarea value={editNForm.content} onChange={e=>setEditNForm(p=>({...p,content:e.target.value}))} rows={5} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                            </div>
                            <div style={{gridColumn:'1/-1'}}>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:6}}>ATTACHMENT</div>
                              <label style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:8,cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                                <input type="file" accept="*/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadNutritionFile(e.target.files[0],n.id)} />
                                {uploadingNutritionFile ? '⏳ Uploading...' : editNForm.file_name ? `✓ ${editNForm.file_name}` : '+ Replace / Attach File'}
                              </label>
                              {editNForm.file_url && (
                                <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{fontSize:11,color:'var(--muted)',fontFamily:"'Poppins',sans-serif"}}>{editNForm.file_type?.toUpperCase()} · {editNForm.file_name}</span>
                                  <button type="button" onClick={()=>setEditNForm(p=>({...p,file_url:'',file_type:'',file_name:''}))} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:12}}>✕</button>
                                </div>
                              )}
                            </div>
                            <div>
                              <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>SORT ORDER</div>
                              <input type="number" value={editNForm.sort_order} onChange={e=>setEditNForm(p=>({...p,sort_order:e.target.value}))} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                            </div>
                          </div>
                          <button className="btn btn-red" disabled={savingNutrition} style={{fontSize:12,padding:'9px 24px',alignSelf:'flex-start'}} onClick={()=>updateNutritionItem(n.id)}>
                            {savingNutrition ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>)}

                {adminTab === "reports" && (<>
                  <div className="s-eye">Confidential</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Metabolic Test Reports</div>
                  <div className="s-sub" style={{marginBottom:24}}>Upload a member's test results. Only that member (and admins) can ever see it.</div>

                  {/* UPLOAD FORM */}
                  <form onSubmit={createReport} style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:24,marginBottom:28,display:'flex',flexDirection:'column',gap:14}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase'}}>New Report</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>MEMBER *</div>
                        <select value={reportForm.user_id} onChange={e=>setReportForm(p=>({...p,user_id:e.target.value}))} required
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                          <option value="">Select a member…</option>
                          {adminMembers.map(m=><option key={m.id} value={m.id}>{m.email}{m.is_admin?' (admin)':''}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TEST DATE *</div>
                        <input type="date" value={reportForm.test_date} onChange={e=>setReportForm(p=>({...p,test_date:e.target.value}))} required
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                      <div style={{gridColumn:'1/-1'}}>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TITLE <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>— optional, e.g. "Spring 2026 Metabolic Test"</span></div>
                        <input value={reportForm.title} onChange={e=>setReportForm(p=>({...p,title:e.target.value}))} placeholder="Report title"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                    </div>

                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginTop:4}}>Progress Metrics <span style={{color:'var(--muted)',letterSpacing:0,textTransform:'none'}}>— optional, feeds the member's dashboard graph</span></div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12}}>
                      {REPORT_METRICS.map(m => (
                        <div key={m.key}>
                          <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>{m.label.toUpperCase()} <span style={{opacity:0.6}}>({m.unit})</span></div>
                          {m.key === 'cho_fat_ratio_rest' ? (
                            <input type="text" inputMode="decimal" placeholder="e.g. 60:40" value={reportForm[m.key]} onChange={e=>setReportForm(p=>({...p,[m.key]:e.target.value}))}
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                          ) : (
                            <input type="number" step="any" value={reportForm[m.key]} onChange={e=>setReportForm(p=>({...p,[m.key]:e.target.value}))}
                              style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}} />
                          )}
                        </div>
                      ))}
                    </div>

                    <div>
                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>— optional, shown to the member</span></div>
                      <textarea value={reportForm.notes} onChange={e=>setReportForm(p=>({...p,notes:e.target.value}))} rows={3}
                        style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                    </div>

                    <div>
                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:8}}>REPORT FILE * <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>— PDF or image</span></div>
                      <label style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:8,cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                        <input type="file" accept="application/pdf,image/*" style={{display:'none'}} onChange={e=>setReportForm(p=>({...p,file:e.target.files[0]||null}))} />
                        {reportForm.file ? `✓ ${reportForm.file.name}` : '+ Attach report file'}
                      </label>
                    </div>

                    <div>
                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:8}}>WALKTHROUGH VIDEO <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>— optional</span></div>
                      <label style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:8,cursor:'pointer',color:'var(--muted)',fontSize:12,fontFamily:"'Poppins',sans-serif"}}>
                        <input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setReportForm(p=>({...p,video_url:url})))} />
                        {uploadingVideo ? '⏳ Uploading...' : reportForm.video_url ? '✓ Video attached' : '+ Attach a results walkthrough video'}
                      </label>
                    </div>

                    <button type="submit" className="btn btn-red" disabled={savingReport||!reportForm.user_id||!reportForm.test_date||!reportForm.file} style={{alignSelf:'flex-start',padding:'10px 24px',fontSize:13}}>
                      {uploadingReportFile ? 'Uploading…' : savingReport ? 'Saving…' : 'Upload Report'}
                    </button>
                  </form>

                  {/* LIST */}
                  {adminReportsLoading ? (
                    <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
                  ) : adminReports.length === 0 ? (
                    <div style={{color:'var(--muted)',fontSize:13}}>No reports uploaded yet.</div>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {adminReports.map(r => (
                        <div key={r.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                              <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{adminMembers.find(m=>m.id===r.user_id)?.email || 'Unknown member'}</span>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',letterSpacing:1}}>{new Date(r.test_date+'T12:00:00').toLocaleDateString('en-CA',{year:'numeric',month:'short',day:'numeric'})}</span>
                              <span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--muted)',background:'var(--bg2)',padding:'2px 6px',borderRadius:4}}>{r.file_type === 'pdf' ? '📄 PDF' : '🖼 IMAGE'}</span>
                        {r.video_url && <span style={{fontSize:10,fontFamily:"'Poppins',sans-serif",color:'var(--muted)',background:'var(--bg2)',padding:'2px 6px',borderRadius:4}}>🎥 VIDEO</span>}
                            </div>
                            {r.title && <div style={{color:'var(--cream)',fontSize:13,marginBottom:2}}>{r.title}</div>}
                            <div style={{color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                              {REPORT_METRICS.filter(m=>r[m.key]!=null).map(m=>`${m.label} ${r[m.key]}`).join(' · ') || 'No metrics recorded'}
                            </div>
                          </div>
                          <div style={{display:'flex',gap:8,flexShrink:0}}>
                            <button onClick={()=>openReportFile(r)} style={{background:'none',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'6px 14px',fontSize:11,fontFamily:"'Poppins',sans-serif",cursor:'pointer'}}>VIEW</button>
                            <button onClick={()=>deleteReport(r)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* VIEWER */}
                  {openReport && (
                    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={()=>{setOpenReport(null);setReportSignedUrl('');}}>
                      <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:20,maxWidth:700,width:'100%',maxHeight:'85vh',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                          <div style={{color:'var(--cream)',fontWeight:700,fontSize:14}}>{openReport.title || openReport.file_name}</div>
                          <button onClick={()=>{setOpenReport(null);setReportSignedUrl('');}} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:20}}>✕</button>
                        </div>
                        {!reportSignedUrl ? (
                          <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading…</div>
                        ) : openReport.file_type === 'pdf' ? (
                          <iframe src={reportSignedUrl} title={openReport.file_name} style={{width:'100%',height:500,border:'1px solid var(--b2)',borderRadius:8}} />
                        ) : (
                          <img src={reportSignedUrl} alt={openReport.file_name} style={{width:'100%',borderRadius:8}} />
                        )}
                      </div>
                    </div>
                  )}
                </>)}

                {adminTab === "messages" && (<>
                  <div className="s-eye">Private</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Messages</div>
                  <div className="s-sub" style={{marginBottom:24}}>Direct threads with members.</div>

                  {pushPermission !== 'granted' && pushPermission !== 'unsupported' && (
                    <div style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'12px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                      <div style={{flex:1,minWidth:200,color:'var(--muted)',fontSize:12}}>Turn on notifications so you don't miss a member message.</div>
                      <button onClick={enablePushNotifications} disabled={pushBusy} className="btn btn-red" style={{fontSize:11,padding:'7px 16px'}}>{pushBusy?'…':'Enable Notifications'}</button>
                    </div>
                  )}

                  {!adminDmSelected ? (<>
                    <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24}}>
                      <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>START A CONVERSATION</div>
                      <select onChange={e=>{ if(e.target.value){ openAdminDmThread(e.target.value); e.target.value=''; } }}
                        style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}>
                        <option value="">Select a member…</option>
                        {adminMembers.filter(m=>!m.is_admin).map(m=><option key={m.id} value={m.id}>{m.email}</option>)}
                      </select>
                    </div>

                    {adminDmThreads.length === 0 ? (
                      <div style={{color:'var(--muted)',fontSize:13}}>No conversations yet.</div>
                    ) : (
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {adminDmThreads.map(t => (
                          <div key={t.member_id} onClick={()=>openAdminDmThread(t.member_id)}
                            style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'14px 18px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}
                            onMouseEnter={e=>e.currentTarget.style.borderColor='var(--red)'}
                            onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b2)'}>
                            <div style={{minWidth:0}}>
                              <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{t.email}</span>
                                {t.unreadCount > 0 && <span style={{background:'var(--red)',color:'var(--on-red)',borderRadius:10,fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'1px 6px',fontWeight:700}}>{t.unreadCount}</span>}
                              </div>
                              <div style={{color:'var(--muted)',fontSize:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:2}}>{t.lastMessage}</div>
                            </div>
                            <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',flexShrink:0}}>{timeAgo(t.lastAt)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>) : (<>
                    <button className="btn btn-ghost" style={{marginBottom:16,fontSize:13}} onClick={()=>setAdminDmSelected(null)}>← Back to threads</button>
                    <div style={{color:'var(--cream)',fontWeight:700,fontSize:14,marginBottom:14}}>{adminMembers.find(m=>m.id===adminDmSelected)?.email || 'Member'}</div>
                    <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16,minHeight:200}}>
                      {dmLoading ? (
                        <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>Loading...</div>
                      ) : dmMessages.length === 0 ? (
                        <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:40}}>No messages yet.</div>
                      ) : dmMessages.map(m => {
                        const mine = m.sender_id === session.user.id;
                        return (
                          <div key={m.id} style={{display:'flex',justifyContent:mine?'flex-end':'flex-start'}}>
                            <div style={{maxWidth:'75%',background:mine?'var(--red)':'var(--card)',color:mine?'var(--on-red)':'var(--cream)',border:mine?'none':'1px solid var(--b2)',borderRadius:12,padding:'10px 14px'}}>
                              <div style={{fontSize:13,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{m.body}</div>
                              <div style={{fontSize:9,fontFamily:"'Poppins',sans-serif",letterSpacing:1,opacity:0.7,marginTop:4}}>{timeAgo(m.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <form onSubmit={e=>sendDmMessage(adminDmSelected, e)} style={{display:'flex',gap:10,alignItems:'flex-end'}}>
                      <textarea value={newDmMessage} onChange={e=>setNewDmMessage(e.target.value)}
                        placeholder="Reply to this member..." rows={2}
                        style={{flex:1,background:'var(--card)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:8,padding:'10px 12px',fontSize:13,resize:'none',fontFamily:'inherit'}} />
                      <button type="submit" className="btn btn-red" disabled={sendingDm||!newDmMessage.trim()} style={{whiteSpace:'nowrap',padding:'10px 18px'}}>
                        {sendingDm ? '...' : 'Send'}
                      </button>
                    </form>
                  </>)}
                </>)}

                {adminTab === "reminders" && (<>
                  <div className="s-eye">Notifications</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Daily Reminders</div>
                  <div className="s-sub" style={{marginBottom:24}}>Members get one push notification a day at the time they choose. The category rotates daily (move, meal, motivation) and a random active message from it is sent.</div>

                  <form onSubmit={addMotivationMsg} style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:20,marginBottom:24,display:'flex',flexDirection:'column',gap:12}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase'}}>New Message</div>
                    <select value={newMotivation.category} onChange={e=>setNewMotivation(p=>({...p,category:e.target.value}))}
                      style={{background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13}}>
                      <option value="move">Move / be active</option>
                      <option value="meal">Healthier meal choice</option>
                      <option value="motivation">General motivation</option>
                    </select>
                    <textarea value={newMotivation.body} onChange={e=>setNewMotivation(p=>({...p,body:e.target.value}))} rows={2} placeholder="Keep it short — this is a push notification."
                      style={{background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit'}} />
                    <button type="submit" className="btn btn-red" disabled={savingMotivation||!newMotivation.body.trim()} style={{alignSelf:'flex-start',padding:'9px 22px',fontSize:12}}>{savingMotivation?'Adding…':'+ Add Message'}</button>
                  </form>

                  {[['move','Move / be active'],['meal','Healthier meal choice'],['motivation','General motivation']].map(([cat,label]) => (
                    <div key={cat} style={{marginBottom:22}}>
                      <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:10}}>{label}</div>
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {motivationMsgs.filter(m=>m.category===cat).length === 0 && <div style={{color:'var(--muted)',fontSize:12}}>None yet.</div>}
                        {motivationMsgs.filter(m=>m.category===cat).map(m => (
                          <div key={m.id} style={{background:'var(--card)',border:'1px solid var(--b2)',borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',gap:10,opacity:m.active?1:0.55}}>
                            <div style={{flex:1,color:'var(--cream)',fontSize:13}}>{m.body}</div>
                            <button onClick={()=>toggleMotivationMsg(m)} style={{background:'none',border:'1px solid var(--b2)',color:'var(--muted)',borderRadius:6,padding:'3px 10px',fontSize:10,fontFamily:"'Poppins',sans-serif",cursor:'pointer'}}>{m.active?'ACTIVE':'PAUSED'}</button>
                            <button onClick={()=>deleteMotivationMsg(m.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>)}

                {adminTab === "squad" && (<>
                  <div className="s-eye">Profile</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Owner / Head Coach Bio</div>
                  <div className="s-sub" style={{marginBottom:24}}>What members see on the "Owner / Head Coach" tab.</div>

                  {/* TEXT FIELDS */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:24,marginBottom:24}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:18}}>Bio Details</div>
                    <div style={{display:'flex',flexDirection:'column',gap:14}}>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>YOUR NAME</div>
                        <input value={bioForm.name} onChange={e=>setBioForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Adam Harrington" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'9px 12px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>TAGLINE / TITLE</div>
                        <input value={bioForm.tagline} onChange={e=>setBioForm(p=>({...p,tagline:e.target.value}))} placeholder="e.g. Head Coach · Endurance Specialist" style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'9px 12px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>BIO</div>
                        <textarea value={bioForm.bio} onChange={e=>setBioForm(p=>({...p,bio:e.target.value}))} rows={8} placeholder="Tell your members about yourself — your background, coaching philosophy, achievements..." style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'9px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box',lineHeight:1.6}} />
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>CREDENTIALS &amp; CERTIFICATIONS <span style={{color:'var(--muted)',fontWeight:400,letterSpacing:0,textTransform:'none',fontSize:9}}>(one per line)</span></div>
                        <textarea value={bioForm.credentials} onChange={e=>setBioForm(p=>({...p,credentials:e.target.value}))} rows={4} placeholder={"CSCS - Certified Strength & Conditioning Specialist\nRunning Coach Level 2\n10x Marathon Finisher"} style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'9px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box',lineHeight:1.7}} />
                      </div>
                      <button className="btn btn-red" disabled={savingBio} style={{padding:'11px 0',fontSize:13}} onClick={saveCoachBio}>
                        {savingBio ? 'Saving...' : 'Save Bio'}
                      </button>
                    </div>
                  </div>

                  {/* PHOTO MANAGEMENT */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:12,padding:24}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:18}}>Photos</div>
                    <div style={{fontSize:12,color:'var(--muted)',marginBottom:14,lineHeight:1.5}}>The first photo is used as your hero/profile image. Add more for the gallery below it.</div>
                    <label style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'12px 0',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:8,color:'var(--cream)',cursor:'pointer',fontWeight:600,fontSize:13,marginBottom:20}}>
                      <input type="file" accept="image/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadBioPhoto(e.target.files[0])} />
                      {uploadingBioPhoto ? '⏳ Uploading...' : '+ Upload Photo'}
                    </label>
                    {coachBio?.photo_urls && coachBio.photo_urls.length > 0 ? (
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:10}}>
                        {coachBio.photo_urls.map((url,i) => (
                          <div key={i} style={{position:'relative',borderRadius:8,overflow:'hidden',border:i===0?'2px solid var(--red)':'1px solid var(--b2)'}}>
                            <img src={url} alt={`bio ${i}`} style={{width:'100%',aspectRatio:'1',objectFit:'cover',display:'block'}} />
                            {i===0 && <div style={{position:'absolute',top:4,left:4,background:'var(--red)',color:'var(--on-red)',fontSize:9,fontFamily:"'Poppins',sans-serif",padding:'2px 6px',borderRadius:3,letterSpacing:1}}>HERO</div>}
                            {i!==0 && (
                              <button onClick={async()=>{
                                const reordered = [url, ...coachBio.photo_urls.filter((_,j)=>j!==i)];
                                await supabase.from('coach_bio').upsert({ id:1, photo_urls: reordered });
                                setCoachBio(p=>({...p, photo_urls: reordered}));
                              }} style={{position:'absolute',bottom:4,left:4,background:'rgba(146,41,32,0.9)',border:'none',color:'#fff',borderRadius:3,fontSize:8,fontFamily:"'Poppins',sans-serif",letterSpacing:1,padding:'2px 5px',cursor:'pointer'}}>SET HERO</button>
                            )}
                            <button onClick={()=>deleteBioPhoto(url)} style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.7)',border:'none',color:'#fff',borderRadius:'50%',width:22,height:22,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>✕</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{color:'var(--muted)',fontSize:12,textAlign:'center',padding:20}}>No photos yet. Upload one above.</div>
                    )}
                  </div>
                </>)}

                {adminTab === "calendar" && (<>
                  <div className="s-eye">Schedule</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Workout Calendar</div>
                  <div className="s-sub" style={{marginBottom:20}}>Prescribe workouts to Elevate &amp; Apex members on specific days.</div>

                  {/* Member selector */}
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:6}}>MEMBER</div>
                    <select value={calMember} onChange={e=>{ setCalMember(e.target.value); setScheduledWorkouts([]); setAssigningCalDay(null); if(e.target.value) fetchAdminCalScheduled(e.target.value, calWeekStart); }}
                      style={{width:'100%',maxWidth:340,background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13}}>
                      <option value="">— Select a member —</option>
                      {adminMembers.filter(m=>m.tier==='elevate'||m.tier==='apex').map(m=>(
                        <option key={m.id} value={m.id}>{m.email} ({m.tier})</option>
                      ))}
                    </select>
                  </div>

                  {/* Week navigation */}
                  {(()=>{
                    const days = getWeekDays(calWeekStart);
                    const label = `${days[0].toLocaleDateString('en-CA',{month:'short',day:'numeric'})} – ${days[6].toLocaleDateString('en-CA',{month:'short',day:'numeric',year:'numeric'})}`;
                    const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                    return (<>
                      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
                        <button className="btn btn-ghost" style={{fontSize:12,padding:'6px 14px'}} onClick={()=>{ const d=new Date(calWeekStart); d.setDate(d.getDate()-7); setCalWeekStart(d); if(calMember) fetchAdminCalScheduled(calMember,d); }}>← Prev</button>
                        <span style={{fontFamily:"'Poppins',sans-serif",fontSize:12,color:'var(--cream)',letterSpacing:1}}>{label}</span>
                        <button className="btn btn-ghost" style={{fontSize:12,padding:'6px 14px'}} onClick={()=>{ const d=new Date(calWeekStart); d.setDate(d.getDate()+7); setCalWeekStart(d); if(calMember) fetchAdminCalScheduled(calMember,d); }}>Next →</button>
                      </div>

                      {/* 7-day grid */}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:6,marginBottom:24}}>
                        {days.map((day,di) => {
                          const ds = toDateStr(day);
                          const isToday = ds === new Date().toISOString().slice(0,10);
                          const dayWorkouts = scheduledWorkouts.filter(s => s.scheduled_date === ds);
                          const isAssigning = assigningCalDay === ds;
                          return (
                            <div key={ds} style={{background:'var(--card)',border:`1px solid ${isToday?'var(--red)':'var(--b2)'}`,borderRadius:8,padding:8,minHeight:90,display:'flex',flexDirection:'column',gap:4}}>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--muted)',textTransform:'uppercase'}}>{DAY_LABELS[di]}</div>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:14,fontWeight:700,color:isToday?'var(--red)':'var(--cream)',marginBottom:2}}>{day.getDate()}</div>
                              {dayWorkouts.map(sw=>(
                                <div key={sw.id} style={{background:sw.completed?'rgba(90,158,111,0.15)':'var(--bg2)',border:`1px solid ${sw.completed?'#2f7a4a':'var(--b2)'}`,borderRadius:5,padding:'4px 6px',fontSize:10,display:'flex',alignItems:'center',justifyContent:'space-between',gap:4}}>
                                  <span style={{color:'var(--cream)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{sw.workouts?.title}</span>
                                  {sw.completed && <span style={{color:'#2f7a4a',fontSize:9,flexShrink:0}}>✓</span>}
                                  {!sw.completed && <button onClick={()=>removeScheduledWorkout(sw.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11,padding:'0 2px',flexShrink:0,lineHeight:1}}>✕</button>}
                                </div>
                              ))}
                              {calMember && !isAssigning && (
                                <button onClick={()=>{ setAssigningCalDay(ds); setAssigningCalWkt(''); }} style={{background:'none',border:'1px dashed var(--b2)',color:'var(--muted)',borderRadius:5,padding:'3px 0',fontSize:10,cursor:'pointer',marginTop:'auto'}}>+ Add</button>
                              )}
                              {calMember && isAssigning && (
                                <div style={{marginTop:4,display:'flex',flexDirection:'column',gap:4}}>
                                  <select value={assigningCalWkt} onChange={e=>setAssigningCalWkt(e.target.value)}
                                    style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--red)',color:'var(--cream)',borderRadius:4,padding:'4px 6px',fontSize:10}}>
                                    <option value="">— Workout —</option>
                                    {adminWorkouts.filter(w=>w.tier==='elevate'||w.tier==='apex'||w.visibility==='private').map(w=>(
                                      <option key={w.id} value={w.id}>{w.title}</option>
                                    ))}
                                  </select>
                                  <div style={{display:'flex',gap:4}}>
                                    <button className="btn btn-red" style={{fontSize:10,padding:'4px 8px',flex:1}} disabled={!assigningCalWkt||savingCal} onClick={scheduleWorkoutForMember}>{savingCal?'…':'Add'}</button>
                                    <button className="btn btn-ghost" style={{fontSize:10,padding:'4px 8px'}} onClick={()=>setAssigningCalDay(null)}>✕</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Completion log */}
                      {scheduledWorkouts.filter(s=>s.completed).length > 0 && (
                        <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:10,padding:16}}>
                          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'#2f7a4a',textTransform:'uppercase',marginBottom:10}}>Completed This Week</div>
                          {scheduledWorkouts.filter(s=>s.completed).map(sw=>(
                            <div key={sw.id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:'1px solid var(--b1)'}}>
                              <span style={{color:'#2f7a4a',fontSize:13}}>✓</span>
                              <span style={{color:'var(--cream)',fontSize:13,flex:1}}>{sw.workouts?.title}</span>
                              <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>
                                {new Date(sw.scheduled_date+'T12:00:00').toLocaleDateString('en-CA',{weekday:'short',month:'short',day:'numeric'})}
                              </span>
                              {sw.completed_at && <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)'}}>done {new Date(sw.completed_at).toLocaleTimeString('en-CA',{hour:'2-digit',minute:'2-digit'})}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </>);
                  })()}
                </>)}

                {adminTab === "library" && (<>
                  <div className="s-eye">Exercises</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Exercise Library</div>
                  <div className="s-sub" style={{marginBottom:20}}>All exercises are available in the workout builder. Sorted by muscle group.</div>

                  {/* Add exercise form */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--b2)',borderRadius:10,padding:20,marginBottom:24}}>
                    <div style={{fontFamily:"'Poppins',sans-serif",fontSize:11,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase',marginBottom:14}}>Add Exercise</div>
                    <form onSubmit={createLibraryExercise} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                      <div style={{gridColumn:'1/-1'}}>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NAME *</div>
                        <input value={newLibEx.name} onChange={e=>setNewLibEx(p=>({...p,name:e.target.value}))} required placeholder="e.g. Romanian Deadlift"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>MUSCLE GROUP</div>
                        <select value={newLibEx.muscle_group} onChange={e=>setNewLibEx(p=>({...p,muscle_group:e.target.value}))}
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                          {['Chest','Back','Legs','Shoulders','Arms','Core','Full Body','Glutes','Calves'].map(m=><option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>CATEGORY</div>
                        <select value={newLibEx.category} onChange={e=>setNewLibEx(p=>({...p,category:e.target.value}))}
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}}>
                          <option>Strength</option><option>Rehab</option>
                        </select>
                      </div>
                      <div style={{gridColumn:'1/-1'}}>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>VIDEO URL <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>optional</span></div>
                        <input value={newLibEx.video_url} onChange={e=>setNewLibEx(p=>({...p,video_url:e.target.value}))} placeholder="YouTube or Shorts URL, or upload below"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box',marginBottom:8}} />
                        <label style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'var(--black)',border:'2px dashed var(--b2)',borderRadius:6,cursor:'pointer',color:'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif"}}>
                          <input type="file" accept="video/*" style={{display:'none'}} onChange={e=>e.target.files[0]&&uploadVideoFile(e.target.files[0],url=>setNewLibEx(p=>({...p,video_url:url})))} />
                          {uploadingVideo ? '⏳ Uploading...' : '+ Or upload a video file'}
                        </label>
                      </div>
                      <div style={{gridColumn:'1/-1'}}>
                        <div style={{fontSize:10,color:'var(--muted)',fontFamily:"'Poppins',sans-serif",letterSpacing:1,marginBottom:4}}>NOTES <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:9}}>optional</span></div>
                        <input value={newLibEx.notes} onChange={e=>setNewLibEx(p=>({...p,notes:e.target.value}))} placeholder="Coaching cue or description"
                          style={{width:'100%',background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 10px',fontSize:13,boxSizing:'border-box'}} />
                      </div>
                      <div style={{gridColumn:'1/-1'}}><button type="submit" className="btn btn-red" disabled={savingLibEx}>{savingLibEx?'Saving…':'+ Add to Library'}</button></div>
                    </form>
                  </div>

                  {/* Filters */}
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
                    {['All','Chest','Back','Legs','Shoulders','Arms','Core','Full Body','Glutes','Calves'].map(m=>(
                      <button key={m} onClick={()=>setLibMuscle(m)} style={{padding:'5px 12px',borderRadius:20,border:`1px solid ${libMuscle===m?'var(--red)':'var(--b2)'}`,background:libMuscle===m?'var(--red)':'transparent',color:libMuscle===m?'#fff':'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>{m}</button>
                    ))}
                    {['All','Strength','Rehab'].map(c=>(
                      <button key={c} onClick={()=>setLibCat(c)} style={{padding:'5px 12px',borderRadius:20,border:`1px solid ${libCat===c?'var(--red)':'var(--b2)'}`,background:libCat===c?'rgba(146,41,32,0.3)':'transparent',color:libCat===c?'var(--red)':'var(--muted)',fontSize:11,fontFamily:"'Poppins',sans-serif",letterSpacing:1,cursor:'pointer'}}>{c}</button>
                    ))}
                  </div>
                  <input value={libSearch} onChange={e=>setLibSearch(e.target.value)} placeholder="Search exercises..."
                    style={{width:'100%',background:'var(--bg2)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,boxSizing:'border-box',marginBottom:16}} />

                  {/* Exercise list */}
                  {(()=>{
                    const filtered = exerciseLibrary.filter(ex =>
                      (libMuscle==='All' || ex.muscle_group===libMuscle) &&
                      (libCat==='All' || ex.category===libCat) &&
                      (!libSearch || ex.name.toLowerCase().includes(libSearch.toLowerCase()))
                    );
                    if (!filtered.length) return <div style={{color:'var(--muted)',fontSize:13,padding:'20px 0'}}>No exercises match your filters.</div>;
                    const groups = [...new Set(filtered.map(e=>e.muscle_group))].sort();
                    return groups.map(mg => (
                      <div key={mg} style={{marginBottom:20}}>
                        <div style={{fontFamily:"'Poppins',sans-serif",fontSize:10,letterSpacing:2,color:'var(--red)',textTransform:'uppercase',marginBottom:8}}>{mg}</div>
                        {filtered.filter(e=>e.muscle_group===mg).map(ex=>(
                          <div key={ex.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'var(--card)',border:'1px solid var(--b2)',borderRadius:8,marginBottom:6,gap:10}}>
                            <div style={{flex:1}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                <span style={{color:'var(--cream)',fontSize:13,fontWeight:600}}>{ex.name}</span>
                                <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:ex.category==='Rehab'?'#b85f26':'var(--muted)',background:ex.category==='Rehab'?'rgba(224,123,57,0.1)':'var(--bg2)',padding:'2px 6px',borderRadius:3}}>{ex.category}</span>
                              </div>
                              {ex.notes && <div style={{color:'var(--muted)',fontSize:11,marginTop:3,fontStyle:'italic'}}>{ex.notes}</div>}
                              {ex.video_url && <div style={{color:'var(--red)',fontSize:10,marginTop:3,fontFamily:"'Poppins',sans-serif",letterSpacing:1}}>▶ VIDEO</div>}
                            </div>
                            <button onClick={()=>deleteLibraryExercise(ex.id)} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16,padding:'2px 6px',flexShrink:0}}>✕</button>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}

                  {exerciseLibrary.length === 0 && (
                    <div style={{color:'var(--muted)',fontSize:13,textAlign:'center',padding:30}}>
                      Library is empty. Add exercises above or run the migration SQL to import your existing workout exercises.
                    </div>
                  )}
                </>)}

                {adminTab === "analytics" && (<>
                  <div className="s-eye">Insights</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Analytics</div>
                  <div className="s-sub">Membership and revenue overview.</div>
                  {(()=>{
                    const nonAdmin = adminMembers.filter(m=>!m.is_admin);
                    const apex = nonAdmin.filter(m=>m.tier==='apex').length;
                    const elevate = nonAdmin.filter(m=>m.tier==='elevate').length;
                    const stride = nonAdmin.filter(m=>m.tier==='stride').length;
                    const total = nonAdmin.length;
                    const mrr = (apex * 99.99) + (elevate * 49.99);
                    return (<>
                      <div className="astats">
                        <div className="astat"><div className="astat-val">{total}</div><div className="astat-lbl">Total Members</div></div>
                        <div className="astat"><div className="astat-val">CA${mrr.toFixed(0)}</div><div className="astat-lbl">MRR</div></div>
                        <div className="astat"><div className="astat-val">{apex + elevate}</div><div className="astat-lbl">Paid Members</div></div>
                      </div>
                      <div className="chart-box">
                        <div className="chart-mono">Tier Distribution</div>
                        {[['Stride',stride],['Elevate',elevate],['Apex',apex]].map(([n,c]) => (
                          <div key={n} style={{marginBottom:16}}>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:2,color:'var(--muted)',textTransform:'uppercase'}}>
                              <span>{n}</span><span>{c} member{c!==1?'s':''}</span>
                            </div>
                            <div style={{height:5,background:'var(--bg3)',borderRadius:1}}>
                              <div style={{height:'100%',width:total>0?`${(c/total)*100}%`:'0%',background:'var(--red)',borderRadius:1,transition:'width 0.6s'}} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>);
                  })()}
                </>)}

                {adminTab === "qa" && (<>
                  <div className="s-eye">Community</div>
                  <div className="s-title" style={{fontSize:24,marginBottom:4}}>Q & A</div>
                  <div className="s-sub" style={{marginBottom:24}}>Questions from members. Click a question to reply.</div>
                  {qaPosts.filter(p=>!p.is_admin_post).length === 0 && <div style={{color:'var(--muted)',fontSize:13}}>No member questions yet.</div>}
                  {qaPosts.filter(p=>!p.is_admin_post).map(p=>(
                    <div key={p.id} style={{background:'var(--bg2)',border:`1px solid ${openQaPost?.id===p.id?'var(--red)':'var(--b2)'}`,borderRadius:10,marginBottom:10,overflow:'hidden'}}>
                      <div style={{padding:'14px 20px',cursor:'pointer'}} onClick={()=>{ if(openQaPost?.id===p.id){setOpenQaPost(null);setQaReplies([]);}else{setOpenQaPost(p);const fetch=async()=>{const{data}=await supabase.from('qa_replies').select('*,profiles(email)').eq('post_id',p.id).order('created_at',{ascending:true});if(data)setQaReplies(data);};fetch();} }}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:9,letterSpacing:1,color:'var(--red)',background:'rgba(146,41,32,0.15)',padding:'2px 7px',borderRadius:3,textTransform:'uppercase'}}>{p.topic}</span>
                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)'}}>{p.profiles?.email}</span>
                          <span style={{fontFamily:"'Poppins',sans-serif",fontSize:10,color:'var(--muted)',marginLeft:'auto'}}>{new Date(p.created_at).toLocaleDateString()}</span>
                        </div>
                        <div style={{color:'var(--cream)',fontSize:14}}>{p.content}</div>
                      </div>
                      {openQaPost?.id===p.id && (
                        <div style={{borderTop:'1px solid var(--b2)',padding:16}}>
                          {qaReplies.map(r=>(
                            <div key={r.id} style={{marginBottom:12,paddingBottom:12,borderBottom:'1px solid var(--b1)'}}>
                              <div style={{fontFamily:"'Poppins',sans-serif",fontSize:9,color:'var(--muted)',marginBottom:4}}>{r.profiles?.email} · {new Date(r.created_at).toLocaleDateString()}</div>
                              <div style={{color:'var(--cream)',fontSize:13,lineHeight:1.7}}>{r.content}</div>
                            </div>
                          ))}
                          <form onSubmit={submitQaReply} style={{display:'flex',gap:10,alignItems:'flex-end',marginTop:8}}>
                            <textarea value={newQaReply} onChange={e=>setNewQaReply(e.target.value)} rows={2} placeholder="Type your reply…" style={{flex:1,background:'var(--black)',border:'1px solid var(--b2)',color:'var(--cream)',borderRadius:6,padding:'8px 12px',fontSize:13,resize:'vertical',fontFamily:'inherit',boxSizing:'border-box'}} />
                            <button type="submit" className="btn btn-red" disabled={postingQa} style={{flexShrink:0}}>{postingQa?'…':'Reply'}</button>
                          </form>
                        </div>
                      )}
                    </div>
                  ))}
                </>)}

              </div>
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <footer style={{
          borderTop:"1px solid var(--b2)", padding:"18px 32px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          flexWrap:"wrap", gap:10, background:"var(--bg2)",
        }}>
          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:8.5,letterSpacing:2.5,color:"var(--muted)",textTransform:"uppercase"}}>
            © 2025 Evolution Endurance · Collingwood, ON
          </div>
          <div style={{fontFamily:"'Poppins',sans-serif",fontSize:8.5,letterSpacing:2.5,color:"var(--red)",textTransform:"uppercase"}}>
            Test. Train. Run. No BS.
          </div>
        </footer>

      </div>
    </>
  );
}
